import { createContext, type ReactNode, useContext } from 'react';

export type Language = 'nl' | 'en' | 'fr';

const LanguageContext = createContext<Language>('nl');
/** The personal assistant's name from Admin's branding; texts say `{assistant}` for it. */
const AssistantNameContext = createContext('Nivi');

export const messages = {
  nl: {
    orchWaitingTool: 'Wacht op een tool in Chat',
    orchGoodAnswer: 'Goed antwoord',
    orchBadAnswer: 'Slecht antwoord',
    orchApproveTitle: 'Plan goedkeuren',
    orchApproveBudget: 'Boven budget',
    orchApprovePolicy: 'Ter goedkeuring',
    orchEstimate: 'Geschat',
    orchRunPlan: 'Plan uitvoeren ({count} stappen)',
    orchApproveHint: 'Schrap een stap of kies een ander model; de kosten rekenen mee.',
    orchStepModel: 'Model voor {step}',
    feedbackWhy: 'Wat ging er mis?',
    feedbackWrong: 'Fout',
    feedbackLayout: 'Opmaak',
    feedbackNotAsked: 'Niet wat ik vroeg',
    feedbackSlow: 'Te traag',
    artifactVersion: 'versie {n}',
    artifactChanged: '{parts} gewijzigd',
    artifactVersions: 'Versies',
    artifactVersionOf: 'Versie {n} van {total}',
    artifactWhatChanged: 'Gewijzigd:',
    artifactFirstVersion: 'Eerste versie bekijken',
    summaryTitle: 'Samenvatting',
    summaryMaking: 'De samenvatting wordt gemaakt…',
    summaryFailed: 'De samenvatting kon niet gemaakt worden. Probeer opnieuw.',
    summaryTopic: 'Waar het over gaat',
    summaryDecided: 'Beslist',
    summaryOpen: 'Nog open',
    summaryMade: 'Gemaakt in dit gesprek',
    summaryCopy: 'Kopiëren',
    summaryContinue: 'Verder in nieuw gesprek',
    summaryContinueHint:
      'Een nieuw gesprek begint met deze samenvatting en is sneller en goedkoper.',
    summaryContinueTitle: 'Vervolg: {title}',
    summaryFrom: 'Samenvatting van "{title}"',
    summaryOffer: 'Dit gesprek is lang geworden. Verder in een nieuw gesprek met een samenvatting?',
    summaryOfferYes: 'Samenvatten',
    aiNotice: 'Je praat met een AI-assistent. Antwoorden kunnen fouten bevatten.',
    aiNoticeShort: 'AI-assistent · kan fouten maken',
    aiNoticeMore: 'Meer info',
    aiNoticeTitle: 'Over AI in Chat',
    aiNoticeModels:
      'Antwoorden komen van AI-modellen; NIVIO kiest soms per stap een model. Ze kunnen fouten bevatten: controleer belangrijke informatie.',
    aiNoticeMedia: 'Beelden, video en presentaties die Chat maakt, zijn door AI gemaakt.',
    aiNoticePolicy: 'Lees het AI-beleid van je organisatie',
    templateSaveTitle: 'Bewaren als sjabloon',
    templateSaveDescription:
      'Start deze vraag later opnieuw met één klik, met hetzelfde model, dezelfde skills en kennis.',
    templateName: 'Naam',
    templatePrompt: 'Vraag',
    templateFieldsHint: 'Woorden tussen { } vraagt Chat telkens in, bv. {maand} of {klant}.',
    templateIncludes: 'Wat mee gaat',
    templateWho: 'Wie mag het gebruiken',
    templateOnlyMe: 'Alleen ik',
    templateEveryone: 'Iedereen die dit model, deze skills en kennis mag gebruiken',
    templateSaved: 'Bewaard',
    templateSaveFailed: 'Het sjabloon kon niet bewaard worden.',
    templateDelete: 'Sjabloon {name} verwijderen',
    templateDeleteFailed: 'Het sjabloon kon niet verwijderd worden.',
    templateFillDescription: 'Vul in wat dit sjabloon telkens vraagt.',
    templateStart: 'Starten',
    back: 'Terug',
    agentManual: 'Zelf invullen',
    agentTest: 'Testen',
    agentRetest: 'Opnieuw testen',
    agentSteps: 'Stappen',
    teamStepGoal: 'Doel',
    teamStepCompose: 'Samenstellen',
    teamStep1Intro: 'Meerdere agents die samen één opdracht afwerken.',
    teamStep2Intro: 'Voorgesteld door NIVIO. Pas de volgorde of de leden aan.',
    teamEditIntro: 'Pas de opdracht, de volgorde, de leden of de planning aan.',
    teamStep3Intro: 'Een proefrun van het hele team, zonder schrijfacties.',
    teamDescribeLabel: 'Welke opdracht moet het team afwerken?',
    teamDescribeHint:
      'Een team is zinvol als de opdracht uit losse delen bestaat die elk andere kennis vragen.',
    teamDescribePlaceholder:
      'Bv. maak elke maandag een gezondheidsrapport per sleutelklant: openstaande facturen, open tickets en recent nieuws.',
    teamHow1: 'Specialisten werken het na elkaar uit, elk met eigen kennis.',
    teamHow2: 'Elke specialist ziet wat de vorige vond.',
    teamHow3: 'De coördinator bundelt alles tot één resultaat.',
    teamWrite: 'Stel een team voor',
    teamWriting: 'NIVIO stelt een team voor…',
    teamDescribeNote:
      'NIVIO kiest uit je eigen agents en stelt nieuwe voor waar er geen past. Er wordt nog niets bewaard.',
    teamWriteFailed:
      'NIVIO kon geen team voorstellen. Probeer het opnieuw of beschrijf de opdracht anders.',
    teamSection: 'Team',
    teamSectionHelp: 'De opdracht lezen alle specialisten en de coördinator bij elke uitvoering.',
    teamOrder: 'Volgorde',
    teamOrderHelp:
      'De specialisten werken van boven naar onder; de coördinator bundelt hun bijdragen.',
    teamExisting: 'Bestaand',
    teamNew: 'Nieuw',
    teamNewAgent: 'Nieuwe agent',
    teamRemove: 'Uit het team halen',
    teamCoordinator: 'Coördinator',
    teamCoordinatorHelp: 'Bundelt de bijdragen tot één resultaat.',
    teamDefaultCoordinator: 'Standaard NIVIO-coördinator',
    teamAddExisting: '+ Bestaande agent',
    teamAddNew: 'Nieuwe agent',
    teamMaxHelp:
      'Maximaal 3 specialisten. Een nieuwe agent wordt pas gemaakt als je het team maakt, en start alleen in het team.',
    teamScheduleHelp:
      'Wanneer het team zelf start. De agents in het team volgen deze planning, niet hun eigen.',
    teamTesting: 'Het team werkt de stappen na elkaar af…',
    teamScoreLine: '{checksPassed} van {checks} controles in orde.',
    teamPerStep: 'Per stap',
    teamChecks: 'controles',
    teamStepOk: 'In orde',
    teamStepWaiting: 'Wacht op de vorige stap',
    teamSuggestionFor: 'Voorstel voor {name}',
    teamChangeAgentTitle: '{name} aanpassen?',
    teamChangeAgentHelp:
      'Deze agent werkt ook buiten dit team. De aanpassing geldt overal zodra je het team bewaart.',
    teamTestRule:
      'Elke stap wordt apart beoordeeld, zodat je ziet welke agent het team tegenhoudt. Een stap die mislukt, houdt de volgende tegen.',
    teamSaveFailed: 'Het team kon niet bewaard worden. Probeer het opnieuw.',
    teamCheckSpecialist: 'Levert een eigen, controleerbare bijdrage voor de teamopdracht',
    teamCheckAnswer: 'Beantwoordt de teamopdracht in één overzichtelijk resultaat',
    teamCheckUsesAll: 'Gebruikt de bijdragen van alle specialisten',
    teamLastTest: 'Laatste test {n}%',
    agentStepDescribe: 'Beschrijven',
    agentStepSetup: 'Inrichten',
    agentStepTest: 'Testen',
    agentStep1Intro: 'Vertel wat de agent moet doen; NIVIO schrijft hem uit.',
    agentStep2Intro: 'Pas aan wat niet klopt, en test de agent.',
    agentStep3Intro: 'Test de agent voor je hem bewaart.',
    agentDescribeLabel: 'Wat moet je agent doen?',
    agentDescribeHint:
      'Zeg wat de agent doet, met welke gegevens, hoe vaak, en hoe het resultaat eruitziet.',
    agentDescribePlaceholder:
      'Bv. volg elke werkdag de openstaande facturen op en stel herinneringsmails voor.',
    agentIdeas: 'Of begin met een voorbeeld',
    agentIdeaRelease: 'Wekelijkse release-notitie uit GitHub',
    agentIdeaReleasePrompt:
      'Maak elke vrijdag een release-notitie van wat er deze week in onze GitHub-repo gewijzigd is, met de open issues.',
    agentIdeaNews: 'Nieuwsoverzicht van mijn klanten',
    agentIdeaNewsPrompt:
      'Zoek elke ochtend nieuws over mijn belangrijkste klanten en vat per klant samen wat relevant is voor ons.',
    agentIdeaStock: 'Voorraad onder minimum melden',
    agentIdeaStockPrompt:
      'Controleer elke werkdag welke artikelen onder hun minimumvoorraad zitten en stel een bestellijst voor.',
    agentIdeaAgenda: 'Agenda van morgen voorbereiden',
    agentIdeaAgendaPrompt:
      'Bereid elke avond mijn afspraken van morgen voor: wie, waarover, en wat ik vooraf moet weten.',
    agentDescribeNote:
      'NIVIO maakt een naam, instructies en 3 testgevallen. Er wordt nog niets bewaard.',
    agentWrite: 'Schrijf de agent',
    agentWriting: 'NIVIO schrijft…',
    agentWriteFailed: 'De agent kon niet uitgeschreven worden. Probeer opnieuw.',
    agentWriteTestsOnly: 'Laat de agent zoals hij is; schrijf alleen zijn 3 testgevallen.',
    agentSaveFailed: 'De agent kon niet bewaard worden.',
    agentSaveKnowledge:
      'Deze skill gebruikt kennis die je niet mag gebruiken ({groups}). Kies een andere skill.',
    agentInstructionsHelp: 'De instructies leest de agent bij elke uitvoering.',
    agentRewrite: 'Schrijf het anders',
    agentRewriteShorter: 'Korter',
    agentRewriteShorterPrompt: 'Maak de instructies korter, zonder iets wezenlijks te verliezen.',
    agentRewriteStricter: 'Strenger',
    agentRewriteStricterPrompt: 'Maak de instructies strenger en preciezer over het resultaat.',
    agentRewriteEnglish: 'In het Engels',
    agentRewriteEnglishPrompt: 'Write the name and instructions in English.',
    agentRewriteWish: 'Eigen wens, bv. „voeg een totaal toe”',
    agentRewriteGo: 'Herschrijf',
    agentSkillKnowledge: 'Skill en kennis',
    agentSkillKnowledgeHelp:
      'NIVIO stelde dit voor op basis van je beschrijving. Je ziet alleen wat je mag gebruiken.',
    agentModelHelp: 'Wie het werk doet.',
    agentProviderModel: 'Provider en model',
    agentScheduleHelp: 'Wanneer de agent zelf start. Je kunt hem altijd ook met de hand starten.',
    agentSchedule_manual: 'Alleen handmatig',
    agentSchedule_weekdays: 'Elke werkdag',
    agentSchedule_daily: 'Dagelijks',
    agentSchedule_weekly: 'Wekelijks',
    agentSchedule_hourly: 'Elk uur',
    agentScore: 'Slagingskans',
    agentScore_ok: 'Goed',
    agentScore_warn: 'Kan beter',
    agentScore_err: 'Werkt niet',
    agentScoreStale: 'Instructies gewijzigd: test opnieuw',
    agentTesting: 'NIVIO voert de testgevallen uit en beoordeelt de antwoorden…',
    agentScoreLine:
      '{passed} van {tests} tests geslaagd · {checksPassed} van {checks} controles in orde.',
    agentTestFailed: 'De tests konden niet lopen. Probeer opnieuw.',
    agentTestNote:
      'Getest zonder schrijfacties: er wordt niets verstuurd of gewijzigd. NIVIO beoordeelt de antwoorden.',
    agentImprove: 'Verbeter de instructies',
    agentImproving: 'NIVIO zoekt aanpassingen…',
    agentImproveHelp: 'Aanpassingen die de mislukte controles moeten oplossen.',
    agentApply: 'Toepassen',
    agentApplyAllRetest: 'Alles toepassen en opnieuw testen',
    agentTestCases: 'Testgevallen',
    agentTestCol: 'Test',
    agentChecksCol: 'Controles',
    agentChecksDone: 'In orde',
    agentResultCol: 'Resultaat',
    agentTestRunning: 'Bezig',
    agentTestError: 'Niet gelukt',
    agentTestPassed: 'Geslaagd',
    agentTestNotPassed: 'Mislukt',
    agentAnswer: 'Wat de agent antwoordde',
    agentNoAnswer: 'Geen antwoord.',
    agentCheckMinor: 'minder belangrijk',
    agentTestRule:
      'Een test is geslaagd als alle belangrijke controles in orde zijn. Onder 80% raden we aan de instructies te verbeteren; bewaren kan altijd.',
    agentNotTested: 'Nog niet getest',
    agentLastTest: 'Laatste test {n}%',
    templates: 'Sjablonen',
    newTemplate: 'Nieuw sjabloon',
    templateManage: 'Sjablonen beheren',
    templateManageShort: 'Beheren',
    templateFromColleague: 'Van collega',
    templateHiddenLabel: 'Verborgen',
    templateSharedLabel: 'Gedeeld',
    templateShownLabel: 'Getoond',
    templateOrderFailed: 'De volgorde kon niet bewaard worden.',
    templateDeleteHelp:
      'Dit kan niet ongedaan worden. Chats die je ermee startte, blijven bestaan.',
    templateEdit: 'Openen en bewerken',
    templateStartChat: 'Chat starten',
    templateMoveUp: 'Hoger',
    templateMoveDown: 'Lager',
    templateShow: 'Tonen op nieuwe chat',
    templateHide: 'Verbergen op nieuwe chat',
    searchTemplates: 'Zoek sjablonen',
    noTemplates:
      'Je hebt nog geen sjablonen. Bewaar een vraag uit een chat als sjabloon, of maak er hier een.',
    templateOrderHelp:
      'Deze volgorde is de volgorde van de gele knoppen op een nieuwe chat. Sleep een rij of kies Hoger of Lager in het menu.',
    templateOrderHelpMenu:
      'Deze volgorde is de volgorde van de gele knoppen op een nieuwe chat. Kies Hoger of Lager in het menu.',
    templateFieldCount: '{n} velden',
    templateCol: 'Sjabloon',
    templateContents: 'Bevat',
    templateNewChatCol: 'Nieuwe chat',
    templateOthersHelp: 'Sjablonen van collega’s kun je starten, niet bewerken of verwijderen.',
    templateNotFound: 'Dit sjabloon bestaat niet meer, of het is niet van jou.',
    templateNew: 'Nieuw sjabloon',
    templateEditIntro: 'Een vraag die je met één klik opnieuw start.',
    templateNewIntro: 'Een vraag met model, skills en kennis, klaar voor één klik.',
    templateNameHelp: 'Staat op de gele knop van een nieuwe chat.',
    templateFields: 'Velden in deze vraag',
    templateNoFields: 'Geen velden. Typ bv. {klant} in de vraag om er een toe te voegen.',
    templateModelKnowledge: 'Model en kennis',
    templateModelKnowledgeHelp: 'Waarmee de chat start. Je kiest alleen wat je zelf mag gebruiken.',
    templateNewChatSection: 'Nieuwe chat',
    templateNewChatHelp: 'Of en waar dit sjabloon tussen de suggesties staat.',
    templateShowOnNewChat: 'Tonen op nieuwe chat',
    templateShowOnNewChatHint:
      'Uit: het sjabloon blijft bewaard, maar verschijnt niet als gele knop.',
    templatePlace: 'Plaats',
    templatePlaceHint: 'De eerste staan meteen zichtbaar; “Andere ideeën” toont de rest.',
    templatePlaceOf: '{n} van {total}',
    templateWhoHelp:
      'Gedeeld zien alleen collega’s die het model, de skills en de kennis mogen gebruiken.',
    templateDeleteButton: 'Sjabloon verwijderen…',
    orchRunAnyway: 'Toch uitvoeren',
    orchPlan: 'Plan',
    orchProgress: 'Voortgang',
    orchStatusRunning: 'Bezig',
    orchStatusDone: 'Klaar',
    orchStatusWaiting: 'Wacht',
    orchStatusFailed: 'Mislukt',
    orchStatusSkipped: 'Overgeslagen',
    orchStatusFallback: 'Reserve',
    orchStepCheck: 'Vertrouwelijkheid controleren',
    orchStepPlan: 'Plan maken',
    orchStepAnswer: 'Antwoord samenstellen',
    orchStepsDone: '{done} van {total} stappen klaar',
    orchSteps: '{count} stappen',
    orchModels: '{count} modellen',
    orchFallbackUsed: 'Reserve gebruikt',
    orchShowPlan: 'Plan bekijken',
    orchClosePanel: 'Paneel sluiten',
    orchCostPerModel: 'Kost per model',
    orchTotal: 'Totaal',
    orchTogether: 'Tegelijk',
    orchConfidential: 'Vertrouwelijk: blijft lokaal',
    orchNotConfidential: 'Niet vertrouwelijk',
    orchCheckUnavailable: 'Lokale check niet bereikbaar',
    orchOverBudget: 'Boven budget: ± {estimate} (limiet {budget})',
    orchModelHint: 'kiest zelf de modellen',
    toolCallOne: '1 actie',
    toolCallMany: '{count} acties',
    toolCallsFailed: '{count} mislukt',
    mentionList: 'Verwijzen naar',
    mentionSkills: 'Skills',
    mentionKnowledge: 'Kennis',
    mentionDocuments: 'Projectdocumenten',
    mentionMicrosoft: 'Microsoft 365',
    mentionMail: 'Mail',
    mentionMailDetail: 'Je eigen mailbox',
    mentionCalendar: 'Agenda',
    mentionCalendarDetail: 'Je eigen agenda',
    mentionDrive: 'OneDrive en SharePoint',
    mentionDriveDetail: 'Bestanden waar jij toegang toe hebt',
    mentionActive: 'Actief',
    mentionNone: 'Niets gevonden',
    signInInvalid: 'Het e-mailadres of wachtwoord klopt niet.',
    signInLocked: 'Te veel pogingen. Wacht even en probeer het opnieuw.',
    signInDisabled: 'Dit account is uitgeschakeld. Neem contact op met je beheerder.',
    signInFailed: 'Aanmelden lukte niet. Probeer het opnieuw.',
    fileTypeUnsupported: 'Dit bestandstype wordt niet ondersteund.',
    lstStatus: 'Status',
    lstAllStatuses: 'Alle statussen',
    lstActions: 'Acties',
    lstChat: 'Chat',
    lstChats: 'chats',
    lstWhen: 'Wanneer',
    lstSearchApps: 'Zoek apps',
    lstApp: 'App',
    lstType: 'Type',
    lstAllTypes: 'Alle types',
    lstOfficeFiles: 'Office-bestanden',
    lstImages: 'Afbeeldingen',
    lstFile: 'Bestand',
    lstSize: 'Grootte',
    lstNeverRun: 'Nog niet uitgevoerd',
    lstSearchAgents: 'Zoek agents',
    lstSearchTasks: 'Zoek taken',
    lstSetup: 'Opzet',
    lstSchedule: 'Planning',
    lstLastRun: 'Laatste run',
    lstStarted: 'Gestart',
    lstResult: 'Resultaat',
    lstUpdated: 'Bijgewerkt',
    lstContent: 'Inhoud',
    lstTeam: 'Team',
    lstDomain: 'Domein',
    selectionActions: 'Acties voor geselecteerde tekst',
    selectionDetails: 'Meer details',
    selectionAsk: 'Vragen',
    selectionDetailsPrompt: 'Geef meer uitleg over de geselecteerde tekst.',
    selectionAskPrompt: 'Mijn vraag over deze tekst: ',

    useCompanyCredentials: 'Meld je aan met je bedrijfsaccount.',
    modelScores: 'Modelbeoordeling',
    notRated: 'Niet beoordeeld',
    modelScoresHelp:
      'Beoordeling door je team. Performance 5 = snelst. Quality 5 = hoogste kwaliteit. Cost 1 = goedkoop, 5 = duur.',
    scoreperformance: 'Prestaties',
    scorequality: 'Kwaliteit',
    scorecost: 'Kosten',

    starterIdeas: 'Ideeën om te starten',
    otherIdeas: 'Andere ideeën',
    heroMorning: 'Goedemorgen',
    heroAfternoon: 'Goedemiddag',
    heroEvening: 'Goedenavond',
    heroQuestion: 'Waar zoek je een antwoord op?',
    improveStarter: 'Een tekst verbeteren',
    summarizeStarter: 'Een document samenvatten',
    prepareStarter: 'Een klantgesprek voorbereiden',
    improvePrompt: 'Help me deze tekst helder en professioneel te maken:\n\n',
    summarizePrompt:
      'Vat het document samen dat ik hieronder toevoeg. Geef de hoofdpunten, acties en open vragen.\n\n',
    preparePrompt:
      'Help me een klantgesprek voor te bereiden. Stel me eerst vragen over de klant en het doel van het gesprek.',
    organizeStarter: 'Mijn werk organiseren',
    organizePrompt: 'Help me deze back-office taak structureren in duidelijke stappen en acties.',
    analyzeStarter: 'Cijfers analyseren',
    analyzePrompt:
      'Help me deze financiële cijfers analyseren en benoem de belangrijkste inzichten en aandachtspunten.\n\n',
    createStarter: 'Een visual maken',
    createPrompt:
      'Help me een duidelijke briefing opstellen en daarna een passende afbeelding maken.',
    activeContext: 'Actieve context',
    removeContext: 'Verwijderen',
    attachmentInProject: 'Bestanden worden toegevoegd aan project',
    attachmentInChat: 'Bestanden worden aan dit gesprek toegevoegd.',
    attachmentImagesMeter: 'Bestanden',
    attachmentTooManyFiles: 'Maximaal 20 bestanden per bericht; {count} niet toegevoegd.',
    queuedMessage: 'In de wachtrij, wordt verstuurd zodra het antwoord klaar is:',
    attachmentUnsupported:
      '{files}: dit bestandstype wordt niet ondersteund. Vraag je beheerder om het toe te voegen.',
    attachmentUploadTooLarge:
      'Het bestand is te groot om te uploaden. Verklein het of vraag je beheerder de limiet te verhogen.',
    queuedCancel: 'Terug bewerken',
    usageThisMonth: 'Gebruik deze maand',
    usageOther: 'Overige',
    usageRequests: 'verzoeken',
    usageNone: 'Nog geen gebruik deze maand.',
    usageBudgetOf: 'van',
    usageCost: 'Kosten',
    disconnectMicrosoft365: 'Ontkoppelen',
    disconnectMicrosoft365Confirm:
      'Je Microsoft 365-account wordt ontkoppeld. Chat kan je mail, agenda, bestanden en Teams dan niet meer gebruiken tot je opnieuw verbindt.',
    disconnecting: 'Ontkoppelen…',
    microsoftDisconnectFailed: 'Ontkoppelen is niet gelukt. Probeer het opnieuw.',
    usageDetails: 'Details per model',
    attachmentSizeMeter: 'Grootte',
    sendHint: 'Enter om te verzenden · Shift + Enter voor een nieuwe regel',
    modelsLoadFailed: 'Modellen konden niet worden geladen. Probeer opnieuw.',
    noModelAccess: 'Er is nog geen model beschikbaar. Vraag je beheerder om toegang.',
    projectHelp: 'Bundel gesprekken en bestanden.',
    knowledgeHelp: 'Gebruik informatie uit gekoppelde bronnen.',
    skillHelp: 'Geef een vaste werkwijze mee.',
    draftPreserved: 'Je bericht staat nog in het invoerveld, zodat je het opnieuw kunt versturen.',
    errorEmptyAnswer: 'Het model gaf geen antwoord. Probeer het opnieuw of kies een ander model.',
    errorThoughtTooLong:
      "Het model dacht zo lang na dat het zijn volledige antwoordlimiet opgebruikte, zonder te antwoorden. Stel de vraag kleiner (bijvoorbeeld per groep cv's), kies een ander model, of vraag je beheerder om voor dit model een lager denkniveau in te stellen.",
    errorGeneric: 'Er ging iets mis bij het maken van het antwoord.',
    errorDetails: 'Technische details',

    attachments: 'Bijlagen',
    attachmentImagesTooLarge:
      'Er zijn te veel afbeeldingen voor één bericht (maximaal 20). Verstuur ze in kleinere groepen.',
    attachmentFileTooLarge:
      '{filename} is groter dan de limiet van {limit} per bestand. Verklein het bestand of splits het op.',
    attachmentTotalTooLarge:
      'De bijlagen zijn samen groter dan de limiet van {limit} per bericht. Verstuur ze in kleinere groepen.',
    attachmentContextTooLarge:
      'De documenttekst is samen te groot voor één bericht. Verwijder enkele bijlagen of stuur ze in kleinere groepen.',
    saveAsSkill: 'Opslaan als skill',
    skillSuggestionText:
      'Deze taak had {n} stappen. Bewaar de werkwijze als skill, dan herhaal je ze later met één vraag.',
    skillSuggestionSave: 'Bewaren als skill',
    skillSuggestionDismiss: 'Niet nu',
    skillFileSection: 'SKILL.md',
    skillFileHelp:
      'SKILL.md is het open Agent Skills-formaat. Zo neem je skills mee uit of naar andere AI-tools.',
    skillImport: 'SKILL.md importeren',
    skillImported: 'Geïmporteerd. Controleer de inhoud voor je opslaat.',
    skillImportFailed: 'Dit bestand kon niet als SKILL.md worden gelezen.',
    skillExport: 'Exporteren als SKILL.md',
    skillPropose: 'Voorstellen aan de organisatie',
    skillProposeHelp:
      'Een beheerder bekijkt je voorstel en kan de skill voor iedereen beschikbaar maken. Je eigen skill blijft zoals hij is.',
    skillProposed: 'Voorgesteld. Een beheerder bekijkt de skill.',
    skillProposeFailed: 'Voorstellen is niet gelukt. Alleen de eigenaar kan een skill voorstellen.',
    branchConversation: 'Verder in nieuwe chat',
    copyAnswer: 'Volledig antwoord kopiëren',
    copyCode: 'Code kopiëren',
    imageInputHint:
      'Maximaal 20 bestanden per bericht. Grote afbeeldingen worden automatisch verkleind.',
    copySelection: 'Selectie kopiëren',
    copied: 'Gekopieerd',
    dropDocuments: 'Selecteer bestanden of sleep ze hierheen.',
    retryAnswer: 'Antwoord opnieuw genereren',
    answerCopied: 'Antwoord gekopieerd',
    answerTruncated:
      'Dit antwoord is afgekapt omdat de maximale lengte is bereikt. Vraag "ga verder" om het af te maken, of vraag een kortere versie.',
    copyAnswerFailed: 'Kopiëren mislukt. Probeer opnieuw.',
    branchConversationHelp:
      'Neem de gespreksgeschiedenis tot en met dit antwoord mee naar een nieuwe chat.',
    branchingConversation: 'Nieuwe chat maken…',
    branchFailed:
      'Nieuwe chat kon niet worden gemaakt. Probeer opnieuw via de knop bij het antwoord.',
    editSkill: 'Skill bewerken',
    newSkill: 'Nieuwe skill',
    skillName: 'Naam',
    skillInstructions: 'Instructies',
    skillWriterTitle: 'Beschrijf je skill',
    skillWriterReviseTitle: 'Laat de skill aanpassen',
    skillWriterIntro:
      'Zeg in je eigen woorden wat de skill moet doen. Een AI-model vult de naam, beschrijving en instructies in; jij kijkt ze na en slaat op.',
    skillWriterReviseIntro:
      'Zeg wat er anders moet. Een AI-model past de naam, beschrijving en instructies aan; jij kijkt ze na en slaat op.',
    skillWriterLabel: 'Wat moet deze skill doen?',
    skillWriterReviseLabel: 'Wat moet er anders?',
    skillWriterPlaceholder:
      'Bijvoorbeeld: maak van ruwe vergadernotities een verslag met besluiten, acties en wie wat doet.',
    skillWriterAction: 'Instructies laten schrijven',
    skillWriterReviseAction: 'Aanpassing laten schrijven',
    skillWriterPending: 'Bezig met schrijven…',
    skillWriterFailed: 'Het schrijven lukte niet. Probeer het opnieuw of vul de velden zelf in.',
    skillWriterNoModel: 'Er is geen model beschikbaar om de skill te schrijven.',
    skillWriterDone: 'Ingevuld door {model}. Kijk alles na voor je opslaat.',
    skillWriterUndo: 'Ongedaan maken',
    skillReviewHelp: 'Controleer de naam, beschrijving en instructies voordat je opslaat.',
    skillDraftLoading: 'Skillvoorstel voorbereiden…',
    skillDraftFailed: 'Het voorstel kon niet worden gemaakt. Probeer opnieuw.',
    personalSkill: 'Persoonlijk',
    sharedSkill: 'Gedeeld met team',
    skillSharing: 'Delen met team',
    skillSharingHelp:
      'Teamleden kunnen de skill gebruiken. Alleen jij kunt bewerken. Kies Persoonlijk om delen te stoppen.',
    skillPrivateHelp:
      'Je skill is alleen voor jou zichtbaar. Je kunt hem later via Skills met een team delen.',
    skillNoGroups:
      'Je bent geen lid van een gebruikersgroep. Vraag een beheerder om je aan een team toe te voegen.',
    skillGroupsFailed: 'Teams konden niet worden geladen.',
    skillGroupUnavailable: 'Team niet meer beschikbaar; kies Persoonlijk.',
    skillSaveFailed: 'Opslaan is mislukt. Controleer je teamtoegang en probeer opnieuw.',
    skillSaveConflict:
      'Deze skill is gewijzigd. Sluit de editor en open de skill opnieuw voordat je opslaat.',
    skillSaved: 'Skill opgeslagen.',
    sharedWithMe: 'Gedeeld met mij',

    recentConversations: 'Recent',
    conversationActions: 'Acties voor gesprek',
    allProjects: 'Alle projecten',
    fillInMissingFields: 'Vul de ontbrekende velden in',
    conversationsIntro: 'Zoek in je hele historiek en open een gesprek.',
    moveToProject: 'Koppelen aan project',
    removeFromProject: 'Uit project halen',
    loadMoreConversations: 'Meer gesprekken laden',
    projectActions: 'Acties voor project',
    newChatInProject: 'Nieuwe chat in dit project',
    openProject: 'Project openen',
    groupBy: 'Groeperen',
    groupByDate: 'Op datum',
    groupByProject: 'Op project',
    showAllOfProject: 'Alle gesprekken tonen',
    historyLoadFailed: 'De historiek kon niet worden geladen.',
    retryHistory: 'Opnieuw proberen',

    chatContext: 'Context',
    addContext: 'Toevoegen',
    backToContext: 'Terug',
    favoriteSkills: 'Favoriete skills',
    noFavoriteSkills: 'Markeer skills met een ster om ze hier te tonen.',
    favoriteSkill: 'Toevoegen aan favorieten',
    unfavoriteSkill: 'Verwijderen uit favorieten',
    contextLoadFailed: 'De lijst kon niet worden geladen.',
    retryContext: 'Opnieuw proberen',

    chatContextIntro:
      'Kies het project, de kennis en de skills voor dit gesprek. Wijzigingen worden direct toegepast.',
    searchArtifacts: 'Zoek bestanden',
    noSearchResults: 'Geen resultaten voor deze zoekopdracht.',
    projectSettings: 'Projectinstellingen',
    settingsConflict:
      'Deze instellingen zijn ondertussen gewijzigd. Laad de actuele waarden; je voorgestelde wijziging blijft bewaard.',
    reloadSettings: 'Actuele instellingen laden',
    applyOwnChanges: 'Mijn wijziging toepassen',
    documentUploading: 'Bestand uploaden…',
    documentQueued: 'Bestand opgeslagen; wacht op verwerking…',
    documentProcessing: 'Document wordt verwerkt…',
    documentProcessingFailed:
      'Dit document kon niet voor chat worden verwerkt. Bekijk de documentstatus in het project.',
    approvalUnknown: 'Uitkomst onbekend',
    unknownOutcomeHelp:
      'Controleer eerst in het externe systeem of deze actie is uitgevoerd. De knoppen hieronder registreren je controle en voeren de actie niet opnieuw uit.',
    verifyOutcome: 'Resultaat vastleggen',
    verifyOutcomeDescription:
      'Heb je in het externe systeem gecontroleerd of deze actie is uitgevoerd? Hiermee leg je alleen het gecontroleerde resultaat vast.',
    verifiedExecuted: 'Gecontroleerd: uitgevoerd',
    verifiedNotExecuted: 'Gecontroleerd: niet uitgevoerd',
    outcomeVerified: 'Door jou gecontroleerd',
    actionReference: 'Actiereferentie',
    queued: 'In wachtrij',
    runQueued: 'Wacht op beschikbare uitvoercapaciteit.',
    confirm: 'Bevestigen',

    savingSettings: 'Instellingen opslaan…',
    newResponse: 'Nieuw antwoord',
    generatingAnswer: 'Antwoord wordt gemaakt…',
    dataOwner: 'Data-eigenaar',
    loading: 'Laden...',
    backendUnavailable: 'De chat-backend is niet bereikbaar.',
    conversationNotFound: 'Gesprek niet gevonden.',
    conversationUpdateFailed: 'De gespreksinstellingen konden niet worden bijgewerkt.',
    newConversation: 'Nieuw gesprek',
    newConversationFailed: 'Nieuw gesprek kon niet worden gestart.',
    testInChatSaveFirst: 'Sla je wijzigingen eerst op om te testen.',
    incompatibleSkillModels:
      'Deze skills vereisen verschillende modellen en kunnen niet samen worden gebruikt.',
    modelRequiredBySkill: 'Het gekozen model is niet compatibel met de geselecteerde skill.',
    projectActionFailed: 'De projectactie kon niet worden uitgevoerd.',
    conversations: 'Gesprekken',
    noConversations: 'Nog geen gesprekken.',
    searchConversations: 'Zoek gesprekken',
    noConversationMatches: 'Geen gesprekken gevonden.',
    rename: 'Hernoemen',
    swipeProject: 'Project',
    swipeOutOfProject: 'Uit project',
    chatActionFailed: 'Dat lukte niet. Probeer het opnieuw.',
    renameConversation: 'Gesprek hernoemen',
    tools: 'Tools',
    aiApps: 'AI-apps',
    aiAppsIntro:
      'Start gerichte HTML app-gesprekken en groepeer interactieve tools apart van algemene skills.',
    startAiApp: 'AI-app starten',
    customHtmlApp: 'Vrije HTML app',
    customHtmlAppDesc: 'Bouw een interactieve app op maat vanuit een open vraag of idee.',
    businessDashboard: 'Bedrijfsdashboard',
    businessDashboardDesc: 'Maak compacte dashboards met KPI’s, filters, tabellen en inzichten.',
    calculatorApp: 'Rekentool',
    calculatorAppDesc: 'Ontwerp rekentools, estimators en simulaties met duidelijke resultaten.',
    configuratorApp: 'Configurator',
    configuratorAppDesc: 'Bouw keuzehulpen met opties, samenvattingen en vergelijkingen.',
    workflowApp: 'Workflowtool',
    workflowAppDesc: 'Maak intakeflows, checklists en beslissingstools voor operationeel werk.',
    noAiAppSkill: 'De HTML App Builder skill is nog niet beschikbaar voor jouw account.',
    aiTasks: 'AI-taken',
    tasksAndAgents: 'Taken en agents',
    moreTabs: 'Meer',
    agentWorkspaceTitle: 'AI-agents',
    agentWorkspaceIntro: 'Volg je agents op en bekijk hun resultaten.',
    agentTeamsTab: 'Teams',
    agentApprovalsWaiting: 'acties wachten op jouw goedkeuring.',
    viewApprovals: 'Goedkeuringen bekijken',
    agentConfiguration: 'Configuratie',
    agentLastResult: 'Laatste resultaat',
    agentOpenResult: 'Resultaat openen',
    agentTeamRuns: 'Teamuitvoeringen',
    agentNoKnowledge: 'Geen kennisbronnen geselecteerd',
    agents: 'Agents',
    agent: 'Agent',
    duet: '{assistant}',
    settingsGeneral: 'Algemeen',
    settingsApp: 'Meldingen & app',
    duetSectionIntro: 'Waar je agents zich melden met resultaten, meldingen en goedkeuringen.',
    duetBriefOn: 'Ochtendbrief aan',
    duetBriefDays: 'Dagen',
    duetWeekdays: 'Werkdagen',
    duetEveryDay: 'Elke dag',
    duetMonitorOn: 'Monitor aan',
    duetMonitorWhen: 'Wanneer',
    duetWorkHours: 'Werkdagen 8–19 u',
    duetAlways: 'Altijd',
    duetResultPanel: 'Resultaat',
    duetResultUnavailable: 'Dit resultaat kan nu niet geladen worden.',
    duetOpenAsChat: 'Openen als chat',
    duetMonitor: 'Monitor',
    duetMonitorHint:
      'Kijkt elke drie uur of iets je aandacht vraagt en meldt het in {assistant}. Na een rustige controle wacht hij zes uur.',
    duetMonitorPushes: 'Meldingen per dag',
    duetMonitorPushesHint:
      'Alleen dringende zaken sturen een melding. Goedkeuringen tellen niet mee.',
    duetMonitorSources: 'Datasets die hij controleert',
    duetMonitorSave: 'Monitor opslaan',
    duetMonitorSaveFailed:
      'De monitor kon niet worden opgeslagen. Controleer het model en je datasets.',
    duetUrgent: 'Dringend',
    duetAttention: 'Aandacht',
    duetMute: 'Niet meer melden',
    duetMuted: 'Dit wordt niet meer gemeld.',
    tabChat: 'Chat',
    tabMore: 'Meer',
    tabHistory: 'Gesprekken',
    tabBar: 'Hoofdnavigatie',
    pullToRefresh: 'Trek om te vernieuwen',
    pullRelease: 'Laat los om te vernieuwen',
    pullRefreshing: 'Vernieuwen…',
    duetUndo: 'Ongedaan maken',
    duetReadBrief: 'Ochtendbrief voorlezen',
    shareAnswer: 'Antwoord delen',
    takePhoto: 'Foto maken',
    choosePhotos: "Foto's",
    cameraShort: 'Camera',
    fileShort: 'Bestand',
    forThisConversation: 'Voor dit gesprek',
    knowledgeOn: '{count} aan',
    skillAuto: 'Automatisch',
    activeContextCount: '{count} bronnen en skills actief',
    cameraBack: 'Terug naar de camera',
    cameraPhotoOf: 'Foto {n} van {total}',
    cameraDelete: 'Deze foto verwijderen',
    cameraMode: 'Soort foto',
    cameraModePhoto: 'Foto',
    cameraModeDocument: 'Document',
    cameraFlashOn: 'Flits aan',
    cameraFlashOff: 'Flits uit',
    cameraShot: 'Genomen foto',
    cameraNoAccess:
      'NIVIO kan de camera niet openen. Geef toegang in de instellingen van je telefoon, of gebruik de camera-app van je telefoon.',
    cameraUsePhoneApp: 'Camera-app gebruiken',
    cameraLive: 'Camerabeeld',
    cameraPrivate: "Je foto's blijven in dit gesprek",
    cameraAnother: 'Nog een foto nemen',
    cameraQuestion: "Wat wil je weten over deze foto's?",
    cameraOnlyAdd: 'Alleen toevoegen aan het bericht',
    cameraReview: "{count} foto's bekijken",
    cameraShutter: 'Foto nemen',
    cameraFlip: 'Andere camera',
    offlineQueued:
      'Je bent offline. Je bericht gaat weg zodra je weer verbinding hebt. Laat de app open.',
    offlineWaiting: 'Wacht op verbinding',
    duetAskFurther: 'Vraag verder',
    duetAskAbout: 'Over',
    duetFollowUp: 'Opvolging voorstellen',
    duetFollowUpPrompt:
      'Stel een opvolgactie voor bij deze melding, zoals een mail of een taak. Voer niets uit zonder mijn akkoord.',
    duetSnoozeDay: 'Morgen opnieuw',
    duetSnoozeWeek: 'Over een week',
    duetSnoozed: 'Stil tot',
    duetUseful: 'Nuttig',
    duetNoise: 'Niet nuttig',
    duetFeedback: 'Was deze melding nuttig?',
    duetReadAloud: 'Voorlezen',
    duetAskShort: 'Vraag',
    duetLater: 'Later',
    duetMuteShort: 'Stil',
    duetMonitorRules: 'Eigen regels',
    duetMonitorRulesHint:
      'In gewone taal, bijvoorbeeld: “meld alleen facturen boven €5.000” of “klant Acme is altijd dringend”.',
    duetNotifications: 'Meldingen',
    duetNotificationsIntro: 'Wanneer en waar {assistant} je mag storen.',
    duetQuietOn: 'Rustige uren',
    duetQuietHint:
      'Meldingen van {assistant} wachten tijdens deze uren en komen daarna samen als één melding. Goedkeuringen komen altijd door.',
    duetQuietFrom: 'Van',
    duetQuietTo: 'Tot',
    duetQuietSave: 'Rustige uren opslaan',
    duetQuietSaveFailed: 'De rustige uren konden niet worden opgeslagen.',
    duetDevicePushes: 'Meldingen van {assistant} op dit toestel',
    duetDevicePushesHint:
      'Ochtendbrief en monitor. Goedkeuringen en afgeronde agents melden zich op elk toestel met meldingen aan.',
    duetBrief: 'Ochtendbrief',
    duetBriefHint:
      'Elke ochtend een korte dagstart in {assistant}: je afspraken, wat vandaag vervalt en wat sinds gisteren je aandacht vraagt.',
    duetBriefSources: 'Datasets in de brief',
    duetBriefMicrosoftHint:
      'Agenda, mail, Teams, To Do en OneDrive/SharePoint tellen mee zodra je je Microsoft-account koppelt.',
    duetBriefSave: 'Ochtendbrief opslaan',
    duetBriefSaved: 'Opgeslagen.',
    duetBriefSaveFailed:
      'De ochtendbrief kon niet worden opgeslagen. Controleer het model en je datasets.',
    duetCardApproval: 'Goedkeuring nodig',
    duetProposedBy: 'Voorgesteld door',
    duetReview: 'Bekijken',
    duetCardDone: 'Klaar',
    duetTask: 'Taak',
    duetOpenResult: 'Resultaat openen',
    duetWaiting: 'Wacht op jou',
    duetActionConfirm:
      '{assistant} bereidt dit voor in de draad en toont het eerst: “{action}”. Een mail vraagt nog een bevestiging voor hij vertrekt.',
    duetActionPrepare: 'Voorbereiden',
    duetActionApproval: 'akkoord nodig',
    duetActionDone: 'gedaan',
    niviEarlierDays: 'Eerdere dagen',
    niviOnlyToday: 'Alleen vandaag',
    niviShowDay: 'Toon',
    niviAllDays: 'Alle dagen',
    niviAlertsOne: '1 melding',
    niviAlertsCount: '{count} meldingen',
    niviQuestionsOne: '1 vraag',
    niviQuestionsCount: '{count} vragen',
    duetPushAttention: 'Ook een melding bij ‘Aandacht’',
    duetPushAttentionHint:
      'Dringende punten geven altijd een melding. Zet dit uit om wat enkel aandacht vraagt alleen in {assistant} te tonen.',
    duetTestPush: 'Stuur testmelding',
    duetTestPushSent: 'Verstuurd naar {count} toestel(len).',
    duetTestPushNone: 'Geen toestel ontving de melding. Zet meldingen aan op dit toestel.',
    duetTestPushFailed: 'De testmelding kon niet verstuurd worden.',
    duetNew: 'Nieuw',
    duetUnavailable: '{assistant} kan nu niet geopend worden. Probeer het later opnieuw.',
    niviEnable: '{assistant} gebruiken',
    niviEnableHint:
      'Zet {assistant} in je menu, met ochtendbrief, monitor en meldingen. Uitzetten pauzeert de ochtendbrief en de monitor.',
    niviOff: '{assistant} staat uit. Zet het aan in Instellingen om het te gebruiken.',
    niviOpenSettings: 'Naar Instellingen',
    duetStartSetting: 'Open de app op {assistant}',
    duetStartSettingHint:
      'Je agents melden zich daar met resultaten en goedkeuringen. Chat blijft één tik verder.',
    agentsIntro:
      'Persoonlijke AI-agents met een vaste skill, kennis en veilige tools. Externe acties vragen altijd jouw goedkeuring.',
    createAgent: 'Agent maken',
    editAgent: 'Agent bewerken',
    yourAgents: 'Jouw agents',
    agentsNotAvailable: 'Persoonlijke agents zijn niet ingeschakeld voor jouw account.',
    noAgents: 'Nog geen agents.',
    agentTitlePlaceholder: 'Financiële opvolgingsagent',
    agentInstructions: 'Instructies',
    agentInstructionsPlaceholder:
      'Beschrijf het doel, waarop de agent moet letten en hoe het resultaat eruit moet zien.',
    agentReadOnlyNote:
      'Deze agent werkt zelfstandig volgens de planning. Externe schrijf- of deleteacties worden altijd als voorstel ter goedkeuring aangeboden.',
    agentActionFailed: 'De agent kon niet worden bijgewerkt.',
    agentUsedByTeam: 'Pauzeer of verwijder eerst de agentteams die deze agent gebruiken.',
    agentTeams: 'Agentteams',
    agentTeamsIntro:
      'Optioneel: laat meerdere gespecialiseerde agents samenwerken aan één terugkerende opdracht.',
    yourAgentTeams: 'Jouw agentteams',
    createAgentTeam: 'Agentteam maken',
    noAgentTeams: 'Nog geen agentteams.',
    teamTitlePlaceholder: 'Klantgezondheidsteam',
    teamInstructions: 'Teamopdracht',
    teamInstructionsPlaceholder: 'Beschrijf de terugkerende vraag die het team moet beantwoorden.',
    coordinator: 'Coördinator',
    specialists: 'Specialisten',
    selectAgent: 'Kies een agent',
    agentTeamReadOnlyNote:
      'Teams werken sequentieel. Externe schrijf- of deleteacties worden altijd als voorstel ter goedkeuring aangeboden. De coördinator bundelt de resultaten.',
    agentTeamActionFailed: 'Het agentteam kon niet worden bijgewerkt.',
    approvals: 'Goedkeuringen',
    approvalsIntro: 'Externe acties van agents worden pas na jouw goedkeuring uitgevoerd.',
    noApprovals: 'Geen actievoorstellen.',
    approvalErrorAgentDisabled:
      'De agent die dit voorstelde, staat niet meer aan. Zet hem weer aan en laat hem het opnieuw voorstellen.',
    approvalErrorToolAccess: 'De agent heeft geen toegang meer tot deze tool.',
    approvalErrorDatasetDisabled: 'Deze dataset staat niet meer aan voor de agent.',
    approvalErrorDatasetAccess: 'Je hebt geen toegang meer tot deze actie in de dataset.',
    approvalErrorInterrupted:
      'De uitvoering werd onderbroken en is misschien toch gelukt. Controleer het externe systeem voor je het opnieuw probeert.',
    approvalErrorVerifiedNotExecuted: 'Je hebt bevestigd dat de actie niet is uitgevoerd.',
    approvalErrorGeneric: 'De actie is mislukt. De foutmelding staat bij Details.',
    approvalProposedBy: 'Voorgesteld door',
    approvalExpiresAt: 'Vervalt op',
    approvalExactAction:
      'Dit wordt precies zo uitgevoerd. Wil je iets anders, weiger dan dit voorstel.',
    approvalRawData: 'Ruwe gegevens',
    approvalHandled: 'Dit voorstel is afgehandeld. Er valt niets meer goed te keuren.',
    approvalNotFound: 'Dit voorstel bestaat niet of hoort niet bij jouw account.',
    approve: 'Goedkeuren',
    reject: 'Weigeren',
    approveAction: 'Actie goedkeuren',
    approvalPending: 'Wacht op goedkeuring',
    approvalApproved: 'Goedgekeurd',
    approvalExecuting: 'Wordt uitgevoerd',
    approvalExecuted: 'Uitgevoerd',
    approvalRejected: 'Geweigerd',
    approvalExpired: 'Verlopen',
    approvalError: 'Mislukt',
    advancedSettings: 'Geavanceerde instellingen',
    writeAction: 'Schrijven',
    fileOrFolder: 'Bestand of map',
    teamsMessage: 'Teams-bericht',
    plannerTask: 'Planner-taak',
    toolActCalendarWrite: 'Werkt de agenda bij',
    toolActTodoRead: 'Bekijkt To Do',
    toolActTodoWrite: 'Werkt een taak bij in To Do',
    calendarEvent: 'Afspraak',
    todoTask: 'To Do-taak',
    listItem: 'Lijstitem',
    aiTask: 'AI-taak',
    signOut: 'Afmelden',
    appearance: 'Weergave',
    theme: 'Thema',
    textSize: 'Tekstgrootte',
    light: 'Licht',
    dimmed: 'Gedimd',
    dark: 'Donker',
    themeSystem: 'Systeem',
    themeHint:
      'Systeem volgt de instelling van je toestel en wisselt mee. Je keuze geldt op dit toestel.',
    chatIntro:
      'Stel vragen, schrijf teksten of zoek in je gekoppelde kennisgroepen. Gesprekken zijn versleuteld en blijven aan jouw account gekoppeld.',
    typeMessage: 'Typ je vraag…',
    generating: 'Genereren... (Esc om te stoppen)',
    message: 'Bericht',
    stopGenerating: 'Genereren stoppen',
    stop: 'Stoppen',
    send: 'Verzenden',
    attachFile: 'Bestand koppelen',
    dictate: 'Dicteren',
    dictateHold: 'Ingedrukt houden om te dicteren',
    dictateHolding: 'Aan het opnemen… loslaten om om te zetten, wegschuiven om te annuleren',
    dictateHoldCancel: 'Loslaten om te annuleren',
    dictateHoldLabel: 'Aan het opnemen… laat los',
    dictateHoldHint: 'Houd de microfoon ingedrukt terwijl je praat',
    dictateHoldAgain: 'Microfoon toegestaan. Houd de knop opnieuw ingedrukt terwijl je praat.',
    dictateStop: 'Opname stoppen en omzetten naar tekst',
    dictating: 'Aan het opnemen… (Esc om te annuleren)',
    transcribing: 'Tekst wordt herkend…',
    dictationDenied: 'Geen toegang tot de microfoon. Sta de microfoon toe in je browser.',
    dictationFailed: 'De opname kon niet worden omgezet naar tekst. Probeer opnieuw.',
    readAloudSection: 'Voorlezen',
    readAloudSetting: 'Antwoorden voorlezen na een gesproken vraag',
    readAloudSettingHint:
      'Stel je een vraag met de microfoon, dan wordt het antwoord op deze telefoon of tablet voorgelezen.',
    readAloudSummarySetting: 'Alleen een samenvatting voorlezen',
    readAloudSummarySettingHint:
      'Een lang antwoord wordt in een paar zinnen verteld. Het volledige antwoord blijft op het scherm. Zet uit om altijd alles voor te lezen.',
    readingAloud: 'Wordt voorgelezen…',
    readAloudStop: 'Stoppen',
    readAloudFailed: 'Voorlezen lukt nu niet',
    speechCode: 'Zie de code op het scherm.',
    speechTable: 'Zie de tabel op het scherm.',
    speechLink: 'link',
    removeAttachment: 'Bijlage verwijderen',
    readingAttachment: 'Bijlage wordt gelezen...',
    attachmentPreviewUnavailable: 'De bijlage kon nog niet gelezen worden.',
    attachmentDefaultPrompt: 'Vat de toegevoegde documenten samen.',
    attachmentContextLabel: 'Bijlage',
    sqliteAttachmentDefaultPrompt: 'Lees de SQLite database uit en geef een overzicht.',
    sqliteToolAvailable: 'Kan je database lezen',
    model: 'Model',
    modelAuto: 'Automatisch',
    modelAutoHint: 'NIVIO kiest per vraag het passende model',
    modelFast: 'Snel',
    modelFastHint: 'Korte vragen, samenvatten, herschrijven',
    modelBalanced: 'Gebalanceerd',
    modelBalancedHint: 'Het meeste dagelijkse werk',
    modelThorough: 'Grondig',
    modelThoroughHint: 'Analyse, lange documenten, moeilijke vragen · duurder',
    modelAll: 'Alle modellen…',
    modelBack: 'Terug',
    modelAnsweredBy: 'Beantwoord door {model}',
    searchModels: 'Zoek modellen',
    selectModel: 'Kies een model',
    noModels: 'Geen modellen beschikbaar. Vraag je admin om toegang.',
    searchProjects: 'Zoek projecten',
    knowledge: 'Kennis',
    generatingInBackground: 'Genereert nog in de achtergrond',
    knowledgeGroup: 'kennisgroep',
    knowledgeGroups: 'kennisgroepen',
    searchKnowledge: 'Zoek kennis',
    noKnowledge: 'Geen kennisgroepen beschikbaar voor jouw account. Vraag je admin om toegang.',
    dataset: 'bron',
    datasets: 'bronnen',
    skill: 'Skill',
    automatic: 'automatisch',
    autoSkillRemove: 'Klik om geen skill te gebruiken in dit gesprek.',
    autoSkillNone: 'Geen skill',
    autoSkillBack: 'Laat NIVIO weer een skill kiezen',
    autoSkillHint:
      'Gekozen door de chat voor deze vraag. Kies zelf een skill met @ om te overschrijven.',
    searchSkills: 'Zoek skills',
    skills: 'Skills',
    skillsIntro:
      'Bekijk de skills die jouw admin beschikbaar heeft gemaakt en start direct een gesprek met de juiste context.',
    startWithSkill: 'Start met skill',
    defaultModel: 'Standaardmodel',
    noKnowledgeRequired: 'Geen kennis nodig',
    noDescription: 'Geen beschrijving.',
    other: 'Overig',
    noSkill: 'Skills automatisch kiezen',
    clearSelection: 'Selectie wissen',
    noSkills: 'Geen skills beschikbaar voor jouw account. Vraag je admin om toegang.',
    settings: 'Instellingen',
    settingsIntro: 'Beheer je chatvoorkeuren en bekijk je eigen gebruik.',
    profile: 'Profiel',
    openAdmin: 'Admin openen',
    admin: 'Admin',
    member: 'Lid',
    adminOnly: 'Alleen admins kunnen dit scherm openen.',
    toolFailures: 'Mislukte toolcalls',
    toolFailuresIntro: 'Bekijk mislukte toolcalls zonder promptinhoud of berichtteksten te tonen.',
    toolFailuresLoadFailed: 'Toolcall-fouten konden niet geladen worden.',
    noToolFailures: 'Geen mislukte toolcalls gevonden.',
    unknownTool: 'Onbekende tool',
    user: 'Gebruiker',
    toolCallId: 'Toolcall ID',
    details: 'Details',
    chatDefaults: 'Chat-standaarden',
    defaultKnowledge: 'Standaardkennis',
    usage: 'Gebruik',
    settingsAdvanced: 'Geavanceerd',
    usageShow: 'Toon details',
    usageSummaryBudget:
      'Deze maand gebruikte je {spent} van je budget van {budget}, in {count} berichten.',
    usageSummarySpent: 'Deze maand gebruikte je {spent}, in {count} berichten.',
    usageSummaryMessages: 'Deze maand stuurde je {count} berichten.',
    usageSummaryAll: 'Sinds je begon: {count} berichten.',
    usageAdvancedIntro: 'Hoeveel je deze maand en in totaal gebruikte, voor wie het wil opvolgen.',
    connectedAccounts: 'Gekoppelde accounts',
    microsoft365: 'Microsoft 365',
    connectMicrosoft365: 'Microsoft 365 koppelen',
    reconnectMicrosoft365: 'Opnieuw koppelen',
    microsoftReconnectRequired:
      'Er zijn nieuwe rechten nodig om mailinhoud en je agenda te lezen. Koppel je Microsoft-account opnieuw.',
    microsoftNotConnected:
      'Koppel Microsoft 365 om mail, OneDrive en SharePoint-context beschikbaar te maken.',
    microsoftStatusUnavailable: 'Microsoft 365-status kon niet geladen worden.',
    microsoftAdminSetupRequired: 'Microsoft 365 moet eerst door een admin geconfigureerd worden.',
    microsoftConnectFailed: 'Microsoft 365 koppelen is mislukt.',
    devopsTokenSaved: 'Token opgeslagen',
    devopsTokenIntro:
      'Gebruik je eigen persoonlijk toegangstoken (PAT) voor de organisatie {organization}, als je niet met Microsoft aanmeldt.',
    devopsTokenLabel: 'Persoonlijk toegangstoken',
    devopsTokenReplace: 'Nieuw token om het huidige te vervangen',
    devopsTokenChecking: 'Controleren…',
    devopsTokenRemove: 'Token verwijderen',
    devopsTokenRemoveConfirm:
      'Azure DevOps gebruikt daarna weer je Microsoft-aanmelding, als die er is.',
    devopsTokenInvalid:
      'Azure DevOps aanvaardt dit token niet. Controleer of het geldig is, niet verlopen, en aangemaakt voor deze organisatie.',
    devopsTokenFailed: 'Het token kon niet worden opgeslagen. Probeer het later opnieuw.',
    devopsTokenPrivacy:
      'Het token wordt versleuteld bewaard, alleen voor jou gebruikt en nooit meer getoond. Geef het alleen de rechten die je nodig hebt.',
    leexiKeySaved: 'Sleutel opgeslagen',
    leexiKeyCompanyFallback:
      'Zonder eigen sleutel gebruikt NIVIO de bedrijfssleutel die je beheerder instelde.',
    leexiKeyIntro:
      'Vraag je Leexi-beheerder een API-sleutel die beperkt is tot jouw gesprekken (Company Settings → API Keys) en vul die hier in.',
    leexiKeyIdLabel: 'Key ID',
    leexiKeySecretLabel: 'Key secret',
    leexiKeySecretReplace: 'Nieuw geheim om de huidige sleutel te vervangen',
    leexiKeyRemove: 'Sleutel verwijderen',
    leexiKeyRemoveConfirm:
      'NIVIO gebruikt daarna de bedrijfssleutel van je beheerder, als die er is.',
    leexiKeyInvalid: 'Leexi aanvaardt deze sleutel niet. Controleer de Key ID en het geheim.',
    leexiKeyFailed: 'De sleutel kon niet worden opgeslagen. Probeer het later opnieuw.',
    leexiKeyPrivacy:
      'De sleutel wordt versleuteld bewaard, alleen voor jou gebruikt en nooit meer getoond. Geef hem alleen toegang tot wat je nodig hebt.',
    microsoftMail: 'Microsoft Mail',
    connectMicrosoftMail: 'Microsoft Mail koppelen',
    connected: 'Gekoppeld',
    notConnected: 'Niet gekoppeld',
    connectedAs: 'Gekoppeld als',
    connecting: 'Koppelen...',
    mailNotConnected:
      'Koppel je mailbox om de Email Assistant vragen over je e-mail te laten beantwoorden.',
    mailStatusUnavailable: 'Mailstatus kon niet geladen worden.',
    mailAdminSetupRequired: 'Microsoft Mail moet eerst door een admin geconfigureerd worden.',
    mailConnectFailed: 'Microsoft Mail koppelen is mislukt.',
    confirmMailSendTitle: 'Mail versturen?',
    confirmMailSendDescription: 'Controleer de inhoud voor je deze mail verstuurt.',
    mailTo: 'Aan',
    mailCc: 'Cc',
    mailSubject: 'Onderwerp',
    mailBody: 'Inhoud',
    sendMail: 'Mail versturen',
    sending: 'Versturen...',
    mailSent: 'Mail is verstuurd.',
    mailSendFailed: 'Mail versturen is mislukt.',
    thisMonth: 'Deze maand',
    allTime: 'Altijd',
    costManagedInAdmin:
      'Prijzen en budgetten beheert je beheerder. Hier zie je alleen je eigen gebruik.',
    assistant: 'assistent',
    companyAssistants: 'Bedrijfsassistenten',
    personalAssistants: 'Persoonlijke assistenten',
    tool: 'tool',
    toolUsed: 'Tool gebruikt',
    toolsUsed: 'tools gebruikt',
    toolActMakeWord: 'Maakt een Word-document',
    toolActMakeExcel: 'Maakt een Excel-bestand',
    toolActMakePowerPoint: 'Maakt een PowerPoint-presentatie',
    toolActMakeImage: 'Maakt een afbeelding',
    toolActMailRead: 'Leest je mails',
    toolActMailSend: 'Stuurt een mail',
    toolActCalendar: 'Bekijkt je agenda',
    toolActTeamsRead: 'Bekijkt Teams',
    toolActTeamsPost: 'Plaatst een bericht in Teams',
    toolActPlannerRead: 'Bekijkt Planner',
    toolActPlannerWrite: 'Werkt een taak bij in Planner',
    toolActListsRead: 'Bekijkt een SharePoint-lijst',
    toolActListsWrite: 'Werkt een SharePoint-lijst bij',
    toolActDriveSearch: 'Zoekt in OneDrive en SharePoint',
    toolActDriveRead: 'Leest een bestand uit OneDrive of SharePoint',
    toolActDriveWrite: 'Werkt een bestand bij in OneDrive of SharePoint',
    toolActAnalyse: 'Analyseert de bijlage',
    toolActProjectDoc: 'Leest een projectdocument',
    toolActData: 'Bevraagt de database',
    toolActWebSearch: 'Zoekt op het web',
    toolActConsult: 'Raadpleegt {name}',
    toolQuery: 'Hoe dit is opgezocht',
    toolQueryRows: '{count} rijen',
    toolQueryMoreRows: 'Er zijn waarschijnlijk meer rijen dan getoond.',
    toolSourceWeb: 'Web',
    toolSourceImage: 'Afbeelding',
    toolSourceAttachment: 'Bijlage',
    toolSourceProject: 'Projectdocumenten',
    toolSourceData: 'Database',
    toolSources: 'Bronnen',
    failed: 'mislukt',
    done: 'klaar',
    running: 'bezig',
    completed: 'afgerond',
    errorStatus: 'Fout',
    answerMore: 'dit antwoord',
    tokensIn: 'in',
    tokensOut: 'uit',
    aiTasksIntro:
      'Plan een skill als achtergrondtaak. Afgeronde taken verschijnen hier en als gesprek.',
    title: 'Titel',
    description: 'Beschrijving',
    edit: 'Bewerken',
    save: 'Opslaan',
    weeklyFinanceInsight: 'Wekelijks financieel inzicht',
    planning: 'Planning',
    hourly: 'Elk uur',
    every6Hours: 'Elke 6 uur',
    daily: 'Dagelijks',
    weekly: 'Wekelijks',
    prompt: 'Prompt',
    promptPlaceholder: 'Voer deze skill uit en vat het resultaat voor mij samen.',
    createAiTask: 'AI-taak maken',
    aiTaskActionFailed: 'De AI-taak kon niet worden bijgewerkt.',
    editAiTask: 'AI-taak bewerken',
    scheduledAiTasks: 'Geplande AI-taken',
    noAiTasks: 'Nog geen AI-taken.',
    enabled: 'Ingeschakeld',
    paused: 'Gepauzeerd',
    next: 'volgende',
    runNow: 'Nu uitvoeren',
    pause: 'Pauzeren',
    enable: 'Inschakelen',
    delete: 'Verwijderen',
    runs: 'Uitvoeringen',
    runStarted: 'Gestart.',
    noRuns: 'Nog geen uitvoeringen.',
    open: 'Openen',
    view: 'Weergave',
    viewList: 'Lijst',
    viewTiles: 'Tegels',
    releaseNotesOpen: 'Bekijk wat er nieuw is in versie',
    releaseNotesTitle: 'Nieuw in versie',
    releaseNotesIntro: 'Functionele wijzigingen die in deze versie beschikbaar zijn.',
    releaseNotesEmpty: 'Deze versie bevat geen functionele wijzigingen voor gebruikers.',
    actions: 'Acties',
    markRead: 'Markeer gelezen',
    signIn: 'Aanmelden',
    useGatewayCredentials: 'Meld je aan met je NIVIO-account.',
    signInWithSso: 'Microsoft-account',
    continueWithSso: 'Of meld je aan met Microsoft',
    ssoSignInFailed: 'SSO-aanmelding is mislukt.',
    email: 'E-mail',
    password: 'Wachtwoord',
    signingIn: 'Aanmelden...',
    interactiveHtml: 'Webpagina',
    artifacts: 'Bestanden',
    artifactsIntro:
      'Bekijk gegenereerde Office-bestanden, afbeeldingen en interactieve HTML uit je gesprekken.',
    noArtifacts: 'Nog geen bestanden gevonden.',
    projects: 'Projecten',
    project: 'Project',
    noProject: 'Geen project',
    projectsIntro:
      'Bundel gesprekken, instructies en private documenten in een afgebakende projectcontext.',
    createProject: 'Project maken',
    newProject: 'Nieuw project',
    noProjects: 'Nog geen projecten.',
    startProjectChat: 'Start projectchat',
    projectNewChat: 'Nieuwe chat in dit project',
    projectChatsShort: 'Chats',
    projectDocsShort: 'Documenten',
    addDocument: 'Toevoegen',
    documentActions: 'Acties voor document',
    usedInChats: 'gebruikt in chats',
    notInChats: 'niet in chats',
    useInChats: 'Gebruiken in chats',
    projectInstructions: 'Projectinstructies',
    deleteProjectConfirm:
      'Project verwijderen? Documenten en projectkoppelingen worden verwijderd.',
    projectDocuments: 'Projectdocumenten',
    projectDocumentsIntro:
      'Uploads blijven private storage. Tekstbestanden worden meteen als projectcontext gebruikt.',
    projectChats: 'Projectchats',
    projectChatsIntro: 'Gesprekken die aan dit project gekoppeld zijn.',
    noProjectChats: 'Nog geen projectchats.',
    messages: 'berichten',
    uploadDocument: 'Document uploaden',
    indexDocument: 'Verwerken',
    uploadFailed: 'Upload mislukt.',
    noProjectDocuments: 'Nog geen documenten.',
    documents: 'Documenten',
    projectPolicyPrefix: 'Limieten:',
    ocrEnabled: 'OCR aan',
    ocrDisabled: 'OCR uit',
    inactiveDocument: 'niet gebruikt in chat',
    disableInChat: 'Uitsluiten',
    enableInChat: 'Gebruiken',
    manage: 'Beheer',
    displayName: 'Weergavenaam',
    tags: 'Tags',
    tagsPlaceholder: 'kickoff, scope, finance',
    cancel: 'Annuleren',
    documentStatusIndexed: 'Geindexeerd',
    documentStatusIndexing: 'Indexeert',
    documentStatusStored: 'Opgeslagen',
    documentStatusUnsupported: 'Niet ondersteund',
    documentStatusFailed: 'Mislukt',
    previewUnavailable: 'Preview niet beschikbaar.',
    previewTruncated: 'Preview ingekort.',
    openPreview: 'Preview openen',
    sandboxedPreview: 'Sandbox-preview',
    videoPresentation: 'Videopresentatie',
    videoScenes: '{count} scènes',
    recordVideo: 'Opnemen als video',
    stopRecording: 'Opname stoppen',
    recordingFailed:
      'Opnemen lukte niet. Kies dit tabblad wanneer de browser vraagt wat je wilt delen.',
    preview: 'Voorbeeld',
    copyHtml: 'HTML kopiëren',
    openInNewTab: 'Openen in nieuw tabblad',
    close: 'Sluiten',
    modelsAll: 'Alle',
    modelsRecommended: 'Aanbevolen',
    officeFile: 'Office-bestand',
    download: 'Downloaden',
    saving: 'Opslaan...',
    saveToSharePoint: 'Opslaan in SharePoint',
    savedToSharePoint: 'Opgeslagen in SharePoint.',
    deleteConversation: 'Gesprek verwijderen',
    deleteConversationConfirmPrefix: 'Verwijder',
    deleteConversationConfirmSuffix: 'Dit kan niet ongedaan gemaakt worden.',
    resizePreview: 'Previewgrootte aanpassen',
    openNavigation: 'Navigatie openen',
    closeNavigation: 'Navigatie sluiten',
    toggleWiderPreview: 'Preview breder of smaller maken',
    toolIterationCapReached: 'Limiet voor tool-iteraties bereikt.',
    noticeToolsUnavailable:
      'De kennisbronnen konden niet geladen worden. Dit antwoord is gemaakt zonder kennisbronnen en zonder webzoeken.',
    noticeToolDenied:
      'Je hebt geen toegang tot een van de gevraagde kennisbronnen. Die is niet gebruikt.',
    noticeForcedFinal:
      'De limiet voor tools in dit antwoord is bereikt. Het antwoord gebruikt de informatie die al gevonden was.',
    noticeToolCallsDropped: 'Een onvolledige tool-aanroep van het model is overgeslagen.',
    sharePointNotConfigured:
      'SharePoint is nog niet geconfigureerd. Stel dit eerst in bij de gateway-instellingen.',
    sharePointRejected:
      'SharePoint-aanmeldgegevens zijn geweigerd. Controleer tenant, client ID, secret en Graph-rechten.',
    sharePointForbidden: 'De SharePoint-app heeft geen rechten om bestanden hier op te slaan.',
    sharePointMissing: 'De ingestelde SharePoint-drive of map is niet gevonden.',
    sharePointTooLarge: 'Dit bestand is groter dan de ingestelde SharePoint-uploadlimiet.',
    sharePointSaveFailed: 'Opslaan in SharePoint is mislukt.',
    couldNotSaveSharePoint: 'Het bestand kon niet in SharePoint opgeslagen worden.',
    attachShort: '+ Bijlage',
    attentionBadge: 'Taken die aandacht nodig hebben',
    notifyOnFinish: 'Geef een melding als de taak klaar is',
    notifyOnFinishHelp: 'Je krijgt een melding rechtsboven en een badge in de zijbalk.',
    scheduleTime: 'Tijdstip',
    scheduleTimeHint: 'Optioneel. Leeg laten om te herhalen vanaf het moment van opslaan.',
    scheduleTimeWeeklyHint:
      'Optioneel. De eerste run is vandaag op dit tijdstip, of volgende week als het al voorbij is.',
    ownerCol: 'Eigenaar',
    usageCol: 'Gebruik',
    teamSkill: 'teamskill',
    personalSkillLabel: 'Persoonlijke skill',
    usedTimesMonth: '{n} keer gebruikt deze maand',
    usedOnceMonth: '1 keer gebruikt deze maand',
    notUsedMonth: 'Deze maand niet gebruikt',
    usageHint: 'Aantal van je eigen berichten in chats met deze skill, deze maand.',
    version: 'versie',
    testInChat: 'Testen in chat',
    skillExamples: 'Voorbeelden',
    skillExamplesHelp: 'Zinnen die deze skill moeten starten.',
    skillExampleAdd: 'Voorbeeld toevoegen',
    skillExampleRemove: 'Voorbeeld verwijderen',
    skillVisibleTo: 'Zichtbaar voor',
    skillAllowTeamEdits: 'Het team mag bewerken',
    visibilityCol: 'Zichtbaarheid',
    visibilityPrivate: 'Privé',
    visibilityShared: 'Gedeeld',
    sharedWithYou: 'Gedeeld met jou',
    sharedProjectNote:
      '{name} heeft dit project met je gedeeld. Je kunt erin chatten en de documenten gebruiken; alleen de eigenaar kan het wijzigen. Je gesprekken blijven privé.',
    aColleague: 'Een collega',
    sharedBy: 'Gedeeld door {name}',
    showAllDocuments: 'Alle {count} documenten tonen',
    shareWith: 'Delen met',
    shareHelp:
      'Wie het project kan gebruiken: de instructies, documenten, kennisgroepen en skills. Alleen jij kunt het wijzigen. Ieders gesprekken blijven privé.',
    shareNobody: 'Niemand (privé)',
    shareEveryone: 'Iedereen',
    resizeSidebar:
      'Breedte van de zijbalk aanpassen (slepen of pijltjestoetsen, dubbelklik voor standaard)',
    shareGroups: 'Geselecteerde groepen',
    shareGroupsLabel: 'Groepen',
    shareGroupsEmpty: 'Kies minstens één groep, anders blijft het project privé.',
    noProjectSkills: 'Geen skills',
    projectSkillsHelp:
      'Deze skills staan standaard aan in elk nieuw gesprek in dit project. Je kunt ze per gesprek uitzetten. Wie een skill niet mag gebruiken, krijgt hem niet.',
    visibilityHelp: 'Alleen een label: delen met andere mensen is nog niet beschikbaar.',
    lstFiles: 'bestanden',
    runNotifyAgentDone: 'Agent ‘{name}’ is klaar',
    runNotifyAgentFailed: 'Agent ‘{name}’ is mislukt',
    runNotifyTaskDone: 'AI-taak ‘{name}’ is klaar',
    runNotifyTaskFailed: 'AI-taak ‘{name}’ is mislukt',
    runNotifyDoneLabel: 'Klaar',
    runNotifyFailedLabel: 'Mislukt',
    runNotifyOpen: 'Uitvoering openen',
    runNotifyDismiss: 'Melding sluiten',
    tourWelcomeTitle: 'Welkom bij NIVIO, {name}',
    tourWelcomeTitleNoName: 'Welkom bij NIVIO',
    tourWelcomeText:
      'In een korte rondleiding van {count} stappen zie je hoe je vragen stelt, je bedrijfsdata gebruikt en taken automatiseert.',
    tourLater: 'Later',
    tourStart: 'Rondleiding starten',
    tourStepOf: 'Stap {current} van {total}',
    tourSkip: 'Overslaan',
    tourPrevious: 'Vorige',
    tourNext: 'Volgende',
    tourDone: 'Klaar',
    tourAskTitle: 'Vraag stellen',
    tourAskBody:
      'Typ je vraag in gewone taal en druk op Enter. Met Shift+Enter begin je een nieuwe regel.',
    tourDataTitle: 'Je bedrijfsdata gebruiken',
    tourDataBody:
      'Met + zet je kennisgroepen en skills aan, zoals Business Central of SharePoint. Of typ @ in je vraag.',
    tourFilesTitle: 'Bestanden en dicteren',
    tourFilesBody:
      'Voeg bestanden toe via + of sleep ze naar het invoervak. Met de microfoon spreek je je vraag in.',
    tourFilesOnlyTitle: 'Bestanden toevoegen',
    tourFilesOnlyBody:
      'Voeg bestanden toe via + of sleep ze naar het invoervak. NIVIO leest ze mee bij je vraag.',
    tourProjectsTitle: 'Projecten',
    tourProjectsBody:
      'Bundel gesprekken en documenten per klant of onderwerp. Elke vraag in een project gebruikt die documenten.',
    tourAgentsTitle: 'Agents en taken',
    tourAgentsBody:
      'Laat terugkerend werk automatisch uitvoeren, zoals een wekelijks overzicht. Je krijgt een melding als het klaar is.',
    tourAccountsTitle: 'Accounts koppelen',
    tourAccountsBody:
      'Koppel Microsoft 365 onder Instellingen → Gekoppelde accounts. Dan kan NIVIO je mail, agenda en bestanden gebruiken.',
    tourAccountsBodyDevOps:
      'Koppel Microsoft 365 en je Azure DevOps-token onder Instellingen → Gekoppelde accounts. Dan kan NIVIO er met jouw rechten mee werken.',
    tourSetting: 'Rondleiding',
    tourSettingIntro: 'Een korte uitleg van de belangrijkste onderdelen van de chat.',
    tourCompletedOn: 'Doorlopen op {date}',
    tourSkippedStatus: 'Overgeslagen',
    tourNotYet: 'Nog niet doorlopen',
    tourRestart: 'Opnieuw starten',
    pwaInstallTitle: 'App installeren',
    pwaInstallIntro: 'Open de chat als app, vanaf je beginscherm of bureaublad.',
    pwaInstallButton: 'Installeer app',
    pwaInstalled: 'De app is geïnstalleerd op dit apparaat.',
    pwaIosStep1: 'Tik op Deel in de werkbalk van Safari.',
    pwaIosStep2: 'Kies Zet op beginscherm.',
    pwaIosStep3: 'Tik op Voeg toe.',
    pwaGenericHint: 'Gebruik het menu van je browser: Installeer app of Toevoegen aan startscherm.',
    pwaUpdateAvailable: 'Nieuwe versie beschikbaar',
    pwaUpdateReload: 'Vernieuwen',
    pushTitle: 'Meldingen',
    pushIntro:
      'Krijg een melding op dit toestel zodra een agent of AI-taak klaar is, ook als de app dicht is. Per taak kies je zelf of je een melding wilt.',
    pushUnsupported: 'Deze browser ondersteunt geen meldingen.',
    pushServerOff: 'Meldingen zijn niet ingeschakeld door je beheerder.',
    pushIosInstall:
      'Installeer eerst de app op je beginscherm (iPhone of iPad met iOS 16.4 of nieuwer, zie App installeren hierboven). Open de app daarna vanaf je beginscherm om meldingen aan te zetten.',
    pushDenied:
      'Meldingen zijn geblokkeerd voor deze site. Sta ze toe in de instellingen van je browser of toestel en kom daarna terug.',
    pushEnable: 'Meldingen op dit toestel aanzetten',
    pushDisable: 'Meldingen op dit toestel uitzetten',
    pushOn: 'Meldingen staan aan op dit toestel.',
    pushFailed: 'Meldingen aanzetten of uitzetten lukte niet. Probeer het opnieuw.',
    pwaShareHint:
      'Op Android kun je tekst en bestanden uit andere apps delen met Chat (Delen, dan Chat). iPhone en iPad ondersteunen dit niet.',
    shareReceived: 'Gedeeld naar NIVIO: {count} bestand(en).',
    shareUnsupported: 'Delen werkt pas nadat de app is geïnstalleerd en opnieuw is geopend.',
    shareTooLarge: 'Het gedeelde is te groot om hier te openen (maximaal 25 MB).',
    shareExpired: 'Het gedeelde is niet meer beschikbaar. Deel het opnieuw.',
    shareFailed: 'Het gedeelde kon niet worden geopend. Deel het opnieuw.',
  },
  en: {
    orchWaitingTool: 'Waiting for a tool in Chat',
    orchGoodAnswer: 'Good answer',
    orchBadAnswer: 'Bad answer',
    orchApproveTitle: 'Approve the plan',
    orchApproveBudget: 'Over budget',
    orchApprovePolicy: 'For approval',
    orchEstimate: 'Estimated',
    orchRunPlan: 'Run plan ({count} steps)',
    orchApproveHint: 'Leave out a step or pick another model; the cost follows.',
    orchStepModel: 'Model for {step}',
    feedbackWhy: 'What went wrong?',
    feedbackWrong: 'Wrong',
    feedbackLayout: 'Layout',
    feedbackNotAsked: 'Not what I asked',
    feedbackSlow: 'Too slow',
    artifactVersion: 'version {n}',
    artifactChanged: '{parts} changed',
    artifactVersions: 'Versions',
    artifactVersionOf: 'Version {n} of {total}',
    artifactWhatChanged: 'Changed:',
    artifactFirstVersion: 'View the first version',
    summaryTitle: 'Summary',
    summaryMaking: 'Making the summary…',
    summaryFailed: 'The summary could not be made. Try again.',
    summaryTopic: 'What it is about',
    summaryDecided: 'Decided',
    summaryOpen: 'Still open',
    summaryMade: 'Made in this chat',
    summaryCopy: 'Copy',
    summaryContinue: 'Continue in a new chat',
    summaryContinueHint: 'A new chat starts from this summary and is faster and cheaper.',
    summaryContinueTitle: 'Continued: {title}',
    summaryFrom: 'Summary of "{title}"',
    summaryOffer: 'This chat has become long. Continue in a new chat with a summary?',
    summaryOfferYes: 'Summarise',
    aiNotice: 'You are talking to an AI assistant. Answers can contain mistakes.',
    aiNoticeShort: 'AI assistant · can make mistakes',
    aiNoticeMore: 'More info',
    aiNoticeTitle: 'About AI in Chat',
    aiNoticeModels:
      'Answers come from AI models; NIVIO sometimes picks a model per step. They can contain mistakes: check important information.',
    aiNoticeMedia: 'Images, videos and presentations that Chat makes are made by AI.',
    aiNoticePolicy: "Read your organisation's AI policy",
    templateSaveTitle: 'Save as template',
    templateSaveDescription:
      'Start this question again later with one click, with the same model, skills and knowledge.',
    templateName: 'Name',
    templatePrompt: 'Question',
    templateFieldsHint: 'Chat asks for words in { } each time, e.g. {month} or {customer}.',
    templateIncludes: 'What goes along',
    templateWho: 'Who may use it',
    templateOnlyMe: 'Only me',
    templateEveryone: 'Everyone who may use this model, these skills and knowledge',
    templateSaved: 'Saved',
    templateSaveFailed: 'The template could not be saved.',
    templateDelete: 'Delete template {name}',
    templateDeleteFailed: 'The template could not be deleted.',
    templateFillDescription: 'Fill in what this template asks for.',
    templateStart: 'Start',
    back: 'Back',
    agentManual: 'Fill in yourself',
    agentTest: 'Test',
    agentRetest: 'Test again',
    agentSteps: 'Steps',
    teamStepGoal: 'Goal',
    teamStepCompose: 'Compose',
    teamStep1Intro: 'Several agents that finish one task together.',
    teamStep2Intro: 'Proposed by NIVIO. Change the order or the members.',
    teamEditIntro: 'Change the task, the order, the members or the schedule.',
    teamStep3Intro: 'A trial run of the whole team, without write actions.',
    teamDescribeLabel: 'Which task must the team finish?',
    teamDescribeHint:
      'A team makes sense when the task has separate parts that each need other knowledge.',
    teamDescribePlaceholder:
      'E.g. every Monday, make a health report per key customer: open invoices, open tickets and recent news.',
    teamHow1: 'Specialists work it out one after the other, each with its own knowledge.',
    teamHow2: 'Each specialist sees what the previous one found.',
    teamHow3: 'The coordinator bundles everything into one result.',
    teamWrite: 'Propose a team',
    teamWriting: 'NIVIO is proposing a team…',
    teamDescribeNote:
      'NIVIO picks from your own agents and proposes new ones where none fits. Nothing is saved yet.',
    teamWriteFailed: 'NIVIO could not propose a team. Try again or describe the task differently.',
    teamSection: 'Team',
    teamSectionHelp: 'All specialists and the coordinator read the task on every run.',
    teamOrder: 'Order',
    teamOrderHelp:
      'The specialists work from top to bottom; the coordinator bundles their contributions.',
    teamExisting: 'Existing',
    teamNew: 'New',
    teamNewAgent: 'New agent',
    teamRemove: 'Remove from the team',
    teamCoordinator: 'Coordinator',
    teamCoordinatorHelp: 'Bundles the contributions into one result.',
    teamDefaultCoordinator: 'Standard NIVIO coordinator',
    teamAddExisting: '+ Existing agent',
    teamAddNew: 'New agent',
    teamMaxHelp:
      'At most 3 specialists. A new agent is made only when you create the team, and only runs in the team.',
    teamScheduleHelp:
      'When the team starts by itself. The agents in the team follow this schedule, not their own.',
    teamTesting: 'The team works through the steps one after the other…',
    teamScoreLine: '{checksPassed} of {checks} checks passed.',
    teamPerStep: 'Per step',
    teamChecks: 'checks',
    teamStepOk: 'Fine',
    teamStepWaiting: 'Waits for the previous step',
    teamSuggestionFor: 'Suggestion for {name}',
    teamChangeAgentTitle: 'Change {name}?',
    teamChangeAgentHelp:
      'This agent also works outside this team. The change applies everywhere once you save the team.',
    teamTestRule:
      'Each step is judged on its own, so you see which agent holds the team back. A step that fails stops the next ones.',
    teamSaveFailed: 'The team could not be saved. Try again.',
    teamCheckSpecialist: 'Delivers its own, verifiable contribution to the team task',
    teamCheckAnswer: 'Answers the team task in one clear result',
    teamCheckUsesAll: 'Uses the contributions of all specialists',
    teamLastTest: 'Last test {n}%',
    agentStepDescribe: 'Describe',
    agentStepSetup: 'Set up',
    agentStepTest: 'Test',
    agentStep1Intro: 'Say what the agent must do; NIVIO writes it out.',
    agentStep2Intro: 'Change what is not right, then test the agent.',
    agentStep3Intro: 'Test the agent before you save it.',
    agentDescribeLabel: 'What must your agent do?',
    agentDescribeHint:
      'Say what the agent does, with which data, how often, and what the result looks like.',
    agentDescribePlaceholder:
      'E.g. follow up open invoices every weekday and suggest reminder emails.',
    agentIdeas: 'Or start from an example',
    agentIdeaRelease: 'Weekly release note from GitHub',
    agentIdeaReleasePrompt:
      'Every Friday, write a release note of what changed this week in our GitHub repo, with the open issues.',
    agentIdeaNews: 'News about my customers',
    agentIdeaNewsPrompt:
      'Every morning, find news about my main customers and summarise per customer what matters to us.',
    agentIdeaStock: 'Report stock below minimum',
    agentIdeaStockPrompt:
      'Every weekday, check which items are below their minimum stock and suggest an order list.',
    agentIdeaAgenda: "Prepare tomorrow's agenda",
    agentIdeaAgendaPrompt:
      "Every evening, prepare tomorrow's meetings: who, about what, and what I should know beforehand.",
    agentDescribeNote: 'NIVIO makes a name, instructions and 3 test cases. Nothing is saved yet.',
    agentWrite: 'Write the agent',
    agentWriting: 'NIVIO is writing…',
    agentWriteFailed: 'The agent could not be written. Try again.',
    agentWriteTestsOnly: 'Keep the agent exactly as it is; only write its 3 test cases.',
    agentSaveFailed: 'The agent could not be saved.',
    agentSaveKnowledge:
      'This skill uses knowledge you may not use ({groups}). Choose another skill.',
    agentInstructionsHelp: 'The agent reads the instructions on every run.',
    agentRewrite: 'Write it differently',
    agentRewriteShorter: 'Shorter',
    agentRewriteShorterPrompt: 'Make the instructions shorter without losing anything essential.',
    agentRewriteStricter: 'Stricter',
    agentRewriteStricterPrompt: 'Make the instructions stricter and more precise about the result.',
    agentRewriteEnglish: 'In Dutch',
    agentRewriteEnglishPrompt: 'Schrijf de naam en de instructies in het Nederlands.',
    agentRewriteWish: 'Your own wish, e.g. "add a total"',
    agentRewriteGo: 'Rewrite',
    agentSkillKnowledge: 'Skill and knowledge',
    agentSkillKnowledgeHelp:
      'NIVIO suggested these from your description. You only see what you may use.',
    agentModelHelp: 'Who does the work.',
    agentProviderModel: 'Provider and model',
    agentScheduleHelp: 'When the agent starts on its own. You can always start it by hand too.',
    agentSchedule_manual: 'Only by hand',
    agentSchedule_weekdays: 'Every weekday',
    agentSchedule_daily: 'Daily',
    agentSchedule_weekly: 'Weekly',
    agentSchedule_hourly: 'Hourly',
    agentScore: 'Success rate',
    agentScore_ok: 'Good',
    agentScore_warn: 'Could be better',
    agentScore_err: 'Does not work',
    agentScoreStale: 'Instructions changed: test again',
    agentTesting: 'NIVIO is running the test cases and judging the answers…',
    agentScoreLine: '{passed} of {tests} tests passed · {checksPassed} of {checks} checks fine.',
    agentTestFailed: 'The tests could not run. Try again.',
    agentTestNote:
      'Tested without write actions: nothing is sent or changed. NIVIO judges the answers.',
    agentImprove: 'Improve the instructions',
    agentImproving: 'NIVIO is looking for changes…',
    agentImproveHelp: 'Changes that should fix the failed checks.',
    agentApply: 'Apply',
    agentApplyAllRetest: 'Apply all and test again',
    agentTestCases: 'Test cases',
    agentTestCol: 'Test',
    agentChecksCol: 'Checks',
    agentChecksDone: 'Fine',
    agentResultCol: 'Result',
    agentTestRunning: 'Running',
    agentTestError: 'Did not run',
    agentTestPassed: 'Passed',
    agentTestNotPassed: 'Failed',
    agentAnswer: 'What the agent answered',
    agentNoAnswer: 'No answer.',
    agentCheckMinor: 'less important',
    agentTestRule:
      'A test passes when all important checks are fine. Under 80% we suggest improving the instructions; you can always save.',
    agentNotTested: 'Not tested yet',
    agentLastTest: 'Last test {n}%',
    templates: 'Templates',
    newTemplate: 'New template',
    templateManage: 'Manage templates',
    templateManageShort: 'Manage',
    templateFromColleague: 'From a colleague',
    templateHiddenLabel: 'Hidden',
    templateSharedLabel: 'Shared',
    templateShownLabel: 'Shown',
    templateOrderFailed: 'The order could not be saved.',
    templateDeleteHelp: 'This cannot be undone. Chats you started with it stay.',
    templateEdit: 'Open and edit',
    templateStartChat: 'Start chat',
    templateMoveUp: 'Move up',
    templateMoveDown: 'Move down',
    templateShow: 'Show on new chat',
    templateHide: 'Hide on new chat',
    searchTemplates: 'Search templates',
    noTemplates:
      'You have no templates yet. Save a question from a chat as a template, or make one here.',
    templateOrderHelp:
      'This is the order of the yellow buttons on a new chat. Drag a row, or choose Move up or Move down in the menu.',
    templateOrderHelpMenu:
      'This is the order of the yellow buttons on a new chat. Choose Move up or Move down in the menu.',
    templateFieldCount: '{n} fields',
    templateCol: 'Template',
    templateContents: 'Includes',
    templateNewChatCol: 'New chat',
    templateOthersHelp: 'You can start colleagues’ templates, but not edit or delete them.',
    templateNotFound: 'This template no longer exists, or it is not yours.',
    templateNew: 'New template',
    templateEditIntro: 'A question you start again with one click.',
    templateNewIntro: 'A question with its model, skills and knowledge, ready for one click.',
    templateNameHelp: 'Shown on the yellow button of a new chat.',
    templateFields: 'Fields in this question',
    templateNoFields: 'No fields. Type e.g. {customer} in the question to add one.',
    templateModelKnowledge: 'Model and knowledge',
    templateModelKnowledgeHelp: 'What the chat starts with. You can only pick what you may use.',
    templateNewChatSection: 'New chat',
    templateNewChatHelp: 'Whether and where this template shows among the suggestions.',
    templateShowOnNewChat: 'Show on new chat',
    templateShowOnNewChatHint: 'Off: the template is kept, but does not show as a yellow button.',
    templatePlace: 'Place',
    templatePlaceHint: 'The first ones show right away; “Other ideas” shows the rest.',
    templatePlaceOf: '{n} of {total}',
    templateWhoHelp:
      'Shared templates show only to colleagues who may use the model, skills and knowledge.',
    templateDeleteButton: 'Delete template…',
    orchRunAnyway: 'Run anyway',
    orchPlan: 'Plan',
    orchProgress: 'Progress',
    orchStatusRunning: 'Running',
    orchStatusDone: 'Done',
    orchStatusWaiting: 'Waiting',
    orchStatusFailed: 'Failed',
    orchStatusSkipped: 'Skipped',
    orchStatusFallback: 'Reserve',
    orchStepCheck: 'Checking confidentiality',
    orchStepPlan: 'Making a plan',
    orchStepAnswer: 'Writing the answer',
    orchStepsDone: '{done} of {total} steps done',
    orchSteps: '{count} steps',
    orchModels: '{count} models',
    orchFallbackUsed: 'Reserve used',
    orchShowPlan: 'View plan',
    orchClosePanel: 'Close panel',
    orchCostPerModel: 'Cost per model',
    orchTotal: 'Total',
    orchTogether: 'At the same time',
    orchConfidential: 'Confidential: stays local',
    orchNotConfidential: 'Not confidential',
    orchCheckUnavailable: 'Local check not reachable',
    orchOverBudget: 'Over budget: ± {estimate} (limit {budget})',
    orchModelHint: 'picks the models itself',
    toolCallOne: '1 tool call',
    toolCallMany: '{count} tool calls',
    toolCallsFailed: '{count} failed',
    mentionList: 'Refer to',
    mentionSkills: 'Skills',
    mentionKnowledge: 'Knowledge',
    mentionDocuments: 'Project documents',
    mentionMicrosoft: 'Microsoft 365',
    mentionMail: 'Mail',
    mentionMailDetail: 'Your own mailbox',
    mentionCalendar: 'Calendar',
    mentionCalendarDetail: 'Your own calendar',
    mentionDrive: 'OneDrive and SharePoint',
    mentionDriveDetail: 'Files you have access to',
    mentionActive: 'On',
    mentionNone: 'Nothing found',
    signInInvalid: 'The email address or password is incorrect.',
    signInLocked: 'Too many attempts. Wait a moment and try again.',
    signInDisabled: 'This account is disabled. Contact your administrator.',
    signInFailed: 'Signing in failed. Try again.',
    fileTypeUnsupported: 'This file type is not supported.',
    lstStatus: 'Status',
    lstAllStatuses: 'All statuses',
    lstActions: 'Actions',
    lstChat: 'Chat',
    lstChats: 'chats',
    lstWhen: 'When',
    lstSearchApps: 'Search apps',
    lstApp: 'App',
    lstType: 'Type',
    lstAllTypes: 'All types',
    lstOfficeFiles: 'Office files',
    lstImages: 'Images',
    lstFile: 'File',
    lstSize: 'Size',
    lstNeverRun: 'Not run yet',
    lstSearchAgents: 'Search agents',
    lstSearchTasks: 'Search tasks',
    lstSetup: 'Setup',
    lstSchedule: 'Schedule',
    lstLastRun: 'Last run',
    lstStarted: 'Started',
    lstResult: 'Result',
    lstUpdated: 'Updated',
    lstContent: 'Content',
    lstTeam: 'Team',
    lstDomain: 'Domain',
    selectionActions: 'Selected text actions',
    selectionDetails: 'More details',
    selectionAsk: 'Ask',
    selectionDetailsPrompt: 'Explain the selected text in more detail.',
    selectionAskPrompt: 'My question about this text: ',

    useCompanyCredentials: 'Sign in with your company account.',
    modelScores: 'Model ratings',
    notRated: 'Not rated',
    modelScoresHelp:
      'Rated by your team. Performance 5 = fastest. Quality 5 = highest quality. Cost 1 = inexpensive, 5 = expensive.',
    scoreperformance: 'Performance',
    scorequality: 'Quality',
    scorecost: 'Cost',

    starterIdeas: 'Ideas to get started',
    otherIdeas: 'Other ideas',
    heroMorning: 'Good morning',
    heroAfternoon: 'Good afternoon',
    heroEvening: 'Good evening',
    heroQuestion: 'What would you like to find out?',
    improveStarter: 'Improve a text',
    summarizeStarter: 'Summarize a document',
    prepareStarter: 'Prepare a customer meeting',
    improvePrompt: 'Help me make this text clear and professional:\n\n',
    summarizePrompt:
      'Summarize the document I will add below. Include key points, actions and open questions.\n\n',
    preparePrompt:
      'Help me prepare a customer meeting. First ask me about the customer and the goal of the meeting.',
    organizeStarter: 'Organize my work',
    organizePrompt: 'Help me structure this back-office task into clear steps and actions.',
    analyzeStarter: 'Analyze figures',
    analyzePrompt:
      'Help me analyze these financial figures and identify the key insights and concerns.\n\n',
    createStarter: 'Create a visual',
    createPrompt: 'Help me write a clear brief and then create a suitable image.',
    activeContext: 'Active context',
    removeContext: 'Remove',
    attachmentInProject: 'Files will be added to project',
    attachmentInChat: 'Files will be added to this conversation.',
    attachmentImagesMeter: 'Files',
    attachmentTooManyFiles: 'Up to 20 files per message; {count} not added.',
    queuedMessage: 'Queued, sent as soon as the answer is done:',
    attachmentUnsupported:
      '{files}: this file type is not supported. Ask your administrator to add it.',
    attachmentUploadTooLarge:
      'The file is too large to upload. Make it smaller or ask your administrator to raise the limit.',
    queuedCancel: 'Edit again',
    usageThisMonth: 'Usage this month',
    usageOther: 'Other',
    usageRequests: 'requests',
    usageNone: 'No usage yet this month.',
    usageBudgetOf: 'of',
    usageCost: 'Cost',
    disconnectMicrosoft365: 'Disconnect',
    disconnectMicrosoft365Confirm:
      'Your Microsoft 365 account will be disconnected. Chat can no longer use your mail, calendar, files and Teams until you connect again.',
    disconnecting: 'Disconnecting…',
    microsoftDisconnectFailed: 'Disconnecting failed. Try again.',
    usageDetails: 'Details per model',
    attachmentSizeMeter: 'Size',
    sendHint: 'Enter to send · Shift + Enter for a new line',
    modelsLoadFailed: 'Models could not be loaded. Try again.',
    noModelAccess: 'No model is available yet. Ask your administrator for access.',
    projectHelp: 'Keep conversations and files together.',
    knowledgeHelp: 'Use information from connected sources.',
    skillHelp: 'Apply a reusable way of working.',
    draftPreserved: 'Your message is still in the input, so you can send it again.',
    errorEmptyAnswer: 'The model did not give an answer. Try again or choose another model.',
    errorThoughtTooLong:
      'The model thought for so long that it used its whole answer limit without answering. Ask a smaller question (for example a few CVs at a time), choose another model, or ask your admin to set a lower thinking effort for this model.',
    errorGeneric: 'Something went wrong while creating the answer.',
    errorDetails: 'Technical details',

    attachments: 'Attachments',
    attachmentImagesTooLarge:
      'There are too many images for one message (up to 20). Send them in smaller groups.',
    attachmentFileTooLarge:
      '{filename} is larger than the limit of {limit} per file. Make it smaller or split it up.',
    attachmentTotalTooLarge:
      'The attachments together are larger than the limit of {limit} per message. Send them in smaller groups.',
    attachmentContextTooLarge:
      'The combined document text is too large for one message. Remove some attachments or send smaller batches.',
    saveAsSkill: 'Save as skill',
    skillSuggestionText:
      'This task took {n} steps. Save the method as a skill to repeat it later with one question.',
    skillSuggestionSave: 'Save as skill',
    skillSuggestionDismiss: 'Not now',
    skillFileSection: 'SKILL.md',
    skillFileHelp:
      'SKILL.md is the open Agent Skills format. Use it to bring skills from or to other AI tools.',
    skillImport: 'Import SKILL.md',
    skillImported: 'Imported. Check the content before you save.',
    skillImportFailed: 'This file could not be read as SKILL.md.',
    skillExport: 'Export as SKILL.md',
    skillPropose: 'Propose to the organisation',
    skillProposeHelp:
      'An administrator reviews your proposal and can make the skill available to everyone. Your own skill stays as it is.',
    skillProposed: 'Proposed. An administrator will review the skill.',
    skillProposeFailed: 'The proposal failed. Only the owner can propose a skill.',
    branchConversation: 'Branch into new chat',
    copyAnswer: 'Copy full answer',
    copyCode: 'Copy code',
    imageInputHint: 'Up to 20 files per message. Large images are made smaller automatically.',
    copySelection: 'Copy selection',
    copied: 'Copied',
    dropDocuments: 'Select files or drop them here.',
    retryAnswer: 'Regenerate answer',
    answerCopied: 'Answer copied',
    answerTruncated:
      'This answer was cut off because it reached the maximum length. Ask "continue" to finish it, or ask for a shorter version.',
    copyAnswerFailed: 'Copy failed. Try again.',
    branchConversationHelp:
      'Start a new chat with the conversation history up to and including this answer.',
    branchingConversation: 'Creating new chat…',
    branchFailed: 'Could not create the new chat. Try again using the button beside the answer.',
    editSkill: 'Edit skill',
    newSkill: 'New skill',
    skillName: 'Name',
    skillInstructions: 'Instructions',
    skillWriterTitle: 'Describe your skill',
    skillWriterReviseTitle: 'Have the skill revised',
    skillWriterIntro:
      'Say in your own words what the skill should do. An AI model fills in the name, description and instructions; you review them and save.',
    skillWriterReviseIntro:
      'Say what should change. An AI model revises the name, description and instructions; you review them and save.',
    skillWriterLabel: 'What should this skill do?',
    skillWriterReviseLabel: 'What should change?',
    skillWriterPlaceholder:
      'For example: turn rough meeting notes into minutes with decisions, actions and who does what.',
    skillWriterAction: 'Write the instructions',
    skillWriterReviseAction: 'Write the change',
    skillWriterPending: 'Writing…',
    skillWriterFailed: 'Writing did not work. Try again or fill in the fields yourself.',
    skillWriterNoModel: 'No model is available to write the skill.',
    skillWriterDone: 'Filled in by {model}. Review everything before you save.',
    skillWriterUndo: 'Undo',
    skillReviewHelp: 'Review the name, description and instructions before saving.',
    skillDraftLoading: 'Preparing skill draft…',
    skillDraftFailed: 'Unable to prepare the draft. Try again.',
    personalSkill: 'Personal',
    sharedSkill: 'Shared with team',
    skillSharing: 'Share with team',
    skillSharingHelp:
      'Team members can use the skill. Only you can edit it. Choose Personal to stop sharing.',
    skillPrivateHelp:
      'Only you can see this skill. You can share it with a team later from Skills.',
    skillNoGroups: 'You do not belong to a user group. Ask an administrator to add you to a team.',
    skillGroupsFailed: 'Unable to load teams.',
    skillGroupUnavailable: 'Team no longer available; choose Personal.',
    skillSaveFailed: 'Unable to save. Check your team access and try again.',
    skillSaveConflict: 'This skill changed. Close the editor and reopen the skill before saving.',
    skillSaved: 'Skill saved.',
    sharedWithMe: 'Shared with me',

    recentConversations: 'Recent',
    conversationActions: 'Conversation actions',
    allProjects: 'All projects',
    fillInMissingFields: 'Fill in the missing fields',
    conversationsIntro: 'Search your whole history and open a conversation.',
    moveToProject: 'Move to project',
    removeFromProject: 'Remove from project',
    loadMoreConversations: 'Load more conversations',
    projectActions: 'Project actions',
    newChatInProject: 'New chat in this project',
    openProject: 'Open project',
    groupBy: 'Group by',
    groupByDate: 'By date',
    groupByProject: 'By project',
    showAllOfProject: 'Show all conversations',
    historyLoadFailed: 'History could not be loaded.',
    retryHistory: 'Try again',

    chatContext: 'Context',
    addContext: 'Add',
    backToContext: 'Back',
    favoriteSkills: 'Favorite skills',
    noFavoriteSkills: 'Star skills to show them here.',
    favoriteSkill: 'Add to favorites',
    unfavoriteSkill: 'Remove from favorites',
    contextLoadFailed: 'Could not load this list.',
    retryContext: 'Try again',

    chatContextIntro:
      'Choose the project, knowledge and skills for this conversation. Changes apply immediately.',
    searchArtifacts: 'Search files',
    noSearchResults: 'No results match this search.',
    projectSettings: 'Project settings',
    settingsConflict:
      'These settings changed elsewhere. Load the current values; your proposed change is retained.',
    reloadSettings: 'Load current settings',
    applyOwnChanges: 'Apply my change',
    documentUploading: 'Uploading file…',
    documentQueued: 'File saved; waiting for processing…',
    documentProcessing: 'Processing document…',
    documentProcessingFailed:
      'This document could not be processed for chat. Check its status in the project.',
    approvalUnknown: 'Outcome unknown',
    unknownOutcomeHelp:
      'First check whether this action completed in the external system. These buttons record your verification and do not execute the action again.',
    verifyOutcome: 'Record outcome',
    verifyOutcomeDescription:
      'Have you checked the external system to confirm whether this action completed? This only records your verified result.',
    verifiedExecuted: 'Verified: executed',
    verifiedNotExecuted: 'Verified: not executed',
    outcomeVerified: 'Verified by you',
    actionReference: 'Action reference',
    queued: 'Queued',
    runQueued: 'Waiting for available execution capacity.',
    confirm: 'Confirm',

    savingSettings: 'Saving settings…',
    newResponse: 'New response',
    generatingAnswer: 'Generating answer…',
    dataOwner: 'Data owner',
    loading: 'Loading...',
    backendUnavailable: "Couldn't reach the chat backend.",
    conversationNotFound: 'Conversation not found.',
    conversationUpdateFailed: 'The conversation settings could not be updated.',
    newConversation: 'New conversation',
    newConversationFailed: 'New conversation could not be started.',
    testInChatSaveFirst: 'Save your changes first to test them.',
    incompatibleSkillModels: 'These skills require different models and cannot be used together.',
    modelRequiredBySkill: 'The selected model is not compatible with the selected skill.',
    projectActionFailed: 'The project action could not be completed.',
    conversations: 'Conversations',
    noConversations: 'No conversations yet.',
    searchConversations: 'Search conversations',
    noConversationMatches: 'No conversations found.',
    rename: 'Rename',
    swipeProject: 'Project',
    swipeOutOfProject: 'Out of project',
    chatActionFailed: 'That did not work. Please try again.',
    renameConversation: 'Rename conversation',
    tools: 'Tools',
    aiApps: 'AI Apps',
    aiAppsIntro:
      'Start focused HTML app conversations and keep interactive tools separate from general skills.',
    startAiApp: 'Start AI app',
    customHtmlApp: 'Custom HTML app',
    customHtmlAppDesc: 'Build a tailored interactive app from an open question or idea.',
    businessDashboard: 'Business dashboard',
    businessDashboardDesc: 'Create compact dashboards with KPIs, filters, tables, and insights.',
    calculatorApp: 'Calculator',
    calculatorAppDesc: 'Design calculators, estimators, and simulations with clear results.',
    configuratorApp: 'Configurator',
    configuratorAppDesc: 'Build guided option pickers with summaries and comparisons.',
    workflowApp: 'Workflow tool',
    workflowAppDesc: 'Create intake flows, checklists, and decision tools for operational work.',
    noAiAppSkill: 'The HTML App Builder skill is not available for your account yet.',
    aiTasks: 'AI tasks',
    tasksAndAgents: 'Tasks and agents',
    moreTabs: 'More',
    agentWorkspaceTitle: 'AI agents',
    agentWorkspaceIntro: 'Monitor your agents and review their results.',
    agentTeamsTab: 'Teams',
    agentApprovalsWaiting: 'actions are waiting for your approval.',
    viewApprovals: 'View approvals',
    agentConfiguration: 'Configuration',
    agentLastResult: 'Latest result',
    agentOpenResult: 'Open result',
    agentTeamRuns: 'Team runs',
    agentNoKnowledge: 'No knowledge sources selected',
    agents: 'Agents',
    agent: 'Agent',
    duet: '{assistant}',
    settingsGeneral: 'General',
    settingsApp: 'Notifications & app',
    duetSectionIntro: 'Where your agents report with results, alerts and approvals.',
    duetBriefOn: 'Morning brief on',
    duetBriefDays: 'Days',
    duetWeekdays: 'Weekdays',
    duetEveryDay: 'Every day',
    duetMonitorOn: 'Monitor on',
    duetMonitorWhen: 'When',
    duetWorkHours: 'Weekdays 8:00–19:00',
    duetAlways: 'Always',
    duetResultPanel: 'Result',
    duetResultUnavailable: 'This result cannot be loaded right now.',
    duetOpenAsChat: 'Open as chat',
    duetMonitor: 'Monitor',
    duetMonitorHint:
      'Checks every three hours whether something needs you and reports it in {assistant}. After a quiet check it waits six hours.',
    duetMonitorPushes: 'Notifications per day',
    duetMonitorPushesHint: 'Only urgent items send a notification. Approvals do not count.',
    duetMonitorSources: 'Datasets it checks',
    duetMonitorSave: 'Save monitor',
    duetMonitorSaveFailed: 'The monitor could not be saved. Check the model and your datasets.',
    duetUrgent: 'Urgent',
    duetAttention: 'Attention',
    duetMute: 'Do not report again',
    duetMuted: 'This will not be reported again.',
    tabChat: 'Chat',
    tabMore: 'More',
    tabHistory: 'Conversations',
    tabBar: 'Main navigation',
    pullToRefresh: 'Pull to refresh',
    pullRelease: 'Release to refresh',
    pullRefreshing: 'Refreshing…',
    duetUndo: 'Undo',
    duetReadBrief: 'Read the morning brief aloud',
    shareAnswer: 'Share answer',
    takePhoto: 'Take photo',
    choosePhotos: 'Photos',
    cameraShort: 'Camera',
    fileShort: 'File',
    forThisConversation: 'For this conversation',
    knowledgeOn: '{count} on',
    skillAuto: 'Automatic',
    activeContextCount: '{count} sources and skills on',
    cameraBack: 'Back to the camera',
    cameraPhotoOf: 'Photo {n} of {total}',
    cameraDelete: 'Delete this photo',
    cameraMode: 'Kind of photo',
    cameraModePhoto: 'Photo',
    cameraModeDocument: 'Document',
    cameraFlashOn: 'Flash on',
    cameraFlashOff: 'Flash off',
    cameraShot: 'Photo taken',
    cameraNoAccess:
      "NIVIO cannot open the camera. Allow access in your phone's settings, or use your phone's camera app.",
    cameraUsePhoneApp: 'Use the camera app',
    cameraLive: 'Camera view',
    cameraPrivate: 'Your photos stay in this conversation',
    cameraAnother: 'Take another photo',
    cameraQuestion: 'What do you want to know about these photos?',
    cameraOnlyAdd: 'Only add to the message',
    cameraReview: 'View {count} photos',
    cameraShutter: 'Take photo',
    cameraFlip: 'Other camera',
    offlineQueued:
      'You are offline. Your message goes out as soon as you are connected again. Keep the app open.',
    offlineWaiting: 'Waiting for connection',
    duetAskFurther: 'Ask a follow-up',
    duetAskAbout: 'About',
    duetFollowUp: 'Propose a follow-up',
    duetFollowUpPrompt:
      'Propose a follow-up action for this alert, such as a mail or a task. Do not run anything without my approval.',
    duetSnoozeDay: 'Remind me tomorrow',
    duetSnoozeWeek: 'In a week',
    duetSnoozed: 'Quiet until',
    duetUseful: 'Useful',
    duetNoise: 'Not useful',
    duetFeedback: 'Was this alert useful?',
    duetReadAloud: 'Read aloud',
    duetAskShort: 'Ask',
    duetLater: 'Later',
    duetMuteShort: 'Mute',
    duetMonitorRules: 'Your own rules',
    duetMonitorRulesHint:
      'In plain words, for example: “only report invoices above €5,000” or “customer Acme is always urgent”.',
    duetNotifications: 'Notifications',
    duetNotificationsIntro: 'When and where {assistant} may disturb you.',
    duetQuietOn: 'Quiet hours',
    duetQuietHint:
      'Notifications from {assistant} wait during these hours and arrive together as one afterwards. Approvals always come through.',
    duetQuietFrom: 'From',
    duetQuietTo: 'To',
    duetQuietSave: 'Save quiet hours',
    duetQuietSaveFailed: 'The quiet hours could not be saved.',
    duetDevicePushes: 'Notifications from {assistant} on this device',
    duetDevicePushesHint:
      'Morning brief and monitor. Approvals and finished agents notify every device with notifications on.',
    duetBrief: 'Morning brief',
    duetBriefHint:
      'A short start of the day in {assistant} every morning: your meetings, what is due today and what needs you since yesterday.',
    duetBriefSources: 'Datasets in the brief',
    duetBriefMicrosoftHint:
      'Calendar, mail, Teams, To Do and OneDrive/SharePoint count once you connect your Microsoft account.',
    duetBriefSave: 'Save morning brief',
    duetBriefSaved: 'Saved.',
    duetBriefSaveFailed: 'The morning brief could not be saved. Check the model and your datasets.',
    duetCardApproval: 'Approval needed',
    duetProposedBy: 'Proposed by',
    duetReview: 'Review',
    duetCardDone: 'Done',
    duetTask: 'Task',
    duetOpenResult: 'Open result',
    duetWaiting: 'Waiting for you',
    duetActionConfirm:
      '{assistant} prepares this in the thread and shows it first: “{action}”. A mail asks for confirmation once more before it is sent.',
    duetActionPrepare: 'Prepare',
    duetActionApproval: 'needs approval',
    duetActionDone: 'done',
    niviEarlierDays: 'Earlier days',
    niviOnlyToday: 'Today only',
    niviShowDay: 'Show',
    niviAllDays: 'All days',
    niviAlertsOne: '1 alert',
    niviAlertsCount: '{count} alerts',
    niviQuestionsOne: '1 question',
    niviQuestionsCount: '{count} questions',
    duetPushAttention: 'Also notify for ‘Attention’',
    duetPushAttentionHint:
      'Urgent items always notify. Turn this off to show items that only need attention in {assistant} alone.',
    duetTestPush: 'Send test notification',
    duetTestPushSent: 'Sent to {count} device(s).',
    duetTestPushNone: 'No device received it. Turn notifications on for this device.',
    duetTestPushFailed: 'The test notification could not be sent.',
    duetNew: 'New',
    duetUnavailable: '{assistant} cannot be opened right now. Try again later.',
    niviEnable: 'Use {assistant}',
    niviEnableHint:
      'Adds {assistant} to your menu, with the morning brief, monitor and notifications. Turning it off pauses the morning brief and the monitor.',
    niviOff: '{assistant} is off. Turn it on in Settings to use it.',
    niviOpenSettings: 'Go to Settings',
    duetStartSetting: 'Open the app on {assistant}',
    duetStartSettingHint:
      'Your agents report there with results and approvals. Chat stays one tap away.',
    agentsIntro:
      'Personal AI agents with a fixed skill, knowledge, and safe tools. External actions always require your approval.',
    createAgent: 'Create agent',
    editAgent: 'Edit agent',
    yourAgents: 'Your agents',
    agentsNotAvailable: 'Personal agents are not enabled for your account.',
    noAgents: 'No agents yet.',
    agentTitlePlaceholder: 'Financial monitoring agent',
    agentInstructions: 'Instructions',
    agentInstructionsPlaceholder:
      'Describe the goal, what the agent should monitor, and what the result should look like.',
    agentReadOnlyNote:
      'This agent works independently on its schedule. External write and delete actions are always proposed for approval.',
    agentActionFailed: 'The agent could not be updated.',
    agentUsedByTeam: 'Pause or delete the agent teams that use this agent first.',
    agentTeams: 'Agent teams',
    agentTeamsIntro: 'Optional: let multiple specialized agents collaborate on one recurring task.',
    yourAgentTeams: 'Your agent teams',
    createAgentTeam: 'Create agent team',
    noAgentTeams: 'No agent teams yet.',
    teamTitlePlaceholder: 'Customer health team',
    teamInstructions: 'Team brief',
    teamInstructionsPlaceholder: 'Describe the recurring question the team should answer.',
    coordinator: 'Coordinator',
    specialists: 'Specialists',
    selectAgent: 'Select an agent',
    agentTeamReadOnlyNote:
      'Teams work sequentially. External write and delete actions are always proposed for approval. The coordinator combines the results.',
    agentTeamActionFailed: 'The agent team could not be updated.',
    approvals: 'Approvals',
    approvalsIntro: 'External agent actions run only after your approval.',
    noApprovals: 'No action proposals.',
    approvalErrorAgentDisabled:
      'The agent that proposed this is no longer on. Turn it back on and let it propose again.',
    approvalErrorToolAccess: 'The agent no longer has access to this tool.',
    approvalErrorDatasetDisabled: 'This dataset is no longer on for the agent.',
    approvalErrorDatasetAccess: 'You no longer have access to this action in the dataset.',
    approvalErrorInterrupted:
      'The run was interrupted and may have succeeded anyway. Check the external system before you try again.',
    approvalErrorVerifiedNotExecuted: 'You confirmed that the action did not run.',
    approvalErrorGeneric: 'The action failed. The error is under Details.',
    approvalProposedBy: 'Proposed by',
    approvalExpiresAt: 'Expires on',
    approvalExactAction: 'This runs exactly as shown. If you want something else, reject it.',
    approvalRawData: 'Raw data',
    approvalHandled: 'This proposal has been handled. There is nothing left to approve.',
    approvalNotFound: 'This proposal does not exist or does not belong to your account.',
    approve: 'Approve',
    reject: 'Reject',
    approveAction: 'Approve action',
    approvalPending: 'Pending approval',
    approvalApproved: 'Approved',
    approvalExecuting: 'Executing',
    approvalExecuted: 'Executed',
    approvalRejected: 'Rejected',
    approvalExpired: 'Expired',
    approvalError: 'Failed',
    advancedSettings: 'Advanced settings',
    writeAction: 'Write',
    fileOrFolder: 'File or folder',
    teamsMessage: 'Teams message',
    plannerTask: 'Planner task',
    toolActCalendarWrite: 'Updating the calendar',
    toolActTodoRead: 'Looking in To Do',
    toolActTodoWrite: 'Updating a task in To Do',
    calendarEvent: 'Appointment',
    todoTask: 'To Do task',
    listItem: 'List item',
    aiTask: 'AI task',
    signOut: 'Sign out',
    appearance: 'Appearance',
    theme: 'Theme',
    textSize: 'Text size',
    light: 'Light',
    dimmed: 'Dimmed',
    dark: 'Dark',
    themeSystem: 'System',
    themeHint:
      'System follows your device and switches with it. Your choice applies to this device.',
    chatIntro:
      'Ask questions, draft text, or search your enabled knowledge groups. Conversations are encrypted at rest and stay scoped to your account.',
    typeMessage: 'Type your question…',
    generating: 'Generating... (Esc to stop)',
    message: 'Message',
    stopGenerating: 'Stop generating',
    stop: 'Stop',
    send: 'Send',
    attachFile: 'Attach file',
    dictate: 'Dictate',
    dictateHold: 'Hold to dictate',
    dictateHolding: 'Recording… release to convert, slide away to cancel',
    dictateHoldCancel: 'Release to cancel',
    dictateHoldLabel: 'Recording… release',
    dictateHoldHint: 'Hold the microphone while you speak',
    dictateHoldAgain: 'Microphone allowed. Hold the button again while you speak.',
    dictateStop: 'Stop recording and convert to text',
    dictating: 'Recording… (Esc to cancel)',
    transcribing: 'Recognising speech…',
    dictationDenied: 'No access to the microphone. Allow the microphone in your browser.',
    dictationFailed: 'The recording could not be converted to text. Try again.',
    readAloudSection: 'Read aloud',
    readAloudSetting: 'Read answers aloud after a spoken question',
    readAloudSettingHint:
      'When you ask a question with the microphone, the answer is read aloud on this phone or tablet.',
    readAloudSummarySetting: 'Read only a summary',
    readAloudSummarySettingHint:
      'A long answer is told in a few sentences. The full answer stays on the screen. Switch off to always read everything.',
    readingAloud: 'Reading aloud…',
    readAloudStop: 'Stop',
    readAloudFailed: 'Reading aloud is not possible right now',
    speechCode: 'See the code on the screen.',
    speechTable: 'See the table on the screen.',
    speechLink: 'link',
    removeAttachment: 'Remove attachment',
    readingAttachment: 'Reading attachment...',
    attachmentPreviewUnavailable: 'The attachment could not be read yet.',
    attachmentDefaultPrompt: 'Summarize the attached documents.',
    attachmentContextLabel: 'Attachment',
    sqliteAttachmentDefaultPrompt: 'Inspect the SQLite database and give an overview.',
    sqliteToolAvailable: 'Can read your database',
    model: 'Model',
    modelAuto: 'Automatic',
    modelAutoHint: 'NIVIO picks the right model for each question',
    modelFast: 'Fast',
    modelFastHint: 'Short questions, summaries, rewriting',
    modelBalanced: 'Balanced',
    modelBalancedHint: 'Most everyday work',
    modelThorough: 'Thorough',
    modelThoroughHint: 'Analysis, long documents, hard questions · costs more',
    modelAll: 'All models…',
    modelBack: 'Back',
    modelAnsweredBy: 'Answered by {model}',
    searchModels: 'Search models',
    selectModel: 'Select a model',
    noModels: 'No models available. Ask your admin for access.',
    searchProjects: 'Search projects',
    knowledge: 'Knowledge',
    generatingInBackground: 'Still generating in the background',
    knowledgeGroup: 'knowledge group',
    knowledgeGroups: 'knowledge groups',
    searchKnowledge: 'Search knowledge',
    noKnowledge: 'No knowledge groups available for your account. Ask your admin for access.',
    dataset: 'source',
    datasets: 'sources',
    skill: 'Skill',
    automatic: 'automatic',
    autoSkillRemove: 'Click to use no skill in this conversation.',
    autoSkillNone: 'No skill',
    autoSkillBack: 'Let NIVIO choose a skill again',
    autoSkillHint:
      'Picked by the chat for this request. Choose a skill yourself with @ to override.',
    searchSkills: 'Search skills',
    skills: 'Skills',
    skillsIntro:
      'Browse the skills your admin made available and start a conversation with the right context.',
    startWithSkill: 'Start with skill',
    defaultModel: 'Default model',
    noKnowledgeRequired: 'No knowledge required',
    noDescription: 'No description.',
    other: 'Other',
    noSkill: 'Choose skills automatically',
    clearSelection: 'Clear selection',
    noSkills: 'No skills available for your account. Ask your admin for access.',
    settings: 'Settings',
    settingsIntro: 'Manage chat preferences and review your own usage.',
    profile: 'Profile',
    openAdmin: 'Open admin',
    admin: 'Admin',
    member: 'Member',
    adminOnly: 'Only admins can open this screen.',
    toolFailures: 'Failed tool calls',
    toolFailuresIntro: 'Review failed tool calls without showing prompt content or message text.',
    toolFailuresLoadFailed: 'Tool call failures could not be loaded.',
    noToolFailures: 'No failed tool calls found.',
    unknownTool: 'Unknown tool',
    user: 'User',
    toolCallId: 'Tool call ID',
    details: 'Details',
    chatDefaults: 'Chat defaults',
    defaultKnowledge: 'Default knowledge',
    usage: 'Usage',
    settingsAdvanced: 'Advanced',
    usageShow: 'Show details',
    usageSummaryBudget: 'This month you used {spent} of your {budget} budget, in {count} messages.',
    usageSummarySpent: 'This month you used {spent}, in {count} messages.',
    usageSummaryMessages: 'This month you sent {count} messages.',
    usageSummaryAll: '{count} messages since you started.',
    usageAdvancedIntro:
      'How much you used this month and in total, for those who want to follow it up.',
    connectedAccounts: 'Connected accounts',
    microsoft365: 'Microsoft 365',
    connectMicrosoft365: 'Connect Microsoft 365',
    reconnectMicrosoft365: 'Reconnect',
    microsoftReconnectRequired:
      'New permissions are needed to read mail content and your calendar. Connect your Microsoft account again.',
    microsoftNotConnected:
      'Connect Microsoft 365 to make mail, OneDrive, and SharePoint context available.',
    microsoftStatusUnavailable: 'Microsoft 365 status could not be loaded.',
    microsoftAdminSetupRequired: 'Microsoft 365 must be configured by an admin first.',
    microsoftConnectFailed: 'Microsoft 365 connection failed.',
    devopsTokenSaved: 'Token saved',
    devopsTokenIntro:
      'Use your own personal access token (PAT) for the organization {organization} when you do not sign in with Microsoft.',
    devopsTokenLabel: 'Personal access token',
    devopsTokenReplace: 'New token to replace the current one',
    devopsTokenChecking: 'Checking…',
    devopsTokenRemove: 'Remove token',
    devopsTokenRemoveConfirm:
      'Azure DevOps then uses your Microsoft sign-in again, if you have one.',
    devopsTokenInvalid:
      'Azure DevOps does not accept this token. Check that it is valid, not expired, and created for this organization.',
    devopsTokenFailed: 'The token could not be saved. Try again later.',
    devopsTokenPrivacy:
      'The token is stored encrypted, only used for you and never shown again. Give it only the permissions you need.',
    leexiKeySaved: 'Key saved',
    leexiKeyCompanyFallback: 'Without your own key, NIVIO uses the company key your admin set up.',
    leexiKeyIntro:
      'Ask your Leexi admin for an API key limited to your own calls (Company Settings → API Keys) and enter it here.',
    leexiKeyIdLabel: 'Key ID',
    leexiKeySecretLabel: 'Key secret',
    leexiKeySecretReplace: 'New secret to replace the current key',
    leexiKeyRemove: 'Remove key',
    leexiKeyRemoveConfirm: 'NIVIO then uses the company key from your admin, if there is one.',
    leexiKeyInvalid: 'Leexi does not accept this key. Check the key ID and the secret.',
    leexiKeyFailed: 'The key could not be saved. Try again later.',
    leexiKeyPrivacy:
      'The key is stored encrypted, only used for you and never shown again. Give it access only to what you need.',
    microsoftMail: 'Microsoft Mail',
    connectMicrosoftMail: 'Connect Microsoft Mail',
    connected: 'Connected',
    notConnected: 'Not connected',
    connectedAs: 'Connected as',
    connecting: 'Connecting...',
    mailNotConnected:
      'Connect your mailbox so the Email Assistant can answer questions about your email.',
    mailStatusUnavailable: 'Mail status could not be loaded.',
    mailAdminSetupRequired: 'Microsoft Mail must be configured by an admin first.',
    mailConnectFailed: 'Microsoft Mail connection failed.',
    confirmMailSendTitle: 'Send email?',
    confirmMailSendDescription: 'Review the content before sending this email.',
    mailTo: 'To',
    mailCc: 'Cc',
    mailSubject: 'Subject',
    mailBody: 'Body',
    sendMail: 'Send email',
    sending: 'Sending...',
    mailSent: 'Email sent.',
    mailSendFailed: 'Email could not be sent.',
    thisMonth: 'This month',
    allTime: 'All time',
    costManagedInAdmin:
      'Your administrator manages prices and budgets. Here you only see your own usage.',
    assistant: 'assistant',
    companyAssistants: 'Company assistants',
    personalAssistants: 'Personal assistants',
    tool: 'tool',
    toolUsed: 'Tool used',
    toolsUsed: 'tools used',
    toolActMakeWord: 'Creating a Word document',
    toolActMakeExcel: 'Creating an Excel file',
    toolActMakePowerPoint: 'Creating a PowerPoint presentation',
    toolActMakeImage: 'Creating an image',
    toolActMailRead: 'Reading your mail',
    toolActMailSend: 'Sending an email',
    toolActCalendar: 'Checking your calendar',
    toolActTeamsRead: 'Looking in Teams',
    toolActTeamsPost: 'Posting a message in Teams',
    toolActPlannerRead: 'Looking in Planner',
    toolActPlannerWrite: 'Updating a task in Planner',
    toolActListsRead: 'Looking in a SharePoint list',
    toolActListsWrite: 'Updating a SharePoint list',
    toolActDriveSearch: 'Searching OneDrive and SharePoint',
    toolActDriveRead: 'Reading a file from OneDrive or SharePoint',
    toolActDriveWrite: 'Updating a file in OneDrive or SharePoint',
    toolActAnalyse: 'Analysing the attachment',
    toolActProjectDoc: 'Reading a project document',
    toolActData: 'Querying the database',
    toolActWebSearch: 'Searching the web',
    toolActConsult: 'Consulting {name}',
    toolQuery: 'How this was looked up',
    toolQueryRows: '{count} rows',
    toolQueryMoreRows: 'There are probably more rows than shown.',
    toolSourceWeb: 'Web',
    toolSourceImage: 'Image',
    toolSourceAttachment: 'Attachment',
    toolSourceProject: 'Project documents',
    toolSourceData: 'Database',
    toolSources: 'Sources',
    failed: 'failed',
    done: 'done',
    running: 'running',
    completed: 'completed',
    errorStatus: 'error',
    answerMore: 'this answer',
    tokensIn: 'in',
    tokensOut: 'out',
    aiTasksIntro:
      'Schedule a skill to run in the background. Completed tasks appear here and as conversations.',
    title: 'Title',
    description: 'Description',
    edit: 'Edit',
    save: 'Save',
    weeklyFinanceInsight: 'Weekly finance insight',
    planning: 'Schedule',
    hourly: 'Hourly',
    every6Hours: 'Every 6 hours',
    daily: 'Daily',
    weekly: 'Weekly',
    prompt: 'Prompt',
    promptPlaceholder: 'Run this skill and summarize the result for me.',
    createAiTask: 'Create AI task',
    aiTaskActionFailed: 'The AI task could not be updated.',
    editAiTask: 'Edit AI task',
    scheduledAiTasks: 'Scheduled AI tasks',
    noAiTasks: 'No AI tasks yet.',
    enabled: 'Enabled',
    paused: 'Paused',
    next: 'next',
    runNow: 'Run now',
    pause: 'Pause',
    enable: 'Enable',
    delete: 'Delete',
    runs: 'Runs',
    runStarted: 'Started.',
    noRuns: 'No runs yet.',
    open: 'Open',
    view: 'View',
    viewList: 'List',
    viewTiles: 'Tiles',
    releaseNotesOpen: 'See what is new in version',
    releaseNotesTitle: 'New in version',
    releaseNotesIntro: 'Functional changes available in this version.',
    releaseNotesEmpty: 'This version has no functional changes for users.',
    actions: 'Actions',
    markRead: 'Mark read',
    signIn: 'Sign in',
    useGatewayCredentials: 'Sign in with your NIVIO account.',
    signInWithSso: 'Microsoft Account',
    continueWithSso: 'Or sign in with Microsoft',
    ssoSignInFailed: 'SSO sign-in failed.',
    email: 'Email',
    password: 'Password',
    signingIn: 'Signing in...',
    interactiveHtml: 'Web page',
    artifacts: 'Files',
    artifactsIntro:
      'Review generated Office files, images, and interactive HTML across your conversations.',
    noArtifacts: 'No files found yet.',
    projects: 'Projects',
    project: 'Project',
    noProject: 'No project',
    projectsIntro:
      'Bundle conversations, instructions, and private documents into a scoped project context.',
    createProject: 'Create project',
    newProject: 'New project',
    noProjects: 'No projects yet.',
    startProjectChat: 'Start project chat',
    projectNewChat: 'New chat in this project',
    projectChatsShort: 'Chats',
    projectDocsShort: 'Documents',
    addDocument: 'Add',
    documentActions: 'Document actions',
    usedInChats: 'used in chats',
    notInChats: 'not in chats',
    useInChats: 'Use in chats',
    projectInstructions: 'Project instructions',
    deleteProjectConfirm: 'Delete this project? Documents and project links will be removed.',
    projectDocuments: 'Project documents',
    projectDocumentsIntro:
      'Uploads stay in private storage. Text files are immediately used as project context.',
    projectChats: 'Project chats',
    projectChatsIntro: 'Conversations linked to this project.',
    noProjectChats: 'No project chats yet.',
    messages: 'messages',
    uploadDocument: 'Upload document',
    indexDocument: 'Process',
    uploadFailed: 'Upload failed.',
    noProjectDocuments: 'No documents yet.',
    documents: 'Documents',
    projectPolicyPrefix: 'Limits:',
    ocrEnabled: 'OCR on',
    ocrDisabled: 'OCR off',
    inactiveDocument: 'not used in chat',
    disableInChat: 'Exclude',
    enableInChat: 'Use',
    manage: 'Manage',
    displayName: 'Display name',
    tags: 'Tags',
    tagsPlaceholder: 'kickoff, scope, finance',
    cancel: 'Cancel',
    documentStatusIndexed: 'Indexed',
    documentStatusIndexing: 'Indexing',
    documentStatusStored: 'Stored',
    documentStatusUnsupported: 'Unsupported',
    documentStatusFailed: 'Failed',
    previewUnavailable: 'Preview unavailable.',
    previewTruncated: 'Preview truncated.',
    openPreview: 'Open preview',
    sandboxedPreview: 'Sandboxed preview',
    videoPresentation: 'Video presentation',
    videoScenes: '{count} scenes',
    recordVideo: 'Record as video',
    stopRecording: 'Stop recording',
    recordingFailed: 'Recording did not work. Choose this tab when the browser asks what to share.',
    preview: 'Preview',
    copyHtml: 'Copy HTML',
    openInNewTab: 'Open in new tab',
    close: 'Close',
    modelsAll: 'All',
    modelsRecommended: 'Recommended',
    officeFile: 'Office file',
    download: 'Download',
    saving: 'Saving...',
    saveToSharePoint: 'Save to SharePoint',
    savedToSharePoint: 'Saved to SharePoint.',
    deleteConversation: 'Delete conversation',
    deleteConversationConfirmPrefix: 'Delete',
    deleteConversationConfirmSuffix: "This can't be undone.",
    resizePreview: 'Resize preview',
    openNavigation: 'Open navigation',
    closeNavigation: 'Close navigation',
    toggleWiderPreview: 'Make preview wider or narrower',
    toolIterationCapReached: 'Tool-iteration cap reached.',
    noticeToolsUnavailable:
      'The knowledge sources could not be loaded. This answer was made without knowledge sources and without web search.',
    noticeToolDenied:
      'You have no access to one of the requested knowledge sources. It was not used.',
    noticeForcedFinal:
      'The tool limit for this answer was reached. The answer uses the information found so far.',
    noticeToolCallsDropped: 'An incomplete tool call from the model was skipped.',
    sharePointNotConfigured:
      'SharePoint is not configured yet. Configure it in the Gateway admin settings first.',
    sharePointRejected:
      'SharePoint credentials were rejected. Check the tenant, client ID, secret, and Graph permissions.',
    sharePointForbidden: 'The SharePoint app does not have permission to save files here.',
    sharePointMissing: 'The configured SharePoint drive or folder could not be found.',
    sharePointTooLarge: 'This file is larger than the configured SharePoint upload limit.',
    sharePointSaveFailed: 'SharePoint save failed.',
    couldNotSaveSharePoint: 'Could not save the file to SharePoint.',
    attachShort: '+ Attach',
    attentionBadge: 'Tasks that need attention',
    notifyOnFinish: 'Notify me when it finishes',
    notifyOnFinishHelp: 'You get a notification at the top right and a badge in the sidebar.',
    scheduleTime: 'Time',
    scheduleTimeHint: 'Optional. Leave empty to repeat from the moment you save.',
    scheduleTimeWeeklyHint:
      'Optional. The first run is today at this time, or next week if it has passed.',
    ownerCol: 'Owner',
    usageCol: 'Usage',
    teamSkill: 'team skill',
    personalSkillLabel: 'Personal skill',
    usedTimesMonth: 'Used {n} times this month',
    usedOnceMonth: 'Used once this month',
    notUsedMonth: 'Not used this month',
    usageHint: 'Your own messages in chats that used this skill, this month.',
    version: 'version',
    testInChat: 'Test in chat',
    skillExamples: 'Examples',
    skillExamplesHelp: 'Phrases that should trigger this skill.',
    skillExampleAdd: 'Add an example',
    skillExampleRemove: 'Remove example',
    skillVisibleTo: 'Visible to',
    skillAllowTeamEdits: 'Allow edits by the team',
    visibilityCol: 'Visibility',
    visibilityPrivate: 'Private',
    visibilityShared: 'Shared',
    sharedWithYou: 'Shared with you',
    sharedProjectNote:
      '{name} shared this project with you. You can chat in it and use its documents; only the owner can change it. Your conversations stay private.',
    aColleague: 'A colleague',
    sharedBy: 'Shared by {name}',
    showAllDocuments: 'Show all {count} documents',
    shareWith: 'Share with',
    shareHelp:
      "Who can use the project: its instructions, documents, knowledge groups and skills. Only you can change it. Everyone's conversations stay private.",
    shareNobody: 'Nobody (private)',
    shareEveryone: 'Everyone',
    resizeSidebar: 'Resize the sidebar (drag or use the arrow keys, double-click for the default)',
    shareGroups: 'Selected groups',
    shareGroupsLabel: 'Groups',
    shareGroupsEmpty: 'Choose at least one group, or the project stays private.',
    noProjectSkills: 'No skills',
    projectSkillsHelp:
      'These skills are on by default in each new conversation in this project. They can be switched off per conversation. People who may not use a skill do not get it.',
    visibilityHelp: 'A label only: sharing with other people is not available yet.',
    lstFiles: 'files',
    runNotifyAgentDone: 'Agent ‘{name}’ is done',
    runNotifyAgentFailed: 'Agent ‘{name}’ failed',
    runNotifyTaskDone: 'AI task ‘{name}’ is done',
    runNotifyTaskFailed: 'AI task ‘{name}’ failed',
    runNotifyDoneLabel: 'Done',
    runNotifyFailedLabel: 'Failed',
    runNotifyOpen: 'Open run',
    runNotifyDismiss: 'Dismiss notification',
    tourWelcomeTitle: 'Welcome to NIVIO, {name}',
    tourWelcomeTitleNoName: 'Welcome to NIVIO',
    tourWelcomeText:
      'A short tour of {count} steps shows how to ask questions, use your company data and automate tasks.',
    tourLater: 'Later',
    tourStart: 'Start the tour',
    tourStepOf: 'Step {current} of {total}',
    tourSkip: 'Skip',
    tourPrevious: 'Previous',
    tourNext: 'Next',
    tourDone: 'Done',
    tourAskTitle: 'Ask a question',
    tourAskBody:
      'Type your question in plain language and press Enter. Shift+Enter starts a new line.',
    tourDataTitle: 'Use your company data',
    tourDataBody:
      'Use + to turn on knowledge groups and skills, such as Business Central or SharePoint. Or type @ in your question.',
    tourFilesTitle: 'Files and dictation',
    tourFilesBody:
      'Add files with + or drag them into the input. Use the microphone to speak your question.',
    tourFilesOnlyTitle: 'Add files',
    tourFilesOnlyBody:
      'Add files with + or drag them into the input. NIVIO reads them along with your question.',
    tourProjectsTitle: 'Projects',
    tourProjectsBody:
      'Group conversations and documents per customer or topic. Every question in a project uses its documents.',
    tourAgentsTitle: 'Agents and tasks',
    tourAgentsBody:
      'Let recurring work run on its own, such as a weekly overview. You get a notification when it is done.',
    tourAccountsTitle: 'Connect accounts',
    tourAccountsBody:
      'Connect Microsoft 365 under Settings → Connected accounts. NIVIO can then use your mail, calendar and files.',
    tourAccountsBodyDevOps:
      'Connect Microsoft 365 and your Azure DevOps token under Settings → Connected accounts. NIVIO then works with them using your permissions.',
    tourSetting: 'Tour',
    tourSettingIntro: 'A short explanation of the main parts of the chat.',
    tourCompletedOn: 'Completed on {date}',
    tourSkippedStatus: 'Skipped',
    tourNotYet: 'Not taken yet',
    tourRestart: 'Start again',
    pwaInstallTitle: 'Install app',
    pwaInstallIntro: 'Open the chat like an app, from your home screen or desktop.',
    pwaInstallButton: 'Install app',
    pwaInstalled: 'The app is installed on this device.',
    pwaIosStep1: 'Tap Share in the Safari toolbar.',
    pwaIosStep2: 'Choose Add to Home Screen.',
    pwaIosStep3: 'Tap Add.',
    pwaGenericHint: 'Use your browser menu: Install app or Add to home screen.',
    pwaUpdateAvailable: 'New version available',
    pwaUpdateReload: 'Refresh',
    pushTitle: 'Notifications',
    pushIntro:
      'Get a notification on this device when an agent or AI task finishes, even when the app is closed. You choose per task whether you want one.',
    pushUnsupported: 'This browser does not support notifications.',
    pushServerOff: 'Notifications are not enabled by your administrator.',
    pushIosInstall:
      'Install the app on your home screen first (iPhone or iPad with iOS 16.4 or newer, see Install app above). Then open it from your home screen to turn notifications on.',
    pushDenied:
      'Notifications are blocked for this site. Allow them in your browser or device settings, then come back.',
    pushEnable: 'Turn on notifications on this device',
    pushDisable: 'Turn off notifications on this device',
    pushOn: 'Notifications are on for this device.',
    pushFailed: 'Turning notifications on or off failed. Try again.',
    pwaShareHint:
      'On Android you can share text and files from other apps with Chat (Share, then Chat). iPhone and iPad do not support this.',
    shareReceived: 'Shared to NIVIO: {count} file(s).',
    shareUnsupported: 'Sharing only works after the app is installed and reopened.',
    shareTooLarge: 'What you shared is too large to open here (25 MB at most).',
    shareExpired: 'What you shared is no longer available. Share it again.',
    shareFailed: 'What you shared could not be opened. Share it again.',
  },
  fr: {
    orchWaitingTool: 'En attente d’un outil dans Chat',
    orchGoodAnswer: 'Bonne réponse',
    orchBadAnswer: 'Mauvaise réponse',
    orchApproveTitle: 'Approuver le plan',
    orchApproveBudget: 'Budget dépassé',
    orchApprovePolicy: 'À approuver',
    orchEstimate: 'Estimé',
    orchRunPlan: 'Exécuter le plan ({count} étapes)',
    orchApproveHint: 'Retirez une étape ou choisissez un autre modèle ; le coût suit.',
    orchStepModel: 'Modèle pour {step}',
    feedbackWhy: 'Qu’est-ce qui n’allait pas ?',
    feedbackWrong: 'Erroné',
    feedbackLayout: 'Mise en page',
    feedbackNotAsked: 'Pas ce que j’ai demandé',
    feedbackSlow: 'Trop lent',
    artifactVersion: 'version {n}',
    artifactChanged: '{parts} modifié',
    artifactVersions: 'Versions',
    artifactVersionOf: 'Version {n} sur {total}',
    artifactWhatChanged: 'Modifié :',
    artifactFirstVersion: 'Voir la première version',
    summaryTitle: 'Résumé',
    summaryMaking: 'Création du résumé…',
    summaryFailed: 'Le résumé n’a pas pu être créé. Réessayez.',
    summaryTopic: 'De quoi il s’agit',
    summaryDecided: 'Décidé',
    summaryOpen: 'Encore ouvert',
    summaryMade: 'Créé dans cette conversation',
    summaryCopy: 'Copier',
    summaryContinue: 'Continuer dans une nouvelle conversation',
    summaryContinueHint:
      'Une nouvelle conversation part de ce résumé ; elle est plus rapide et moins chère.',
    summaryContinueTitle: 'Suite : {title}',
    summaryFrom: 'Résumé de « {title} »',
    summaryOffer:
      'Cette conversation est devenue longue. Continuer dans une nouvelle avec un résumé ?',
    summaryOfferYes: 'Résumer',
    aiNotice: 'Vous parlez à un assistant IA. Les réponses peuvent contenir des erreurs.',
    aiNoticeShort: 'Assistant IA · peut se tromper',
    aiNoticeMore: 'En savoir plus',
    aiNoticeTitle: 'L’IA dans Chat',
    aiNoticeModels:
      'Les réponses viennent de modèles d’IA ; NIVIO choisit parfois un modèle par étape. Elles peuvent contenir des erreurs : vérifiez les informations importantes.',
    aiNoticeMedia: 'Les images, vidéos et présentations créées par Chat sont générées par l’IA.',
    aiNoticePolicy: 'Lire la politique IA de votre organisation',
    templateSaveTitle: 'Enregistrer comme modèle',
    templateSaveDescription:
      'Relancez cette question plus tard en un clic, avec le même modèle d’IA, les mêmes skills et connaissances.',
    templateName: 'Nom',
    templatePrompt: 'Question',
    templateFieldsHint: 'Chat demande à chaque fois les mots entre { }, p. ex. {mois} ou {client}.',
    templateIncludes: 'Ce qui est inclus',
    templateWho: 'Qui peut l’utiliser',
    templateOnlyMe: 'Moi seulement',
    templateEveryone: 'Tous ceux qui peuvent utiliser ce modèle, ces skills et connaissances',
    templateSaved: 'Enregistré',
    templateSaveFailed: 'Le modèle n’a pas pu être enregistré.',
    templateDelete: 'Supprimer le modèle {name}',
    templateDeleteFailed: 'Le modèle n’a pas pu être supprimé.',
    templateFillDescription: 'Complétez ce que ce modèle demande.',
    templateStart: 'Démarrer',
    back: 'Retour',
    agentManual: 'Remplir soi-même',
    agentTest: 'Tester',
    agentRetest: 'Tester à nouveau',
    agentSteps: 'Étapes',
    teamStepGoal: 'Objectif',
    teamStepCompose: 'Composer',
    teamStep1Intro: 'Plusieurs agents qui accomplissent ensemble une même tâche.',
    teamStep2Intro: 'Proposé par NIVIO. Modifiez l’ordre ou les membres.',
    teamEditIntro: 'Modifiez la tâche, l’ordre, les membres ou le planning.',
    teamStep3Intro: 'Une exécution d’essai de toute l’équipe, sans actions d’écriture.',
    teamDescribeLabel: 'Quelle tâche l’équipe doit-elle accomplir ?',
    teamDescribeHint:
      'Une équipe a du sens quand la tâche comporte des parties distinctes qui demandent chacune d’autres connaissances.',
    teamDescribePlaceholder:
      'P. ex. chaque lundi, un rapport de santé par client clé : factures ouvertes, tickets ouverts et actualités récentes.',
    teamHow1: 'Les spécialistes travaillent l’un après l’autre, chacun avec ses connaissances.',
    teamHow2: 'Chaque spécialiste voit ce que le précédent a trouvé.',
    teamHow3: 'Le coordinateur réunit tout en un seul résultat.',
    teamWrite: 'Proposer une équipe',
    teamWriting: 'NIVIO propose une équipe…',
    teamDescribeNote:
      'NIVIO choisit parmi vos agents et en propose de nouveaux s’il n’y en a pas. Rien n’est encore enregistré.',
    teamWriteFailed:
      'NIVIO n’a pas pu proposer d’équipe. Réessayez ou décrivez la tâche autrement.',
    teamSection: 'Équipe',
    teamSectionHelp: 'Tous les spécialistes et le coordinateur lisent la tâche à chaque exécution.',
    teamOrder: 'Ordre',
    teamOrderHelp:
      'Les spécialistes travaillent de haut en bas ; le coordinateur réunit leurs contributions.',
    teamExisting: 'Existant',
    teamNew: 'Nouveau',
    teamNewAgent: 'Nouvel agent',
    teamRemove: 'Retirer de l’équipe',
    teamCoordinator: 'Coordinateur',
    teamCoordinatorHelp: 'Réunit les contributions en un seul résultat.',
    teamDefaultCoordinator: 'Coordinateur NIVIO standard',
    teamAddExisting: '+ Agent existant',
    teamAddNew: 'Nouvel agent',
    teamMaxHelp:
      '3 spécialistes au maximum. Un nouvel agent n’est créé qu’avec l’équipe et ne s’exécute que dans l’équipe.',
    teamScheduleHelp:
      'Quand l’équipe démarre d’elle-même. Les agents de l’équipe suivent ce planning, pas le leur.',
    teamTesting: 'L’équipe exécute les étapes l’une après l’autre…',
    teamScoreLine: '{checksPassed} contrôles sur {checks} réussis.',
    teamPerStep: 'Par étape',
    teamChecks: 'contrôles',
    teamStepOk: 'Correct',
    teamStepWaiting: 'Attend l’étape précédente',
    teamSuggestionFor: 'Proposition pour {name}',
    teamChangeAgentTitle: 'Modifier {name} ?',
    teamChangeAgentHelp:
      'Cet agent travaille aussi hors de cette équipe. La modification s’applique partout dès que vous enregistrez l’équipe.',
    teamTestRule:
      'Chaque étape est évaluée séparément : vous voyez quel agent freine l’équipe. Une étape qui échoue arrête les suivantes.',
    teamSaveFailed: 'L’équipe n’a pas pu être enregistrée. Réessayez.',
    teamCheckSpecialist: 'Apporte sa propre contribution vérifiable à la tâche de l’équipe',
    teamCheckAnswer: 'Répond à la tâche de l’équipe en un résultat clair',
    teamCheckUsesAll: 'Utilise les contributions de tous les spécialistes',
    teamLastTest: 'Dernier test {n} %',
    agentStepDescribe: 'Décrire',
    agentStepSetup: 'Configurer',
    agentStepTest: 'Tester',
    agentStep1Intro: 'Dites ce que l’agent doit faire ; NIVIO le rédige.',
    agentStep2Intro: 'Corrigez ce qui ne va pas, puis testez l’agent.',
    agentStep3Intro: 'Testez l’agent avant de l’enregistrer.',
    agentDescribeLabel: 'Que doit faire votre agent ?',
    agentDescribeHint:
      'Dites ce que fait l’agent, avec quelles données, à quelle fréquence et à quoi ressemble le résultat.',
    agentDescribePlaceholder:
      'P. ex. suivre chaque jour ouvrable les factures ouvertes et proposer des e-mails de rappel.',
    agentIdeas: 'Ou partez d’un exemple',
    agentIdeaRelease: 'Note de version hebdomadaire depuis GitHub',
    agentIdeaReleasePrompt:
      'Chaque vendredi, rédige une note de version de ce qui a changé cette semaine dans notre dépôt GitHub, avec les issues ouvertes.',
    agentIdeaNews: 'Actualités de mes clients',
    agentIdeaNewsPrompt:
      'Chaque matin, cherche l’actualité de mes principaux clients et résume par client ce qui nous concerne.',
    agentIdeaStock: 'Signaler le stock sous le minimum',
    agentIdeaStockPrompt:
      'Chaque jour ouvrable, vérifie quels articles sont sous leur stock minimum et propose une liste de commande.',
    agentIdeaAgenda: 'Préparer l’agenda de demain',
    agentIdeaAgendaPrompt:
      'Chaque soir, prépare mes rendez-vous de demain : qui, à quel sujet et ce que je dois savoir avant.',
    agentDescribeNote:
      'NIVIO crée un nom, des instructions et 3 cas de test. Rien n’est encore enregistré.',
    agentWrite: 'Rédiger l’agent',
    agentWriting: 'NIVIO rédige…',
    agentWriteFailed: 'L’agent n’a pas pu être rédigé. Réessayez.',
    agentWriteTestsOnly: 'Garde l’agent tel quel ; rédige seulement ses 3 cas de test.',
    agentSaveFailed: 'L’agent n’a pas pu être enregistré.',
    agentSaveKnowledge:
      'Ce skill utilise des connaissances que vous ne pouvez pas utiliser ({groups}). Choisissez un autre skill.',
    agentInstructionsHelp: 'L’agent lit les instructions à chaque exécution.',
    agentRewrite: 'Réécrire autrement',
    agentRewriteShorter: 'Plus court',
    agentRewriteShorterPrompt: 'Raccourcis les instructions sans rien perdre d’essentiel.',
    agentRewriteStricter: 'Plus strict',
    agentRewriteStricterPrompt:
      'Rends les instructions plus strictes et plus précises sur le résultat.',
    agentRewriteEnglish: 'En anglais',
    agentRewriteEnglishPrompt: 'Write the name and instructions in English.',
    agentRewriteWish: 'Votre souhait, p. ex. « ajoute un total »',
    agentRewriteGo: 'Réécrire',
    agentSkillKnowledge: 'Skill et connaissances',
    agentSkillKnowledgeHelp:
      'NIVIO les a proposés d’après votre description. Vous ne voyez que ce que vous pouvez utiliser.',
    agentModelHelp: 'Qui fait le travail.',
    agentProviderModel: 'Fournisseur et modèle',
    agentScheduleHelp:
      'Quand l’agent démarre seul. Vous pouvez toujours aussi le lancer à la main.',
    agentSchedule_manual: 'Seulement à la main',
    agentSchedule_weekdays: 'Chaque jour ouvrable',
    agentSchedule_daily: 'Chaque jour',
    agentSchedule_weekly: 'Chaque semaine',
    agentSchedule_hourly: 'Toutes les heures',
    agentScore: 'Taux de réussite',
    agentScore_ok: 'Bon',
    agentScore_warn: 'Peut mieux faire',
    agentScore_err: 'Ne fonctionne pas',
    agentScoreStale: 'Instructions modifiées : testez à nouveau',
    agentTesting: 'NIVIO exécute les cas de test et évalue les réponses…',
    agentScoreLine:
      '{passed} tests réussis sur {tests} · {checksPassed} contrôles en ordre sur {checks}.',
    agentTestFailed: 'Les tests n’ont pas pu s’exécuter. Réessayez.',
    agentTestNote:
      'Testé sans actions d’écriture : rien n’est envoyé ni modifié. NIVIO évalue les réponses.',
    agentImprove: 'Améliorer les instructions',
    agentImproving: 'NIVIO cherche des modifications…',
    agentImproveHelp: 'Des modifications qui devraient corriger les contrôles échoués.',
    agentApply: 'Appliquer',
    agentApplyAllRetest: 'Tout appliquer et tester à nouveau',
    agentTestCases: 'Cas de test',
    agentTestCol: 'Test',
    agentChecksCol: 'Contrôles',
    agentChecksDone: 'En ordre',
    agentResultCol: 'Résultat',
    agentTestRunning: 'En cours',
    agentTestError: 'Pas exécuté',
    agentTestPassed: 'Réussi',
    agentTestNotPassed: 'Échoué',
    agentAnswer: 'Ce que l’agent a répondu',
    agentNoAnswer: 'Pas de réponse.',
    agentCheckMinor: 'moins important',
    agentTestRule:
      'Un test réussit quand tous les contrôles importants sont en ordre. Sous 80 %, nous conseillons d’améliorer les instructions ; vous pouvez toujours enregistrer.',
    agentNotTested: 'Pas encore testé',
    agentLastTest: 'Dernier test {n} %',
    templates: 'Modèles',
    newTemplate: 'Nouveau modèle',
    templateManage: 'Gérer les modèles',
    templateManageShort: 'Gérer',
    templateFromColleague: 'D’un collègue',
    templateHiddenLabel: 'Masqué',
    templateSharedLabel: 'Partagé',
    templateShownLabel: 'Affiché',
    templateOrderFailed: 'L’ordre n’a pas pu être enregistré.',
    templateDeleteHelp: 'Action irréversible. Les chats lancés avec ce modèle restent.',
    templateEdit: 'Ouvrir et modifier',
    templateStartChat: 'Démarrer un chat',
    templateMoveUp: 'Monter',
    templateMoveDown: 'Descendre',
    templateShow: 'Afficher sur un nouveau chat',
    templateHide: 'Masquer sur un nouveau chat',
    searchTemplates: 'Rechercher des modèles',
    noTemplates:
      'Vous n’avez pas encore de modèles. Enregistrez une question d’un chat comme modèle, ou créez-en un ici.',
    templateOrderHelp:
      'Cet ordre est celui des boutons jaunes d’un nouveau chat. Glissez une ligne, ou choisissez Monter ou Descendre dans le menu.',
    templateOrderHelpMenu:
      'Cet ordre est celui des boutons jaunes d’un nouveau chat. Choisissez Monter ou Descendre dans le menu.',
    templateFieldCount: '{n} champs',
    templateCol: 'Modèle',
    templateContents: 'Contient',
    templateNewChatCol: 'Nouveau chat',
    templateOthersHelp:
      'Vous pouvez lancer les modèles de vos collègues, mais pas les modifier ni les supprimer.',
    templateNotFound: 'Ce modèle n’existe plus, ou il ne vous appartient pas.',
    templateNew: 'Nouveau modèle',
    templateEditIntro: 'Une question que vous relancez en un clic.',
    templateNewIntro:
      'Une question avec son modèle d’IA, ses skills et connaissances, prête en un clic.',
    templateNameHelp: 'Affiché sur le bouton jaune d’un nouveau chat.',
    templateFields: 'Champs de cette question',
    templateNoFields: 'Aucun champ. Tapez p. ex. {client} dans la question pour en ajouter un.',
    templateModelKnowledge: 'Modèle d’IA et connaissances',
    templateModelKnowledgeHelp:
      'Ce avec quoi le chat démarre. Vous ne choisissez que ce que vous pouvez utiliser.',
    templateNewChatSection: 'Nouveau chat',
    templateNewChatHelp: 'Si et où ce modèle apparaît parmi les suggestions.',
    templateShowOnNewChat: 'Afficher sur un nouveau chat',
    templateShowOnNewChatHint:
      'Désactivé : le modèle est conservé, mais n’apparaît pas comme bouton jaune.',
    templatePlace: 'Position',
    templatePlaceHint:
      'Les premiers sont visibles tout de suite ; « Autres idées » montre le reste.',
    templatePlaceOf: '{n} sur {total}',
    templateWhoHelp:
      'Partagé, il n’apparaît qu’aux collègues qui peuvent utiliser le modèle d’IA, les skills et les connaissances.',
    templateDeleteButton: 'Supprimer le modèle…',
    orchRunAnyway: 'Exécuter quand même',
    orchPlan: 'Plan',
    orchProgress: 'Progression',
    orchStatusRunning: 'En cours',
    orchStatusDone: 'Terminé',
    orchStatusWaiting: 'En attente',
    orchStatusFailed: 'Échec',
    orchStatusSkipped: 'Ignorée',
    orchStatusFallback: 'Réserve',
    orchStepCheck: 'Contrôle de confidentialité',
    orchStepPlan: 'Établir le plan',
    orchStepAnswer: 'Rédiger la réponse',
    orchStepsDone: '{done} étapes sur {total} terminées',
    orchSteps: '{count} étapes',
    orchModels: '{count} modèles',
    orchFallbackUsed: 'Réserve utilisée',
    orchShowPlan: 'Voir le plan',
    orchClosePanel: 'Fermer le panneau',
    orchCostPerModel: 'Coût par modèle',
    orchTotal: 'Total',
    orchTogether: 'En parallèle',
    orchConfidential: 'Confidentiel : reste local',
    orchNotConfidential: 'Non confidentiel',
    orchCheckUnavailable: 'Contrôle local injoignable',
    orchOverBudget: 'Budget dépassé : ± {estimate} (limite {budget})',
    orchModelHint: 'choisit lui-même les modèles',
    toolCallOne: '1 action',
    toolCallMany: '{count} actions',
    toolCallsFailed: '{count} en échec',
    mentionList: 'Faire référence à',
    mentionSkills: 'Skills',
    mentionKnowledge: 'Connaissances',
    mentionDocuments: 'Documents du projet',
    mentionMicrosoft: 'Microsoft 365',
    mentionMail: 'Courrier',
    mentionMailDetail: 'Votre propre boîte mail',
    mentionCalendar: 'Agenda',
    mentionCalendarDetail: 'Votre propre agenda',
    mentionDrive: 'OneDrive et SharePoint',
    mentionDriveDetail: 'Fichiers auxquels vous avez accès',
    mentionActive: 'Actif',
    mentionNone: 'Aucun résultat',
    signInInvalid: "L'adresse e-mail ou le mot de passe est incorrect.",
    signInLocked: 'Trop de tentatives. Patientez un instant et réessayez.',
    signInDisabled: 'Ce compte est désactivé. Contactez votre administrateur.',
    signInFailed: 'La connexion a échoué. Réessayez.',
    fileTypeUnsupported: "Ce type de fichier n'est pas pris en charge.",
    lstStatus: 'Statut',
    lstAllStatuses: 'Tous les statuts',
    lstActions: 'Actions',
    lstChat: 'Chat',
    lstChats: 'chats',
    lstWhen: 'Quand',
    lstSearchApps: 'Rechercher des apps',
    lstApp: 'App',
    lstType: 'Type',
    lstAllTypes: 'Tous les types',
    lstOfficeFiles: 'Fichiers Office',
    lstImages: 'Images',
    lstFile: 'Fichier',
    lstSize: 'Taille',
    lstNeverRun: 'Pas encore exécuté',
    lstSearchAgents: 'Rechercher des agents',
    lstSearchTasks: 'Rechercher des tâches',
    lstSetup: 'Configuration',
    lstSchedule: 'Planification',
    lstLastRun: 'Dernière exécution',
    lstStarted: 'Démarré',
    lstResult: 'Résultat',
    lstUpdated: 'Mis à jour',
    lstContent: 'Contenu',
    lstTeam: 'Équipe',
    lstDomain: 'Domaine',
    selectionActions: 'Actions sur le texte sélectionné',
    selectionDetails: 'Plus de détails',
    selectionAsk: 'Demander',
    selectionDetailsPrompt: 'Explique le texte sélectionné plus en détail.',
    selectionAskPrompt: 'Ma question sur ce texte : ',

    useCompanyCredentials: 'Connectez-vous avec votre compte professionnel.',
    modelScores: 'Évaluation du modèle',
    notRated: 'Non évalué',
    modelScoresHelp:
      'Évaluation de votre équipe. Performance 5 = plus rapide. Quality 5 = meilleure qualité. Cost 1 = peu coûteux, 5 = coûteux.',
    scoreperformance: 'Performance',
    scorequality: 'Quality',
    scorecost: 'Cost',

    starterIdeas: 'Idées pour commencer',
    otherIdeas: 'Autres idées',
    heroMorning: 'Bonjour',
    heroAfternoon: 'Bon après-midi',
    heroEvening: 'Bonsoir',
    heroQuestion: 'Quelle réponse cherchez-vous ?',
    improveStarter: 'Améliorer un texte',
    summarizeStarter: 'Résumer un document',
    prepareStarter: 'Préparer un rendez-vous client',
    improvePrompt: 'Aidez-moi à rendre ce texte clair et professionnel :\n\n',
    summarizePrompt:
      'Résumez le document que je vais ajouter ci-dessous. Indiquez les points clés, les actions et les questions ouvertes.\n\n',
    preparePrompt:
      'Aidez-moi à préparer un rendez-vous client. Posez-moi d’abord des questions sur le client et le but du rendez-vous.',
    organizeStarter: 'Organiser mon travail',
    organizePrompt:
      'Aidez-moi à structurer cette tâche de back-office en étapes et actions claires.',
    analyzeStarter: 'Analyser des chiffres',
    analyzePrompt:
      'Aidez-moi à analyser ces chiffres financiers et à identifier les principaux constats et points d’attention.\n\n',
    createStarter: 'Créer un visuel',
    createPrompt: 'Aidez-moi à rédiger un brief clair, puis à créer une image adaptée.',
    activeContext: 'Contexte actif',
    removeContext: 'Retirer',
    attachmentInProject: 'Les fichiers seront ajoutés au projet',
    attachmentInChat: 'Les fichiers seront ajoutés à cette conversation.',
    attachmentImagesMeter: 'Fichiers',
    attachmentTooManyFiles: '20 fichiers maximum par message ; {count} non ajouté(s).',
    attachmentSizeMeter: 'Taille',
    queuedMessage: 'En attente, envoyé dès que la réponse est prête :',
    attachmentUnsupported:
      '{files} : ce type de fichier n’est pas pris en charge. Demandez à votre administrateur de l’ajouter.',
    attachmentUploadTooLarge:
      'Le fichier est trop volumineux pour être envoyé. Réduisez-le ou demandez à votre administrateur d’augmenter la limite.',
    queuedCancel: 'Modifier',
    usageThisMonth: 'Utilisation ce mois-ci',
    usageOther: 'Autres',
    usageRequests: 'requêtes',
    usageNone: 'Aucune utilisation ce mois-ci.',
    usageBudgetOf: 'sur',
    usageCost: 'Coût',
    disconnectMicrosoft365: 'Déconnecter',
    disconnectMicrosoft365Confirm:
      'Votre compte Microsoft 365 sera déconnecté. Chat ne pourra plus utiliser vos e-mails, agenda, fichiers et Teams tant que vous ne le reconnectez pas.',
    disconnecting: 'Déconnexion…',
    microsoftDisconnectFailed: 'La déconnexion a échoué. Réessayez.',
    usageDetails: 'Détails par modèle',
    sendHint: 'Entrée pour envoyer · Maj + Entrée pour une nouvelle ligne',
    modelsLoadFailed: 'Impossible de charger les modèles. Réessayez.',
    noModelAccess: 'Aucun modèle disponible. Demandez l’accès à votre administrateur.',
    projectHelp: 'Regroupez conversations et fichiers.',
    knowledgeHelp: 'Utilisez les informations des sources connectées.',
    skillHelp: 'Appliquez une méthode réutilisable.',
    draftPreserved: 'Votre message est toujours dans la zone de saisie : vous pouvez le renvoyer.',
    errorEmptyAnswer:
      "Le modèle n'a pas donné de réponse. Réessayez ou choisissez un autre modèle.",
    errorThoughtTooLong:
      'Le modèle a réfléchi si longtemps qu’il a épuisé toute sa limite de réponse sans répondre. Posez une question plus ciblée (par exemple quelques CV à la fois), choisissez un autre modèle ou demandez à votre administrateur un niveau de réflexion plus bas pour ce modèle.',
    errorGeneric: "Une erreur s'est produite lors de la création de la réponse.",
    errorDetails: 'Détails techniques',

    attachments: 'Pièces jointes',
    attachmentImagesTooLarge:
      'Il y a trop d’images pour un seul message (20 maximum). Envoyez-les en plus petits groupes.',
    attachmentFileTooLarge:
      '{filename} dépasse la limite de {limit} par fichier. Réduisez-le ou découpez-le.',
    attachmentTotalTooLarge:
      'Les pièces jointes dépassent ensemble la limite de {limit} par message. Envoyez-les en plus petits groupes.',
    attachmentContextTooLarge:
      'Le texte des documents est trop volumineux pour un seul message. Retirez des pièces jointes ou envoyez-les en petits groupes.',
    saveAsSkill: 'Enregistrer comme skill',
    skillSuggestionText:
      'Cette tâche a pris {n} étapes. Enregistrez la méthode comme skill pour la refaire plus tard en une question.',
    skillSuggestionSave: 'Enregistrer comme skill',
    skillSuggestionDismiss: 'Pas maintenant',
    skillFileSection: 'SKILL.md',
    skillFileHelp:
      "SKILL.md est le format ouvert Agent Skills. Il permet d'échanger des skills avec d'autres outils d'IA.",
    skillImport: 'Importer un SKILL.md',
    skillImported: "Importé. Vérifiez le contenu avant d'enregistrer.",
    skillImportFailed: "Ce fichier n'a pas pu être lu comme SKILL.md.",
    skillExport: 'Exporter en SKILL.md',
    skillPropose: "Proposer à l'organisation",
    skillProposeHelp:
      'Un administrateur examine votre proposition et peut rendre le skill disponible pour tous. Votre propre skill reste inchangé.',
    skillProposed: 'Proposé. Un administrateur examinera le skill.',
    skillProposeFailed: 'La proposition a échoué. Seul le propriétaire peut proposer un skill.',
    branchConversation: 'Continuer dans un nouveau chat',
    copyAnswer: 'Copier toute la réponse',
    copyCode: 'Copier le code',
    imageInputHint:
      '20 fichiers maximum par message. Les grandes images sont réduites automatiquement.',
    copySelection: 'Copier la sélection',
    copied: 'Copié',
    dropDocuments: 'Sélectionnez des fichiers ou déposez-les ici.',
    retryAnswer: 'Régénérer la réponse',
    answerCopied: 'Réponse copiée',
    answerTruncated:
      'Cette réponse a été coupée car elle a atteint la longueur maximale. Demandez « continuer » pour la terminer, ou une version plus courte.',
    copyAnswerFailed: 'Échec de la copie. Réessayez.',
    branchConversationHelp:
      'Reprendre l’historique jusqu’à cette réponse incluse dans un nouveau chat.',
    branchingConversation: 'Création du chat…',
    branchFailed: 'Impossible de créer le chat. Réessayez avec le bouton de la réponse.',
    editSkill: 'Modifier le skill',
    newSkill: 'Nouveau skill',
    skillName: 'Nom',
    skillInstructions: 'Instructions',
    skillWriterTitle: 'Décrivez votre skill',
    skillWriterReviseTitle: 'Faire adapter le skill',
    skillWriterIntro:
      'Dites avec vos mots ce que le skill doit faire. Un modèle d’IA remplit le nom, la description et les instructions ; vous les vérifiez et enregistrez.',
    skillWriterReviseIntro:
      'Dites ce qui doit changer. Un modèle d’IA adapte le nom, la description et les instructions ; vous les vérifiez et enregistrez.',
    skillWriterLabel: 'Que doit faire ce skill ?',
    skillWriterReviseLabel: 'Que faut-il changer ?',
    skillWriterPlaceholder:
      'Par exemple : transformer des notes de réunion brutes en compte rendu avec décisions, actions et responsables.',
    skillWriterAction: 'Rédiger les instructions',
    skillWriterReviseAction: 'Rédiger la modification',
    skillWriterPending: 'Rédaction…',
    skillWriterFailed: 'La rédaction n’a pas abouti. Réessayez ou remplissez les champs vous-même.',
    skillWriterNoModel: 'Aucun modèle n’est disponible pour rédiger le skill.',
    skillWriterDone: 'Rempli par {model}. Vérifiez tout avant d’enregistrer.',
    skillWriterUndo: 'Annuler',
    skillReviewHelp: 'Vérifiez le nom, la description et les instructions avant d’enregistrer.',
    skillDraftLoading: 'Préparation du skill…',
    skillDraftFailed: 'Impossible de préparer le skill. Réessayez.',
    personalSkill: 'Personnel',
    sharedSkill: 'Partagé avec l’équipe',
    skillSharing: 'Partager avec une équipe',
    skillSharingHelp:
      'Les membres peuvent utiliser le skill. Vous seul pouvez le modifier. Choisissez Personnel pour arrêter le partage.',
    skillPrivateHelp:
      'Ce skill est visible uniquement par vous. Vous pourrez le partager avec une équipe depuis Skills.',
    skillNoGroups:
      'Vous ne faites partie d’aucun groupe. Demandez à un administrateur de vous ajouter à une équipe.',
    skillGroupsFailed: 'Impossible de charger les équipes.',
    skillGroupUnavailable: 'Équipe indisponible ; choisissez Personnel.',
    skillSaveFailed: 'Échec de l’enregistrement. Vérifiez votre accès à l’équipe et réessayez.',
    skillSaveConflict:
      'Ce skill a changé. Fermez l’éditeur et rouvrez le skill avant d’enregistrer.',
    skillSaved: 'Skill enregistré.',
    sharedWithMe: 'Partagés avec moi',

    recentConversations: 'Récents',
    conversationActions: 'Actions de conversation',
    allProjects: 'Tous les projets',
    fillInMissingFields: 'Renseignez les champs manquants',
    conversationsIntro: 'Recherchez dans tout votre historique et ouvrez une conversation.',
    moveToProject: 'Déplacer vers un projet',
    removeFromProject: 'Retirer du projet',
    loadMoreConversations: 'Charger plus de conversations',
    projectActions: 'Actions du projet',
    newChatInProject: 'Nouvelle conversation dans ce projet',
    openProject: 'Ouvrir le projet',
    groupBy: 'Regrouper',
    groupByDate: 'Par date',
    groupByProject: 'Par projet',
    showAllOfProject: 'Voir toutes les conversations',
    historyLoadFailed: 'Impossible de charger l’historique.',
    retryHistory: 'Réessayer',

    chatContext: 'Contexte',
    addContext: 'Ajouter',
    backToContext: 'Retour',
    favoriteSkills: 'Skills favoris',
    noFavoriteSkills: 'Ajoutez une étoile aux skills pour les retrouver ici.',
    favoriteSkill: 'Ajouter aux favoris',
    unfavoriteSkill: 'Retirer des favoris',
    contextLoadFailed: 'Impossible de charger cette liste.',
    retryContext: 'Réessayer',

    chatContextIntro:
      'Choisissez le projet, les connaissances et les skills pour cette conversation. Les modifications sont appliquées immédiatement.',
    searchArtifacts: 'Rechercher des fichiers',
    noSearchResults: 'Aucun résultat pour cette recherche.',
    projectSettings: 'Paramètres du projet',
    settingsConflict:
      'Ces paramètres ont été modifiés ailleurs. Chargez les valeurs actuelles ; votre modification proposée est conservée.',
    reloadSettings: 'Charger les paramètres actuels',
    applyOwnChanges: 'Appliquer ma modification',
    documentUploading: 'Téléversement du fichier…',
    documentQueued: 'Fichier enregistré ; en attente de traitement…',
    documentProcessing: 'Traitement du document…',
    documentProcessingFailed:
      'Ce document ne peut pas être traité pour le chat. Consultez son état dans le projet.',
    approvalUnknown: 'Résultat inconnu',
    unknownOutcomeHelp:
      'Vérifiez le résultat dans le système externe. Ces boutons enregistrent votre vérification sans exécuter à nouveau cette action.',
    verifyOutcome: 'Enregistrer le résultat',
    verifyOutcomeDescription:
      'Avez-vous vérifié dans le système externe si cette action a été exécutée ? Seul le résultat vérifié sera enregistré.',
    verifiedExecuted: 'Vérifié : exécuté',
    verifiedNotExecuted: 'Vérifié : non exécuté',
    outcomeVerified: 'Vérifié par vous',
    actionReference: 'Référence de l’action',
    queued: 'En attente',
    runQueued: 'En attente de capacité d’exécution disponible.',
    confirm: 'Confirmer',

    savingSettings: 'Enregistrement des paramètres…',
    newResponse: 'Nouvelle réponse',
    generatingAnswer: 'Génération de la réponse…',
    dataOwner: 'Responsable des données',
    loading: 'Chargement...',
    backendUnavailable: 'Le backend chat est inaccessible.',
    conversationNotFound: 'Conversation introuvable.',
    conversationUpdateFailed: 'Les paramètres de la conversation n’ont pas pu être mis à jour.',
    newConversation: 'Nouvelle conversation',
    newConversationFailed: 'La nouvelle conversation n’a pas pu être démarrée.',
    testInChatSaveFirst: 'Enregistrez d’abord vos modifications pour les tester.',
    incompatibleSkillModels:
      'Ces skills nécessitent des modèles différents et ne peuvent pas être utilisées ensemble.',
    modelRequiredBySkill: 'Le modèle choisi n’est pas compatible avec la skill sélectionnée.',
    projectActionFailed: 'L’action du projet n’a pas pu être effectuée.',
    conversations: 'Conversations',
    noConversations: 'Aucune conversation.',
    searchConversations: 'Rechercher des conversations',
    noConversationMatches: 'Aucune conversation trouvée.',
    rename: 'Renommer',
    swipeProject: 'Projet',
    swipeOutOfProject: 'Hors projet',
    chatActionFailed: 'Cela n’a pas fonctionné. Réessayez.',
    renameConversation: 'Renommer la conversation',
    tools: 'Outils',
    aiApps: 'Apps IA',
    aiAppsIntro:
      'Démarrez des conversations HTML ciblées et séparez les outils interactifs des skills générales.',
    startAiApp: 'Démarrer app IA',
    customHtmlApp: 'App HTML libre',
    customHtmlAppDesc: 'Créez une app interactive sur mesure à partir d’une idée ouverte.',
    businessDashboard: 'Dashboard métier',
    businessDashboardDesc: 'Créez des dashboards compacts avec KPI, filtres, tableaux et insights.',
    calculatorApp: 'Calculateur',
    calculatorAppDesc:
      'Concevez des calculateurs, estimateurs et simulations aux résultats clairs.',
    configuratorApp: 'Configurateur',
    configuratorAppDesc: 'Créez des aides au choix avec options, résumés et comparaisons.',
    workflowApp: 'Outil workflow',
    workflowAppDesc:
      'Créez des flux d’intake, checklists et outils de décision pour le travail opérationnel.',
    noAiAppSkill: 'La skill HTML App Builder n’est pas encore disponible pour votre compte.',
    aiTasks: 'Tâches IA',
    tasksAndAgents: 'Tâches et agents',
    moreTabs: 'Plus',
    agentWorkspaceTitle: 'Agents IA',
    agentWorkspaceIntro: 'Suivez vos agents et consultez leurs résultats.',
    agentTeamsTab: 'Équipes',
    agentApprovalsWaiting: 'actions attendent votre approbation.',
    viewApprovals: 'Voir les approbations',
    agentConfiguration: 'Configuration',
    agentLastResult: 'Dernier résultat',
    agentOpenResult: 'Ouvrir le résultat',
    agentTeamRuns: 'Exécutions d’équipe',
    agentNoKnowledge: 'Aucune source de connaissances sélectionnée',
    agents: 'Agents',
    agent: 'Agent',
    duet: '{assistant}',
    settingsGeneral: 'Général',
    settingsApp: 'Notifications et app',
    duetSectionIntro: 'Là où vos agents signalent résultats, alertes et approbations.',
    duetBriefOn: 'Brief du matin activé',
    duetBriefDays: 'Jours',
    duetWeekdays: 'Jours ouvrables',
    duetEveryDay: 'Tous les jours',
    duetMonitorOn: 'Moniteur activé',
    duetMonitorWhen: 'Quand',
    duetWorkHours: 'Jours ouvrables 8 h–19 h',
    duetAlways: 'Toujours',
    duetResultPanel: 'Résultat',
    duetResultUnavailable: 'Ce résultat ne peut pas être chargé pour le moment.',
    duetOpenAsChat: 'Ouvrir comme chat',
    duetMonitor: 'Moniteur',
    duetMonitorHint:
      'Vérifie toutes les trois heures si quelque chose demande votre attention et le signale dans {assistant}. Après une vérification calme, il attend six heures.',
    duetMonitorPushes: 'Notifications par jour',
    duetMonitorPushesHint:
      'Seuls les éléments urgents envoient une notification. Les approbations ne comptent pas.',
    duetMonitorSources: 'Jeux de données vérifiés',
    duetMonitorSave: 'Enregistrer le moniteur',
    duetMonitorSaveFailed:
      'Le moniteur n’a pas pu être enregistré. Vérifiez le modèle et vos jeux de données.',
    duetUrgent: 'Urgent',
    duetAttention: 'Attention',
    duetMute: 'Ne plus signaler',
    duetMuted: 'Cet élément ne sera plus signalé.',
    tabChat: 'Chat',
    tabMore: 'Plus',
    tabHistory: 'Conversations',
    tabBar: 'Navigation principale',
    pullToRefresh: 'Tirer pour actualiser',
    pullRelease: 'Relâcher pour actualiser',
    pullRefreshing: 'Actualisation…',
    duetUndo: 'Annuler',
    duetReadBrief: 'Lire le brief du matin',
    shareAnswer: 'Partager la réponse',
    takePhoto: 'Prendre une photo',
    choosePhotos: 'Photos',
    cameraShort: 'Appareil photo',
    fileShort: 'Fichier',
    forThisConversation: 'Pour cette conversation',
    knowledgeOn: '{count} activées',
    skillAuto: 'Automatique',
    activeContextCount: '{count} sources et skills actifs',
    cameraBack: 'Retour à l’appareil photo',
    cameraPhotoOf: 'Photo {n} sur {total}',
    cameraDelete: 'Supprimer cette photo',
    cameraMode: 'Type de photo',
    cameraModePhoto: 'Photo',
    cameraModeDocument: 'Document',
    cameraFlashOn: 'Flash activé',
    cameraFlashOff: 'Flash désactivé',
    cameraShot: 'Photo prise',
    cameraNoAccess:
      'NIVIO ne peut pas ouvrir l’appareil photo. Autorisez l’accès dans les réglages du téléphone, ou utilisez l’app Appareil photo.',
    cameraUsePhoneApp: 'Utiliser l’app Appareil photo',
    cameraLive: 'Image de l’appareil photo',
    cameraPrivate: 'Vos photos restent dans cette conversation',
    cameraAnother: 'Prendre une autre photo',
    cameraQuestion: 'Que voulez-vous savoir sur ces photos ?',
    cameraOnlyAdd: 'Seulement ajouter au message',
    cameraReview: 'Voir {count} photos',
    cameraShutter: 'Prendre la photo',
    cameraFlip: 'Autre caméra',
    offlineQueued:
      'Vous êtes hors ligne. Votre message partira dès que la connexion revient. Laissez l’app ouverte.',
    offlineWaiting: 'En attente de connexion',
    duetAskFurther: 'Poser une question',
    duetAskAbout: 'À propos de',
    duetFollowUp: 'Proposer un suivi',
    duetFollowUpPrompt:
      'Propose une action de suivi pour cette alerte, comme un e-mail ou une tâche. N’exécute rien sans mon accord.',
    duetSnoozeDay: 'Rappeler demain',
    duetSnoozeWeek: 'Dans une semaine',
    duetSnoozed: 'Silencieux jusqu’au',
    duetUseful: 'Utile',
    duetNoise: 'Pas utile',
    duetFeedback: 'Cette alerte était-elle utile ?',
    duetReadAloud: 'Lire à voix haute',
    duetAskShort: 'Question',
    duetLater: 'Plus tard',
    duetMuteShort: 'Muet',
    duetMonitorRules: 'Vos propres règles',
    duetMonitorRulesHint:
      'En langage courant, par exemple : « signale seulement les factures au-dessus de 5 000 € » ou « le client Acme est toujours urgent ».',
    duetNotifications: 'Notifications',
    duetNotificationsIntro: 'Quand et où {assistant} peut vous déranger.',
    duetQuietOn: 'Heures calmes',
    duetQuietHint:
      'Les notifications de {assistant} attendent pendant ces heures et arrivent ensuite en une seule. Les approbations passent toujours.',
    duetQuietFrom: 'De',
    duetQuietTo: 'À',
    duetQuietSave: 'Enregistrer les heures calmes',
    duetQuietSaveFailed: 'Les heures calmes n’ont pas pu être enregistrées.',
    duetDevicePushes: 'Notifications de {assistant} sur cet appareil',
    duetDevicePushesHint:
      'Brief du matin et moniteur. Les approbations et les agents terminés notifient chaque appareil où les notifications sont activées.',
    duetBrief: 'Brief du matin',
    duetBriefHint:
      'Chaque matin, un court point de départ dans {assistant} : vos réunions, ce qui arrive à échéance et ce qui demande votre attention depuis hier.',
    duetBriefSources: 'Jeux de données dans le brief',
    duetBriefMicrosoftHint:
      'Agenda, mail, Teams, To Do et OneDrive/SharePoint sont pris en compte dès que vous connectez votre compte Microsoft.',
    duetBriefSave: 'Enregistrer le brief du matin',
    duetBriefSaved: 'Enregistré.',
    duetBriefSaveFailed:
      'Le brief du matin n’a pas pu être enregistré. Vérifiez le modèle et vos jeux de données.',
    duetCardApproval: 'Approbation requise',
    duetProposedBy: 'Proposée par',
    duetReview: 'Examiner',
    duetCardDone: 'Terminé',
    duetTask: 'Tâche',
    duetOpenResult: 'Ouvrir le résultat',
    duetWaiting: 'En attente de vous',
    duetActionConfirm:
      '{assistant} le prépare dans le fil et vous le montre d’abord : « {action} ». Un mail demande encore une confirmation avant l’envoi.',
    duetActionPrepare: 'Préparer',
    duetActionApproval: 'accord requis',
    duetActionDone: 'fait',
    niviEarlierDays: 'Jours précédents',
    niviOnlyToday: 'Aujourd’hui seulement',
    niviShowDay: 'Afficher',
    niviAllDays: 'Tous les jours',
    niviAlertsOne: '1 alerte',
    niviAlertsCount: '{count} alertes',
    niviQuestionsOne: '1 question',
    niviQuestionsCount: '{count} questions',
    duetPushAttention: 'Notifier aussi pour « Attention »',
    duetPushAttentionHint:
      'Les points urgents notifient toujours. Désactivez ceci pour n’afficher que dans {assistant} ce qui demande seulement de l’attention.',
    duetTestPush: 'Envoyer une notification de test',
    duetTestPushSent: 'Envoyée à {count} appareil(s).',
    duetTestPushNone: 'Aucun appareil ne l’a reçue. Activez les notifications sur cet appareil.',
    duetTestPushFailed: 'La notification de test n’a pas pu être envoyée.',
    duetNew: 'Nouveau',
    duetUnavailable: '{assistant} ne peut pas être ouvert pour le moment. Réessayez plus tard.',
    niviEnable: 'Utiliser {assistant}',
    niviEnableHint:
      'Ajoute {assistant} à votre menu, avec le brief du matin, la veille et les notifications. Le désactiver met en pause le brief du matin et la veille.',
    niviOff: '{assistant} est désactivé. Activez-le dans les Réglages pour l’utiliser.',
    niviOpenSettings: 'Aller aux Réglages',
    duetStartSetting: 'Ouvrir l’application sur {assistant}',
    duetStartSettingHint:
      'Vos agents y signalent résultats et approbations. Le chat reste à un geste.',
    agentsIntro:
      'Agents IA personnels avec un skill, des connaissances et des outils sécurisés. Les actions externes exigent toujours votre approbation.',
    createAgent: 'Créer un agent',
    editAgent: 'Modifier l’agent',
    yourAgents: 'Vos agents',
    agentsNotAvailable: 'Les agents personnels ne sont pas activés pour votre compte.',
    noAgents: 'Aucun agent.',
    agentTitlePlaceholder: 'Agent de suivi financier',
    agentInstructions: 'Instructions',
    agentInstructionsPlaceholder:
      'Décrivez l’objectif, les éléments à surveiller et la forme attendue du résultat.',
    agentReadOnlyNote:
      'Cet agent fonctionne de manière autonome selon son planning. Les actions externes d’écriture et de suppression sont toujours proposées pour approbation.',
    agentActionFailed: 'L’agent n’a pas pu être mis à jour.',
    agentUsedByTeam: 'Mettez d’abord en pause ou supprimez les équipes qui utilisent cet agent.',
    agentTeams: 'Équipes d’agents',
    agentTeamsIntro:
      'Optionnel : faites collaborer plusieurs agents spécialisés sur une même tâche récurrente.',
    yourAgentTeams: 'Vos équipes d’agents',
    createAgentTeam: 'Créer une équipe d’agents',
    noAgentTeams: 'Aucune équipe d’agents.',
    teamTitlePlaceholder: 'Équipe santé client',
    teamInstructions: 'Mission de l’équipe',
    teamInstructionsPlaceholder:
      'Décrivez la question récurrente à laquelle l’équipe doit répondre.',
    coordinator: 'Coordinateur',
    specialists: 'Spécialistes',
    selectAgent: 'Sélectionner un agent',
    agentTeamReadOnlyNote:
      'Les équipes travaillent de manière séquentielle. Les actions externes d’écriture et de suppression sont toujours proposées pour approbation. Le coordinateur rassemble les résultats.',
    agentTeamActionFailed: 'L’équipe d’agents n’a pas pu être mise à jour.',
    approvals: 'Approbations',
    approvalsIntro: 'Les actions externes des agents ne sont exécutées qu’après votre approbation.',
    noApprovals: 'Aucune proposition d’action.',
    approvalErrorAgentDisabled:
      'L’agent qui a proposé cette action n’est plus actif. Réactivez-le et laissez-le la proposer à nouveau.',
    approvalErrorToolAccess: 'L’agent n’a plus accès à cet outil.',
    approvalErrorDatasetDisabled: 'Ce jeu de données n’est plus activé pour l’agent.',
    approvalErrorDatasetAccess: 'Vous n’avez plus accès à cette action dans le jeu de données.',
    approvalErrorInterrupted:
      'L’exécution a été interrompue et a peut-être réussi malgré tout. Vérifiez le système externe avant de réessayer.',
    approvalErrorVerifiedNotExecuted: 'Vous avez confirmé que l’action n’a pas été exécutée.',
    approvalErrorGeneric: 'L’action a échoué. Le message d’erreur figure dans Détails.',
    approvalProposedBy: 'Proposée par',
    approvalExpiresAt: 'Expire le',
    approvalExactAction:
      'Elle sera exécutée exactement ainsi. Si vous voulez autre chose, refusez-la.',
    approvalRawData: 'Données brutes',
    approvalHandled: 'Cette proposition a été traitée. Il n’y a plus rien à approuver.',
    approvalNotFound: 'Cette proposition n’existe pas ou n’appartient pas à votre compte.',
    approve: 'Approuver',
    reject: 'Refuser',
    approveAction: 'Approuver l’action',
    approvalPending: 'En attente d’approbation',
    approvalApproved: 'Approuvée',
    approvalExecuting: 'En cours d’exécution',
    approvalExecuted: 'Exécutée',
    approvalRejected: 'Refusée',
    approvalExpired: 'Expirée',
    approvalError: 'Échec',
    advancedSettings: 'Paramètres avancés',
    writeAction: 'Écriture',
    fileOrFolder: 'Fichier ou dossier',
    teamsMessage: 'Message Teams',
    plannerTask: 'Tâche Planner',
    toolActCalendarWrite: 'Met à jour l’agenda',
    toolActTodoRead: 'Consulte To Do',
    toolActTodoWrite: 'Met à jour une tâche dans To Do',
    calendarEvent: 'Rendez-vous',
    todoTask: 'Tâche To Do',
    listItem: 'Élément de liste',
    aiTask: 'Tâche IA',
    signOut: 'Se déconnecter',
    appearance: 'Affichage',
    theme: 'Thème',
    textSize: 'Taille du texte',
    light: 'Clair',
    dimmed: 'Atténué',
    dark: 'Sombre',
    themeSystem: 'Système',
    themeHint:
      'Système suit le réglage de votre appareil et change avec lui. Votre choix vaut pour cet appareil.',
    chatIntro:
      'Posez des questions, rédigez du texte ou cherchez dans vos groupes de connaissances. Les conversations sont chiffrées et liées à votre compte.',
    typeMessage: 'Écrivez votre question…',
    generating: 'Génération... (Esc pour arrêter)',
    message: 'Message',
    stopGenerating: 'Arrêter la génération',
    stop: 'Arrêter',
    send: 'Envoyer',
    attachFile: 'Joindre un fichier',
    dictate: 'Dicter',
    dictateHold: 'Maintenir pour dicter',
    dictateHolding: 'Enregistrement… relâchez pour convertir, glissez pour annuler',
    dictateHoldCancel: 'Relâchez pour annuler',
    dictateHoldLabel: 'Enregistrement… relâchez',
    dictateHoldHint: 'Maintenez le micro pendant que vous parlez',
    dictateHoldAgain: 'Micro autorisé. Maintenez à nouveau le bouton pendant que vous parlez.',
    dictateStop: 'Arrêter l’enregistrement et le convertir en texte',
    dictating: 'Enregistrement… (Échap pour annuler)',
    transcribing: 'Reconnaissance vocale…',
    dictationDenied: 'Pas d’accès au microphone. Autorisez le microphone dans votre navigateur.',
    dictationFailed: 'L’enregistrement n’a pas pu être converti en texte. Réessayez.',
    readAloudSection: 'Lecture à voix haute',
    readAloudSetting: 'Lire les réponses à voix haute après une question parlée',
    readAloudSettingHint:
      'Quand vous posez une question au micro, la réponse est lue à voix haute sur ce téléphone ou cette tablette.',
    readAloudSummarySetting: 'Lire seulement un résumé',
    readAloudSummarySettingHint:
      'Une longue réponse est racontée en quelques phrases. La réponse complète reste à l’écran. Désactivez pour tout lire à chaque fois.',
    readingAloud: 'Lecture en cours…',
    readAloudStop: 'Arrêter',
    readAloudFailed: 'La lecture à voix haute n’est pas possible pour le moment',
    speechCode: 'Voir le code à l’écran.',
    speechTable: 'Voir le tableau à l’écran.',
    speechLink: 'lien',
    removeAttachment: 'Supprimer la pièce jointe',
    readingAttachment: 'Lecture de la pièce jointe...',
    attachmentPreviewUnavailable: 'La pièce jointe n’a pas encore pu être lue.',
    attachmentDefaultPrompt: 'Résume les documents joints.',
    attachmentContextLabel: 'Pièce jointe',
    sqliteAttachmentDefaultPrompt: 'Inspecte la base SQLite et donne un aperçu.',
    sqliteToolAvailable: 'Peut lire votre base de données',
    model: 'Modèle',
    modelAuto: 'Automatique',
    modelAutoHint: 'NIVIO choisit le modèle adapté à chaque question',
    modelFast: 'Rapide',
    modelFastHint: 'Questions courtes, résumés, reformulation',
    modelBalanced: 'Équilibré',
    modelBalancedHint: 'La plupart du travail quotidien',
    modelThorough: 'Approfondi',
    modelThoroughHint: 'Analyse, longs documents, questions difficiles · plus cher',
    modelAll: 'Tous les modèles…',
    modelBack: 'Retour',
    modelAnsweredBy: 'Répondu par {model}',
    searchModels: 'Rechercher des modèles',
    selectModel: 'Choisir un modèle',
    noModels: 'Aucun modèle disponible. Demandez l’accès à votre admin.',
    searchProjects: 'Rechercher des projets',
    knowledge: 'Connaissances',
    generatingInBackground: 'Génération en cours en arrière-plan',
    knowledgeGroup: 'groupe de connaissances',
    knowledgeGroups: 'groupes de connaissances',
    searchKnowledge: 'Rechercher des connaissances',
    noKnowledge:
      'Aucun groupe de connaissances disponible pour votre compte. Demandez l’accès à votre admin.',
    dataset: 'source',
    datasets: 'sources',
    skill: 'Skill',
    automatic: 'automatique',
    autoSkillRemove: 'Cliquez pour n’utiliser aucune compétence dans cette conversation.',
    autoSkillNone: 'Aucune compétence',
    autoSkillBack: 'Laisser NIVIO choisir à nouveau une compétence',
    autoSkillHint:
      'Choisi par le chat pour cette demande. Choisissez vous-même un skill avec @ pour remplacer.',
    searchSkills: 'Rechercher des skills',
    skills: 'Skills',
    skillsIntro:
      'Parcourez les skills mis à disposition par votre admin et démarrez une conversation avec le bon contexte.',
    startWithSkill: 'Démarrer avec le skill',
    defaultModel: 'Modèle par défaut',
    noKnowledgeRequired: 'Aucune connaissance requise',
    noDescription: 'Aucune description.',
    other: 'Autre',
    noSkill: 'Choisir les skills automatiquement',
    clearSelection: 'Effacer la sélection',
    noSkills: 'Aucun skill disponible pour votre compte. Demandez l’accès à votre admin.',
    settings: 'Paramètres',
    settingsIntro: 'Gérez vos préférences chat et consultez votre propre usage.',
    profile: 'Profil',
    openAdmin: 'Ouvrir admin',
    admin: 'Admin',
    member: 'Membre',
    adminOnly: 'Seuls les admins peuvent ouvrir cet écran.',
    toolFailures: 'Appels d’outils échoués',
    toolFailuresIntro:
      'Consultez les appels d’outils échoués sans afficher le contenu des prompts ni les messages.',
    toolFailuresLoadFailed: 'Impossible de charger les erreurs d’outils.',
    noToolFailures: 'Aucun appel d’outil échoué.',
    unknownTool: 'Outil inconnu',
    user: 'Utilisateur',
    toolCallId: 'ID appel outil',
    details: 'Détails',
    chatDefaults: 'Préférences chat',
    defaultKnowledge: 'Connaissance par défaut',
    usage: 'Usage',
    settingsAdvanced: 'Avancé',
    usageShow: 'Afficher les détails',
    usageSummaryBudget:
      'Ce mois-ci, vous avez utilisé {spent} de votre budget de {budget}, en {count} messages.',
    usageSummarySpent: 'Ce mois-ci, vous avez utilisé {spent}, en {count} messages.',
    usageSummaryMessages: 'Ce mois-ci, vous avez envoyé {count} messages.',
    usageSummaryAll: '{count} messages depuis le début.',
    usageAdvancedIntro: 'Ce que vous avez utilisé ce mois-ci et au total, pour qui veut le suivre.',
    connectedAccounts: 'Comptes connectés',
    microsoft365: 'Microsoft 365',
    connectMicrosoft365: 'Connecter Microsoft 365',
    reconnectMicrosoft365: 'Reconnecter',
    microsoftReconnectRequired:
      'De nouvelles autorisations sont nécessaires pour lire le contenu des e-mails et votre agenda. Reconnectez votre compte Microsoft.',
    microsoftNotConnected:
      'Connectez Microsoft 365 pour rendre le contexte mail, OneDrive et SharePoint disponible.',
    microsoftStatusUnavailable: 'Impossible de charger le statut Microsoft 365.',
    microsoftAdminSetupRequired: 'Microsoft 365 doit d’abord être configuré par un admin.',
    microsoftConnectFailed: 'La connexion Microsoft 365 a échoué.',
    devopsTokenSaved: 'Jeton enregistré',
    devopsTokenIntro:
      'Utilisez votre propre jeton d’accès personnel (PAT) pour l’organisation {organization} si vous ne vous connectez pas avec Microsoft.',
    devopsTokenLabel: 'Jeton d’accès personnel',
    devopsTokenReplace: 'Nouveau jeton pour remplacer l’actuel',
    devopsTokenChecking: 'Vérification…',
    devopsTokenRemove: 'Supprimer le jeton',
    devopsTokenRemoveConfirm:
      'Azure DevOps utilise alors à nouveau votre connexion Microsoft, si vous en avez une.',
    devopsTokenInvalid:
      'Azure DevOps n’accepte pas ce jeton. Vérifiez qu’il est valide, non expiré et créé pour cette organisation.',
    devopsTokenFailed: 'Le jeton n’a pas pu être enregistré. Réessayez plus tard.',
    devopsTokenPrivacy:
      'Le jeton est stocké chiffré, utilisé uniquement pour vous et jamais réaffiché. Ne lui donnez que les droits nécessaires.',
    leexiKeySaved: 'Clé enregistrée',
    leexiKeyCompanyFallback:
      'Sans clé personnelle, NIVIO utilise la clé d’entreprise configurée par votre administrateur.',
    leexiKeyIntro:
      'Demandez à votre administrateur Leexi une clé API limitée à vos propres appels (Company Settings → API Keys) et saisissez-la ici.',
    leexiKeyIdLabel: 'Key ID',
    leexiKeySecretLabel: 'Key secret',
    leexiKeySecretReplace: 'Nouveau secret pour remplacer la clé actuelle',
    leexiKeyRemove: 'Supprimer la clé',
    leexiKeyRemoveConfirm:
      'NIVIO utilise alors la clé d’entreprise de votre administrateur, s’il y en a une.',
    leexiKeyInvalid: 'Leexi n’accepte pas cette clé. Vérifiez le Key ID et le secret.',
    leexiKeyFailed: 'La clé n’a pas pu être enregistrée. Réessayez plus tard.',
    leexiKeyPrivacy:
      'La clé est stockée chiffrée, utilisée uniquement pour vous et jamais réaffichée. Ne lui donnez accès qu’à ce dont vous avez besoin.',
    microsoftMail: 'Microsoft Mail',
    connectMicrosoftMail: 'Connecter Microsoft Mail',
    connected: 'Connecté',
    notConnected: 'Non connecté',
    connectedAs: 'Connecté en tant que',
    connecting: 'Connexion...',
    mailNotConnected:
      'Connectez votre boîte mail pour que l’Email Assistant réponde aux questions sur vos e-mails.',
    mailStatusUnavailable: 'Impossible de charger le statut mail.',
    mailAdminSetupRequired: 'Microsoft Mail doit d’abord être configuré par un admin.',
    mailConnectFailed: 'La connexion Microsoft Mail a échoué.',
    confirmMailSendTitle: 'Envoyer l’e-mail ?',
    confirmMailSendDescription: 'Vérifiez le contenu avant d’envoyer cet e-mail.',
    mailTo: 'À',
    mailCc: 'Cc',
    mailSubject: 'Objet',
    mailBody: 'Contenu',
    sendMail: 'Envoyer l’e-mail',
    sending: 'Envoi...',
    mailSent: 'E-mail envoyé.',
    mailSendFailed: 'Impossible d’envoyer l’e-mail.',
    thisMonth: 'Ce mois-ci',
    allTime: 'Depuis toujours',
    costManagedInAdmin:
      'Votre administrateur gère les prix et les budgets. Ici, vous ne voyez que votre propre usage.',
    assistant: 'assistant',
    companyAssistants: 'Assistants de l’entreprise',
    personalAssistants: 'Assistants personnels',
    tool: 'outil',
    toolUsed: 'Outil utilisé',
    toolsUsed: 'outils utilisés',
    toolActMakeWord: 'Crée un document Word',
    toolActMakeExcel: 'Crée un fichier Excel',
    toolActMakePowerPoint: 'Crée une présentation PowerPoint',
    toolActMakeImage: 'Crée une image',
    toolActMailRead: 'Lit vos e-mails',
    toolActMailSend: 'Envoie un e-mail',
    toolActCalendar: 'Consulte votre agenda',
    toolActTeamsRead: 'Consulte Teams',
    toolActTeamsPost: 'Publie un message dans Teams',
    toolActPlannerRead: 'Consulte Planner',
    toolActPlannerWrite: 'Met à jour une tâche dans Planner',
    toolActListsRead: 'Consulte une liste SharePoint',
    toolActListsWrite: 'Met à jour une liste SharePoint',
    toolActDriveSearch: 'Cherche dans OneDrive et SharePoint',
    toolActDriveRead: 'Lit un fichier de OneDrive ou SharePoint',
    toolActDriveWrite: 'Met à jour un fichier dans OneDrive ou SharePoint',
    toolActAnalyse: 'Analyse la pièce jointe',
    toolActProjectDoc: 'Lit un document du projet',
    toolActData: 'Interroge la base de données',
    toolActWebSearch: 'Cherche sur le web',
    toolActConsult: 'Consulte {name}',
    toolQuery: 'Comment cela a été recherché',
    toolQueryRows: '{count} lignes',
    toolQueryMoreRows: 'Il y a probablement plus de lignes que celles affichées.',
    toolSourceWeb: 'Web',
    toolSourceImage: 'Image',
    toolSourceAttachment: 'Pièce jointe',
    toolSourceProject: 'Documents du projet',
    toolSourceData: 'Base de données',
    toolSources: 'Sources',
    failed: 'échoué',
    done: 'terminé',
    running: 'en cours',
    completed: 'terminé',
    errorStatus: 'erreur',
    answerMore: 'cette réponse',
    tokensIn: 'entrée',
    tokensOut: 'sortie',
    aiTasksIntro:
      'Planifiez un skill en arrière-plan. Les tâches terminées apparaissent ici et comme conversations.',
    title: 'Titre',
    description: 'Description',
    edit: 'Modifier',
    save: 'Enregistrer',
    weeklyFinanceInsight: 'Analyse financière hebdomadaire',
    planning: 'Planning',
    hourly: 'Toutes les heures',
    every6Hours: 'Toutes les 6 heures',
    daily: 'Quotidien',
    weekly: 'Hebdomadaire',
    prompt: 'Prompt',
    promptPlaceholder: 'Exécutez ce skill et résumez le résultat pour moi.',
    createAiTask: 'Créer une tâche IA',
    aiTaskActionFailed: 'La tâche IA n’a pas pu être mise à jour.',
    editAiTask: 'Modifier la tâche IA',
    scheduledAiTasks: 'Tâches IA planifiées',
    noAiTasks: 'Aucune tâche IA.',
    enabled: 'Activée',
    paused: 'En pause',
    next: 'prochaine',
    runNow: 'Exécuter maintenant',
    pause: 'Pause',
    enable: 'Activer',
    delete: 'Supprimer',
    runs: 'Exécutions',
    runStarted: 'Démarré.',
    noRuns: 'Aucune exécution.',
    open: 'Ouvrir',
    view: 'Affichage',
    viewList: 'Liste',
    viewTiles: 'Vignettes',
    releaseNotesOpen: 'Voir les nouveautés de la version',
    releaseNotesTitle: 'Nouveautés de la version',
    releaseNotesIntro: 'Modifications fonctionnelles disponibles dans cette version.',
    releaseNotesEmpty:
      'Cette version ne contient aucune modification fonctionnelle pour les utilisateurs.',
    actions: 'Actions',
    markRead: 'Marquer comme lu',
    signIn: 'Se connecter',
    useGatewayCredentials: 'Connectez-vous avec votre compte NIVIO.',
    signInWithSso: 'Microsoft Account',
    continueWithSso: 'Ou connectez-vous avec Microsoft',
    ssoSignInFailed: 'La connexion SSO a échoué.',
    email: 'E-mail',
    password: 'Mot de passe',
    signingIn: 'Connexion...',
    interactiveHtml: 'Page web',
    artifacts: 'Fichiers',
    artifactsIntro:
      'Consultez les fichiers Office, images et HTML interactif générés dans vos conversations.',
    noArtifacts: 'Aucun fichier trouvé.',
    projects: 'Projets',
    project: 'Projet',
    noProject: 'Aucun projet',
    projectsIntro:
      'Regroupez conversations, instructions et documents privés dans un contexte projet.',
    createProject: 'Créer un projet',
    newProject: 'Nouveau projet',
    noProjects: 'Aucun projet.',
    startProjectChat: 'Démarrer un chat projet',
    projectNewChat: 'Nouvelle conversation dans ce projet',
    projectChatsShort: 'Conversations',
    projectDocsShort: 'Documents',
    addDocument: 'Ajouter',
    documentActions: 'Actions du document',
    usedInChats: 'utilisé dans les conversations',
    notInChats: 'hors des conversations',
    useInChats: 'Utiliser dans les conversations',
    projectInstructions: 'Instructions du projet',
    deleteProjectConfirm: 'Supprimer ce projet ? Les documents et liens projet seront supprimés.',
    projectDocuments: 'Documents du projet',
    projectDocumentsIntro:
      'Les uploads restent en stockage privé. Les fichiers texte sont utilisés comme contexte projet.',
    projectChats: 'Chats du projet',
    projectChatsIntro: 'Conversations liées à ce projet.',
    noProjectChats: 'Aucun chat de projet.',
    messages: 'messages',
    uploadDocument: 'Uploader un document',
    indexDocument: 'Traiter',
    uploadFailed: 'Échec du téléversement.',
    noProjectDocuments: 'Aucun document.',
    documents: 'Documents',
    projectPolicyPrefix: 'Limites :',
    ocrEnabled: 'OCR actif',
    ocrDisabled: 'OCR inactif',
    inactiveDocument: 'non utilisé dans le chat',
    disableInChat: 'Exclure',
    enableInChat: 'Utiliser',
    manage: 'Gérer',
    displayName: 'Nom affiché',
    tags: 'Tags',
    tagsPlaceholder: 'kickoff, scope, finance',
    cancel: 'Annuler',
    documentStatusIndexed: 'Indexé',
    documentStatusIndexing: 'Indexation',
    documentStatusStored: 'Stocké',
    documentStatusUnsupported: 'Non pris en charge',
    documentStatusFailed: 'Échec',
    previewUnavailable: 'Aperçu indisponible.',
    previewTruncated: 'Aperçu tronqué.',
    openPreview: 'Ouvrir l’aperçu',
    sandboxedPreview: 'Aperçu sandbox',
    videoPresentation: 'Présentation vidéo',
    videoScenes: '{count} scènes',
    recordVideo: 'Enregistrer en vidéo',
    stopRecording: "Arrêter l'enregistrement",
    recordingFailed:
      "L'enregistrement n'a pas fonctionné. Choisissez cet onglet quand le navigateur demande quoi partager.",
    preview: 'Aperçu',
    copyHtml: 'Copier HTML',
    openInNewTab: 'Ouvrir dans un nouvel onglet',
    close: 'Fermer',
    modelsAll: 'Tous',
    modelsRecommended: 'Recommandé',
    officeFile: 'Fichier Office',
    download: 'Télécharger',
    saving: 'Enregistrement...',
    saveToSharePoint: 'Enregistrer dans SharePoint',
    savedToSharePoint: 'Enregistré dans SharePoint.',
    deleteConversation: 'Supprimer la conversation',
    deleteConversationConfirmPrefix: 'Supprimer',
    deleteConversationConfirmSuffix: 'Cette action est irréversible.',
    resizePreview: 'Redimensionner l’aperçu',
    openNavigation: 'Ouvrir la navigation',
    closeNavigation: 'Fermer la navigation',
    toggleWiderPreview: 'Agrandir ou réduire l’aperçu',
    toolIterationCapReached: 'Limite des itérations d’outils atteinte.',
    noticeToolsUnavailable:
      'Les sources de connaissances n’ont pas pu être chargées. Cette réponse a été faite sans sources de connaissances et sans recherche web.',
    noticeToolDenied:
      'Vous n’avez pas accès à l’une des sources de connaissances demandées. Elle n’a pas été utilisée.',
    noticeForcedFinal:
      'La limite d’outils pour cette réponse a été atteinte. La réponse utilise les informations déjà trouvées.',
    noticeToolCallsDropped: 'Un appel d’outil incomplet du modèle a été ignoré.',
    sharePointNotConfigured:
      'SharePoint n’est pas encore configuré. Configurez-le d’abord dans les paramètres gateway.',
    sharePointRejected:
      'Les identifiants SharePoint ont été refusés. Vérifiez le tenant, le client ID, le secret et les permissions Graph.',
    sharePointForbidden:
      'L’application SharePoint n’a pas les droits nécessaires pour enregistrer des fichiers ici.',
    sharePointMissing: 'Le drive ou dossier SharePoint configuré est introuvable.',
    sharePointTooLarge: 'Ce fichier dépasse la limite d’envoi configurée pour SharePoint.',
    sharePointSaveFailed: 'L’enregistrement dans SharePoint a échoué.',
    couldNotSaveSharePoint: 'Impossible d’enregistrer le fichier dans SharePoint.',
    attachShort: '+ Joindre',
    attentionBadge: 'Tâches à traiter',
    notifyOnFinish: 'Me prévenir à la fin',
    notifyOnFinishHelp:
      'Une notification apparaît en haut à droite et un badge dans la barre latérale.',
    scheduleTime: 'Heure',
    scheduleTimeHint: 'Facultatif. Laissez vide pour répéter à partir de l’enregistrement.',
    scheduleTimeWeeklyHint:
      'Facultatif. La première exécution a lieu aujourd’hui à cette heure, ou la semaine prochaine si elle est passée.',
    ownerCol: 'Propriétaire',
    usageCol: 'Utilisation',
    teamSkill: 'skill d’équipe',
    personalSkillLabel: 'Skill personnel',
    usedTimesMonth: 'Utilisé {n} fois ce mois-ci',
    usedOnceMonth: 'Utilisé une fois ce mois-ci',
    notUsedMonth: 'Pas utilisé ce mois-ci',
    usageHint: 'Vos propres messages dans les chats avec ce skill, ce mois-ci.',
    version: 'version',
    testInChat: 'Tester dans le chat',
    skillExamples: 'Exemples',
    skillExamplesHelp: 'Phrases qui doivent déclencher ce skill.',
    skillExampleAdd: 'Ajouter un exemple',
    skillExampleRemove: 'Supprimer l’exemple',
    skillVisibleTo: 'Visible pour',
    skillAllowTeamEdits: 'Autoriser la modification par l’équipe',
    visibilityCol: 'Visibilité',
    visibilityPrivate: 'Privé',
    visibilityShared: 'Partagé',
    sharedWithYou: 'Partagé avec vous',
    sharedProjectNote:
      '{name} a partagé ce projet avec vous. Vous pouvez y discuter et utiliser ses documents ; seul le propriétaire peut le modifier. Vos conversations restent privées.',
    aColleague: 'Un collègue',
    sharedBy: 'Partagé par {name}',
    showAllDocuments: 'Afficher les {count} documents',
    shareWith: 'Partager avec',
    shareHelp:
      'Qui peut utiliser le projet : ses instructions, documents, groupes de connaissances et skills. Vous seul pouvez le modifier. Les conversations de chacun restent privées.',
    shareNobody: 'Personne (privé)',
    shareEveryone: 'Tout le monde',
    resizeSidebar:
      'Redimensionner la barre latérale (glisser ou flèches, double-clic pour la largeur par défaut)',
    shareGroups: 'Groupes sélectionnés',
    shareGroupsLabel: 'Groupes',
    shareGroupsEmpty: 'Choisissez au moins un groupe, sinon le projet reste privé.',
    noProjectSkills: 'Aucun skill',
    projectSkillsHelp:
      'Ces skills sont activés par défaut dans chaque nouvelle conversation de ce projet. Ils peuvent être désactivés par conversation. Les personnes qui ne peuvent pas utiliser un skill ne l’obtiennent pas.',
    visibilityHelp:
      'Simple étiquette : le partage avec d’autres personnes n’est pas encore disponible.',
    lstFiles: 'fichiers',
    runNotifyAgentDone: 'L’agent « {name} » est terminé',
    runNotifyAgentFailed: 'L’agent « {name} » a échoué',
    runNotifyTaskDone: 'La tâche IA « {name} » est terminée',
    runNotifyTaskFailed: 'La tâche IA « {name} » a échoué',
    runNotifyDoneLabel: 'Terminé',
    runNotifyFailedLabel: 'Échec',
    runNotifyOpen: 'Ouvrir l’exécution',
    runNotifyDismiss: 'Fermer la notification',
    tourWelcomeTitle: 'Bienvenue dans NIVIO, {name}',
    tourWelcomeTitleNoName: 'Bienvenue dans NIVIO',
    tourWelcomeText:
      'Une courte visite en {count} étapes vous montre comment poser des questions, utiliser les données de votre entreprise et automatiser des tâches.',
    tourLater: 'Plus tard',
    tourStart: 'Commencer la visite',
    tourStepOf: 'Étape {current} sur {total}',
    tourSkip: 'Passer',
    tourPrevious: 'Précédent',
    tourNext: 'Suivant',
    tourDone: 'Terminé',
    tourAskTitle: 'Poser une question',
    tourAskBody:
      'Tapez votre question en langage courant et appuyez sur Entrée. Maj+Entrée ajoute une nouvelle ligne.',
    tourDataTitle: 'Utiliser les données de l’entreprise',
    tourDataBody:
      'Avec + vous activez des groupes de connaissances et des skills, comme Business Central ou SharePoint. Ou tapez @ dans votre question.',
    tourFilesTitle: 'Fichiers et dictée',
    tourFilesBody:
      'Ajoutez des fichiers avec + ou glissez-les dans la zone de saisie. Le micro vous permet de dicter votre question.',
    tourFilesOnlyTitle: 'Ajouter des fichiers',
    tourFilesOnlyBody:
      'Ajoutez des fichiers avec + ou glissez-les dans la zone de saisie. NIVIO les lit avec votre question.',
    tourProjectsTitle: 'Projets',
    tourProjectsBody:
      'Regroupez conversations et documents par client ou par sujet. Chaque question dans un projet utilise ses documents.',
    tourAgentsTitle: 'Agents et tâches',
    tourAgentsBody:
      'Faites exécuter le travail récurrent automatiquement, comme un aperçu hebdomadaire. Vous recevez une notification à la fin.',
    tourAccountsTitle: 'Connecter des comptes',
    tourAccountsBody:
      'Connectez Microsoft 365 sous Paramètres → Comptes connectés. NIVIO peut alors utiliser vos e-mails, votre agenda et vos fichiers.',
    tourAccountsBodyDevOps:
      'Connectez Microsoft 365 et votre jeton Azure DevOps sous Paramètres → Comptes connectés. NIVIO les utilise alors avec vos droits.',
    tourSetting: 'Visite guidée',
    tourSettingIntro: 'Une courte explication des principales parties du chat.',
    tourCompletedOn: 'Terminée le {date}',
    tourSkippedStatus: 'Passée',
    tourNotYet: 'Pas encore suivie',
    tourRestart: 'Recommencer',
    pwaInstallTitle: "Installer l'application",
    pwaInstallIntro: "Ouvrez le chat comme une application, depuis l'écran d'accueil ou le bureau.",
    pwaInstallButton: "Installer l'application",
    pwaInstalled: "L'application est installée sur cet appareil.",
    pwaIosStep1: 'Touchez Partager dans la barre de Safari.',
    pwaIosStep2: "Choisissez Sur l'écran d'accueil.",
    pwaIosStep3: 'Touchez Ajouter.',
    pwaGenericHint:
      "Utilisez le menu de votre navigateur : Installer l'application ou Ajouter à l'écran d'accueil.",
    pwaUpdateAvailable: 'Nouvelle version disponible',
    pwaUpdateReload: 'Actualiser',
    pushTitle: 'Notifications',
    pushIntro:
      "Recevez une notification sur cet appareil quand un agent ou une tâche IA est terminé, même si l'application est fermée. Vous choisissez par tâche si vous en voulez une.",
    pushUnsupported: 'Ce navigateur ne prend pas en charge les notifications.',
    pushServerOff: 'Les notifications ne sont pas activées par votre administrateur.',
    pushIosInstall:
      "Installez d'abord l'application sur votre écran d'accueil (iPhone ou iPad avec iOS 16.4 ou plus récent, voir Installer l'application ci-dessus). Ouvrez-la ensuite depuis l'écran d'accueil pour activer les notifications.",
    pushDenied:
      'Les notifications sont bloquées pour ce site. Autorisez-les dans les réglages de votre navigateur ou appareil, puis revenez.',
    pushEnable: 'Activer les notifications sur cet appareil',
    pushDisable: 'Désactiver les notifications sur cet appareil',
    pushOn: 'Les notifications sont activées sur cet appareil.',
    pushFailed: "L'activation ou la désactivation des notifications a échoué. Réessayez.",
    pwaShareHint:
      "Sur Android, vous pouvez partager du texte et des fichiers d'autres applications avec Chat (Partager, puis Chat). iPhone et iPad ne le permettent pas.",
    shareReceived: 'Partagé vers NIVIO : {count} fichier(s).',
    shareUnsupported:
      "Le partage ne fonctionne qu'après l'installation de l'application et sa réouverture.",
    shareTooLarge: 'Le contenu partagé est trop volumineux pour être ouvert ici (25 Mo maximum).',
    shareExpired: "Le contenu partagé n'est plus disponible. Partagez-le à nouveau.",
    shareFailed: "Le contenu partagé n'a pas pu être ouvert. Partagez-le à nouveau.",
  },
} as const;

export type TranslationKey = keyof typeof messages.nl;
export type TFunction = (key: TranslationKey) => string;

export function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'nl';
  const value = navigator.language.toLowerCase();
  if (value.startsWith('fr')) return 'fr';
  if (value.startsWith('en')) return 'en';
  return 'nl';
}

export function translate(language: Language, key: TranslationKey, assistant = 'Nivi'): string {
  const text: string = messages[language][key] ?? messages.nl[key];
  return text.includes('{assistant}') ? text.replaceAll('{assistant}', assistant) : text;
}

export function LanguageProvider({
  language,
  children,
}: {
  language: Language;
  children: ReactNode;
}) {
  return <LanguageContext.Provider value={language}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): Language {
  return useContext(LanguageContext);
}

export function AssistantNameProvider({ name, children }: { name: string; children: ReactNode }) {
  return <AssistantNameContext.Provider value={name}>{children}</AssistantNameContext.Provider>;
}

export function useAssistantName(): string {
  return useContext(AssistantNameContext);
}

export function useT() {
  const language = useLanguage();
  const assistant = useAssistantName();
  return (key: TranslationKey) => translate(language, key, assistant);
}
